import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCurrencies } from '../hooks/useCurrencies';
import useDocumentLock from '../hooks/useDocumentLock';
import ReportDesignerPage from './reports/ReportDesignerPage';
import { renderPrintHtml } from './reports/reportEngine';
import { getDefaultLayout } from './printTemplates/defaults';
import AuditPopover from '../components/AuditPopover';
import NumberInput from '../components/NumberInput';
import { formatCariOption } from '../utils/cariFormat';
import SearchableSelect from '../components/SearchableSelect';
import CariCard from '../components/CariCard';
import ModuleOverlay from '../components/ModuleOverlay';
import QuickListManager from '../components/QuickListManager';
import DataTable from '../components/DataTable';
import BarcodeScanner from '../components/BarcodeScanner';
import { fmtAmount, fmtQty, fmtRate, qtyStep, amountStep, rateStep, getQtyDecimals, getAmountDecimals } from '../utils/numberFormat';
import { TabIcon } from '../utils/tabIcons';
import { Modal, Button, Pager, ListPageHeader, ListPageStats, FilterBar } from '../components/ui';
import { CardHero, CardStatusBadge, CardTabs, CardSplitButton } from '../components/card';
import AttachmentManager from '../components/AttachmentManager';
import CustomFieldsSection from '../components/CustomFieldsSection';
import QuoteActivities from '../components/QuoteActivities';
import EDocumentBadge from '../components/EDocumentBadge';
import PriceHistoryModal from '../components/PriceHistoryModal';

class RenderBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { console.error('[RenderBoundary]', err, info); }
  render() {
    if (this.state.err) {
      return (
        <div style={{ padding: 16, color: '#b91c1c', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 8, fontFamily: 'monospace', fontSize: '0.82rem', whiteSpace: 'pre-wrap' }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Render hatası:</div>
          <div>{String(this.state.err?.message || this.state.err)}</div>
          {this.state.err?.stack && <div style={{ marginTop: 8, opacity: 0.7, fontSize: '0.72rem' }}>{this.state.err.stack.split('\n').slice(0,5).join('\n')}</div>}
        </div>
      );
    }
    return this.props.children;
  }
}

const invCariLabel = (a) => `${a.code || ''}${a.code ? ' - ' : ''}${a.title || ''}${a.is_prospect ? ' 🌟' : ''}`;
const invCariSubLabel = (a) => {
  const parts = [];
  if (a.is_prospect) parts.push('Potansiyel');
  if (a.phone || a.mobile) parts.push(a.phone || a.mobile);
  if (a.identity_type === 'individual') {
    if (a.id_number) parts.push('TC: ' + a.id_number);
  } else {
    if (a.tax_office) parts.push(a.tax_office);
    if (a.tax_number) parts.push(a.tax_number);
  }
  parts.push(a.currency_code || 'TRY');
  return parts.filter(Boolean).join(' | ');
};
const invCariSearchKeys = ['code', 'title', 'phone', 'mobile', 'tax_number', 'id_number', 'tax_office'];


// labelKey: render sırasında t(labelKey, label) ile çevrilir; key yoksa label fallback olur.
const ALL_TABS = [
  {id:0, label:'Fatura Başlığı',     icon:'general',    labelKey:'tabs.invoiceHeader'},
  {id:1, label:'Kalemler',           icon:'cart',       labelKey:'tabs.lines'},
  {id:2, label:'Fatura / Sevkiyat',  icon:'package',    labelKey:'tabs.invoiceShipment'},
  {id:3, label:'E-Ticaret',          icon:'card',       labelKey:'tabs.ecommerce'},
  {id:4, label:'e-Belge',            icon:'id',         labelKey:'tabs.eDoc'},
  {id:5, label:'Notlar',             icon:'notes',      labelKey:'tabs.notes'},
  {id:6, label:'Bağlı Belgeler',     icon:'activity',   labelKey:'tabs.linkedDocs'},
  {id:7, label:'Aktarım Geçmişi',    icon:'activity',   labelKey:'tabs.transferHistory'},
  {id:8, label:'Belgeler / Ekler',   icon:'paperclip',  labelKey:'tabs.attachments'},
  {id:9, label:'Görüşmeler',         icon:'phone',      labelKey:'tabs.chatter'},
  {id:10,label:'Özel Alanlar',        icon:'sparkles',   labelKey:'tabs.customFields'},
];

const emptyLine = {
  stock_id:'', stock_name:'', stock_code:'', barcode:'',
  manual_stock_name:'',
  unit_id:'', unit2_id:'', unit3_id:'',
  unit_type:'unit1', unit_ratio:1,
  unit_name:'', unit2_name:'', unit3_name:'',
  quantity:1, quantity_base:1,
  unit_price:0,
  unit_price_local:0,
  unit_price_net:0,
  is_vat_included:false,
  discount_rate1:0, discount_rate2:0, discount_rate3:0, discount_rate4:0, discount_rate5:0,
  discount_amount:0, otv_rate:0, otv_amount:0, otv_type:'rate',
  vat_rate:20, vat_amount:0, withholding_rate:0, withholding_amount:0,
  line_subtotal:0, line_discount:0, line_net:0, line_otv:0,
  line_vat_base:0, line_vat:0, line_withholding:0, line_total:0,
  line_total_local:0,
  warehouse_id:'', personnel_id:'', description:'', delivery_date:'', profit_pct:'',
  units:[], track_serial:false, track_lot:false,
  line_ship_to_name:'', line_ship_to_address:'', line_ship_to_city:'', line_carrier_id:'',
  price_type:'1', color_id:null, size_id:null, color_name:'', size_name:''
};

function SalesInvoicePage({ mode = 'sales', autoOpenId = null, onNavigate = null, viewOnly = false }) {
  // mode:
  //   'sales'                  → sales + retail_sale (toptan + perakende satış)
  //   'returns'                → sales_return + retail_sale_return (satış iadeleri)
  //   'purchase'               → purchase (alış)
  //   'purchase-returns'       → purchase_return (alış iadesi)
  //   'waybill-sales'          → sales_waybill (satış sevk irsaliyesi)
  //   'waybill-sales-returns'  → sales_waybill_return
  //   'waybill-purchase'       → purchase_waybill (alış sevk irsaliyesi)
  //   'waybill-purchase-returns' → purchase_waybill_return
  const isPurchaseMode = mode === 'purchase' || mode === 'purchase-returns' || mode === 'waybill-purchase' || mode === 'waybill-purchase-returns' || mode === 'order-outgoing' || mode === 'quote-incoming';
  const isReturnMode   = mode === 'returns'  || mode === 'purchase-returns' || mode === 'waybill-sales-returns' || mode === 'waybill-purchase-returns';
  const isWaybillMode  = mode === 'waybill-sales' || mode === 'waybill-sales-returns' || mode === 'waybill-purchase' || mode === 'waybill-purchase-returns';
  const isPurchaseWaybillMode = mode === 'waybill-purchase' || mode === 'waybill-purchase-returns';
  const isOrderMode         = mode === 'order-incoming' || mode === 'order-outgoing';
  const isOrderIncomingMode = mode === 'order-incoming';
  const isOrderOutgoingMode = mode === 'order-outgoing';
  const isQuoteMode         = mode === 'quote-incoming' || mode === 'quote-outgoing';
  const isQuoteIncomingMode = mode === 'quote-incoming';
  const isQuoteOutgoingMode = mode === 'quote-outgoing';
  const supportsRetail = !isPurchaseMode && !isWaybillMode && !isOrderMode && !isQuoteMode;
  let filterTypes, wholesaleType, retailType, pageTitle;
  if (isQuoteIncomingMode) {
    filterTypes = 'purchase'; wholesaleType = 'purchase'; retailType = 'purchase';
    pageTitle = 'Alınan Teklifler';
  } else if (isQuoteOutgoingMode) {
    filterTypes = 'sales'; wholesaleType = 'sales'; retailType = 'sales';
    pageTitle = 'Verilen Teklifler';
  } else if (isOrderOutgoingMode) {
    filterTypes = 'purchase_waybill'; wholesaleType = 'purchase_waybill'; retailType = 'purchase_waybill';
    pageTitle = 'Verilen Siparişler';
  } else if (isOrderIncomingMode) {
    filterTypes = 'sales_waybill'; wholesaleType = 'sales_waybill'; retailType = 'sales_waybill';
    pageTitle = 'Alınan Siparişler';
  } else if (isPurchaseWaybillMode && isReturnMode) {
    filterTypes = 'purchase_waybill_return'; wholesaleType = 'purchase_waybill_return'; retailType = 'purchase_waybill_return';
    pageTitle = 'Alış İade İrsaliyeleri';
  } else if (isPurchaseWaybillMode) {
    filterTypes = 'purchase_waybill'; wholesaleType = 'purchase_waybill'; retailType = 'purchase_waybill';
    pageTitle = 'Alış İrsaliyeleri';
  } else if (isWaybillMode && isReturnMode) {
    filterTypes = 'sales_waybill_return'; wholesaleType = 'sales_waybill_return'; retailType = 'sales_waybill_return';
    pageTitle = 'Satış İade İrsaliyeleri';
  } else if (isWaybillMode) {
    filterTypes = 'sales_waybill'; wholesaleType = 'sales_waybill'; retailType = 'sales_waybill';
    pageTitle = 'Satış İrsaliyeleri';
  } else if (isPurchaseMode && isReturnMode) {
    filterTypes = 'purchase_return'; wholesaleType = 'purchase_return'; retailType = 'purchase_return';
    pageTitle = 'Alış İade Faturaları';
  } else if (isPurchaseMode) {
    filterTypes = 'purchase'; wholesaleType = 'purchase'; retailType = 'purchase';
    pageTitle = 'Alış Faturaları';
  } else if (isReturnMode) {
    filterTypes = 'sales_return,retail_sale_return'; wholesaleType = 'sales_return'; retailType = 'retail_sale_return';
    pageTitle = 'Satış İade Faturaları';
  } else {
    filterTypes = 'sales,retail_sale'; wholesaleType = 'sales'; retailType = 'retail_sale';
    pageTitle = 'Satış Faturaları';
  }
  // Backend endpoint moda göre seçilir:
  //  - alış irsaliyesi → /purchase-waybills
  //  - satış irsaliyesi → /sales-waybills
  //  - alış faturası   → /purchase-invoices
  //  - satış faturası  → /sales-invoices
  const apiBase = isQuoteIncomingMode ? '/purchase-quotes'
                : isQuoteOutgoingMode ? '/sales-quotes'
                : isOrderOutgoingMode ? '/purchase-orders'
                : isOrderIncomingMode ? '/sales-orders'
                : isPurchaseWaybillMode ? '/purchase-waybills'
                : isWaybillMode ? '/sales-waybills'
                : isPurchaseMode ? '/purchase-invoices'
                : '/sales-invoices';
  const [activeTab, setActiveTab]   = useState(0);
  const { currencies: currencyList } = useCurrencies();
  const [invoices,  setInvoices]    = useState([]);
  // Satın alma cilası: fiyat geçmişi modal + 3-way match uyarıları
  const [priceHistoryFor, setPriceHistoryFor] = useState(null); // { stock_id, stock_name, line_idx }
  const [threeWayWarnings, setThreeWayWarnings] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedWaybillIds, setSelectedWaybillIds] = useState([]);
  const [convertingInvoice, setConvertingInvoice]   = useState(false);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [showPrintPicker, setShowPrintPicker] = useState(null);  // null | {docLabel, lines, header, totals}
  const [activityForQuote, setActivityForQuote] = useState(null);  // null | { id, doc_no, account_title }
  const [showConvertToOrder, setShowConvertToOrder] = useState(false);
  const [convertingToOrder, setConvertingToOrder] = useState(false);
  // Pergola maliyet bilgileri — kalem fiyatı değişince Karlılık % reaktif hesabı için
  const [pergolaCosts, setPergolaCosts] = useState({}); // { 'PRG001': { cost_try, cost_usd, cost_eur, profit_pct, ... } }
  const [filters, setFilters] = useState({ date_from: '', date_to: '', account_id: '', personnel_id: '', status: '', invoiced: 'all', valid_from: '', valid_to: '' });
  // Manuel aktarım (fatura editörü içinde)
  const [showWaybillImport, setShowWaybillImport]   = useState(false);
  const [showDocSettings, setShowDocSettings]       = useState(false);
  const [availableWaybills, setAvailableWaybills]   = useState([]);
  const [importingWaybills, setImportingWaybills]   = useState(false);
  const [importPickedIds, setImportPickedIds]       = useState([]);
  const [sourceWaybillIds, setSourceWaybillIds]     = useState([]);  // save'de backend'e gönderilir
  const [linkedDocs, setLinkedDocs]                 = useState([]);
  const [linkedDocsLoading, setLinkedDocsLoading]   = useState(false);
  const [linkedReceipts, setLinkedReceipts]         = useState([]); // cari tahsilat makbuzları
  const [linkedPayments, setLinkedPayments]         = useState([]); // cari tediye makbuzları
  const [total,     setTotal]       = useState(0);
  const [loading,   setLoading]     = useState(false);
  const [search,    setSearch]      = useState('');
  const [lookups,   setLookups]     = useState({
    accounts:[], warehouses:[], personnel:[], projects:[],
    stocks:[], units:[], withholdings:[], specialCodes1:[], specialCodes2:[],
    settings:{}, ecommercePlatforms:[], carriers:[]
  });
  const [selected,   setSelected]   = useState(null);
  const [isNew,      setIsNew]      = useState(false);
  const [isEditing,  setIsEditing]  = useState(false);
  const [isApprovalReadonly, setIsApprovalReadonly] = useState(false); // Bekleyen Onaylar'dan açıldıysa salt-okunur
  const [hasApprovalRecord, setHasApprovalRecord] = useState(false); // bu belge hiyerarşi sürecinden geçti mi
  const [saving,     setSaving]     = useState(false);
  const [msg,        setMsg]        = useState(null);
  const [showModal,  setShowModal]  = useState(false);
  const [showQuick,  setShowQuick]  = useState(null);
  const barcodeRef  = useRef(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [stockSearch,  setStockSearch]  = useState('');
  const [stockResults, setStockResults] = useState([]);
  const [activeLineIdx, setActiveLineIdx] = useState(null);
  // Çoklu stok seçim modalı
  const [showMultiStock, setShowMultiStock] = useState(false);
  const [multiStockSearch, setMultiStockSearch] = useState('');
  const [showSeriesPicker, setShowSeriesPicker] = useState(false);
  const [seriesList, setSeriesList] = useState([]);
  const [seriesPickerLoading, setSeriesPickerLoading] = useState(false);
  const [seriesSearch, setSeriesSearch] = useState('');
  const [selectedStockIds, setSelectedStockIds] = useState(() => new Set());
  // Hızlı ekleme modalları
  const [quickAddOpen, setQuickAddOpen] = useState(null); // 'cari' | 'personel' | 'stok' | null
  // Cari liste seçici modal'ı
  const [showAccountList, setShowAccountList] = useState(false);
  const [accountAddresses, setAccountAddresses] = useState([]); // seçili carinin sevk adresleri
  const [accountListSearch, setAccountListSearch] = useState('');
  // Aktif fiyat tanımı (cari + invoice_type'a göre resolve edilir)
  const [activePriceDef, setActivePriceDef] = useState(null);
  // Fiyat seçme modali — { lineIdx, stockId, stockName, items: [...] }
  const [pricePicker, setPricePicker] = useState(null);
  const [pricePickerLoading, setPricePickerLoading] = useState(false);

  const localCurrency = lookups.settings?.local_currency || 'TRY';
  const now = new Date();

  const [header, setHeader] = useState({
    invoice_type:'sales', invoice_number:'', invoice_date:now.toISOString().substring(0,10),
    invoice_time:now.toTimeString().substring(0,5), due_date:'', account_id:'',
    warehouse_id:'', personnel_id:'', project_id:'',
    is_vat_included:false, currency_code:'TRY', exchange_rate:1, local_currency:'TRY',
    special_code1:'', special_code2:'', special_code3:'', notes:'', internal_note:'',
    dispatch_number:'', order_number:'', status:'draft', order_status_id:'',
    e_doc_type:'', e_invoice_scenario:'', e_invoice_type_code:'',
    tax_exemption_code:'', tax_exemption_reason:'',
    bill_to_mode:'same', bill_to_customer_id:'', bill_to_name:'', bill_to_tax_number:'',
    bill_to_address:'', bill_to_city:'', bill_to_district:'', bill_to_country:'Turkiye',
    bill_to_phone:'', bill_to_email:'',
    ship_to_mode:'none', ship_to_name:'', ship_to_address:'', ship_to_city:'',
    ship_to_district:'', ship_to_country:'Turkiye', ship_to_phone:'',
    carrier_id:'', tracking_number:'',
    ecommerce_platform_id:'', ecommerce_order_no:'', ecommerce_order_date:'',
    is_ecommerce_order:false,
    commission_rate:0, commission_amount:0,
    delivery_date:'', dispatch_date:'',
    carrier_name:'', carrier_vkn:'', carrier_tracking_no:'',
    vehicle_plate:'', driver_name:'', driver_tckn:'',
    valid_until:'', quote_status_id:'',
    record_to_stock:true, record_to_account:true,
  });
  const [lines,  setLines]  = useState([]);
  const [mobileEditIdx, setMobileEditIdx] = useState(null);
  const [discountModalIdx, setDiscountModalIdx] = useState(null);
  const [barcodeScannerOpen, setBarcodeScannerOpen] = useState(false);
  const stockSearchWrapRef = useRef(null);
  const [stockDropdownPos, setStockDropdownPos] = useState(null);

  useEffect(() => {
    if (!stockResults.length) { setStockDropdownPos(null); return; }
    const updatePos = () => {
      const el = stockSearchWrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setStockDropdownPos({ top: r.bottom + 2, left: r.left, width: r.width });
    };
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [stockResults.length]);
  const [totals, setTotals] = useState({
    subtotal:0, discount_total:0, net_total:0, otv_total:0,
    vat_base:0, vat_total:0, withholding_total:0, grand_total:0, vat_breakdown:[]
  });

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search, invoice_type: filterTypes };
      if (filters.date_from)  params.date_from  = filters.date_from;
      if (filters.date_to)    params.date_to    = filters.date_to;
      if (filters.account_id) params.account_id = filters.account_id;
      if (isWaybillMode) {
        params.invoiced = filters.invoiced || 'all';
      } else {
        if (filters.status) params.status = filters.status;
      }
      if (isOrderMode) {
        if (filters.order_approved)      params.order_approved      = filters.order_approved;
        if (filters.accounting_approved) params.accounting_approved = filters.accounting_approved;
        if (filters.fulfillment_status)  params.fulfillment_status  = filters.fulfillment_status;
      }
      const res = await api.get(apiBase, { params });
      if (res.data.success) { setInvoices(res.data.data.items); setTotal(res.data.data.total); }
    } catch(e) { console.error(e); } finally { setLoading(false); }
  }, [search, apiBase, filterTypes, isWaybillMode, filters]);

  // Stats sadece invoice mode için (quote/order/waybill için yok)
  const isInvoiceMode = !isQuoteMode && !isOrderMode && !isWaybillMode;
  const fetchStats = useCallback(async () => {
    if (!isInvoiceMode) { setStats(null); return; }
    try {
      const r = await api.get(apiBase + '/stats', { params: { invoice_type: filterTypes } });
      if (r.data.success) setStats(r.data.data);
    } catch {}
  }, [apiBase, filterTypes, isInvoiceMode]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const fetchLookups = async (priceDefId) => {
    try {
      const params = priceDefId ? { price_definition_id: priceDefId } : {};
      // Sales orders lookups endpoint waybill ile aynı yolda mevcut değil, sales-waybills'inkini kullan
      const lookupBase = isQuoteIncomingMode ? '/purchase-waybills'
                       : isQuoteOutgoingMode ? '/sales-waybills'
                       : isOrderOutgoingMode ? '/purchase-waybills'
                       : isOrderIncomingMode ? '/sales-waybills'
                       : apiBase;
      const res = await api.get(lookupBase + '/lookups/all', { params });
      if (res.data.success) {
        const data = res.data.data || {};
        try {
          const br = await api.get('/branches', { params: { is_active: 'yes' } });
          if (br.data?.success) data.branches = br.data.data.items || [];
        } catch {}
        setLookups(data);
      }
      // Order modunda sipariş durumları da çek
      if (isOrderMode) {
        try {
          const sr = await api.get('/order-statuses');
          if (sr.data.success) setOrderStatuses(sr.data.data || []);
        } catch (_) {}
      }
      // Quote modunda teklif durumları çek
      if (isQuoteMode) {
        try {
          const qs = await api.get('/quote-statuses');
          if (qs.data.success) setQuoteStatuses(qs.data.data || []);
        } catch (_) {}
      }
    } catch(e) { console.error(e); }
  };
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [showOrderStatusMgr, setShowOrderStatusMgr] = useState(false);
  const [quoteStatuses, setQuoteStatuses] = useState([]);
  const [showQuoteStatusMgr, setShowQuoteStatusMgr] = useState(false);
  const [quoteActivities, setQuoteActivities] = useState([]);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [approvalInfo, setApprovalInfo] = useState(null); // { order_approved_at/by_name, accounting_approved_at/by_name }
  const [approvalSaving, setApprovalSaving] = useState(false);
  const [approvalHistory, setApprovalHistory] = useState(null); // { source_table, source_id, data: { actions, status, ... } } veya null
  const { user: currentUser } = useAuth();
  const { t: tt } = useTranslation();
  const isAdmin = currentUser?.role === 'admin';

  const fetchApprovalInfo = useCallback(async (orderId) => {
    if (!orderId || !isOrderMode) { setApprovalInfo(null); return; }
    try {
      const r = await api.get(apiBase + '/' + orderId + '/approval-info');
      if (r.data.success) setApprovalInfo(r.data.data);
    } catch (_) { setApprovalInfo(null); }
  }, [isOrderMode]);

  // ── Kısmi aktarım (kalem-bazlı) ───────────────────────────────────────────
  const [convertingOrder, setConvertingOrder] = useState(false);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [convertTarget, setConvertTarget] = useState('waybill');     // 'waybill' | 'invoice'
  const [convertItems, setConvertItems] = useState([]);              // [{ order_line_id, line_no, stock_label, total, transferred, remaining, qty }]
  const [transfers, setTransfers] = useState([]);                    // GET /:id/transfers cache

  const fetchTransfers = useCallback(async (orderId) => {
    if (!orderId || !isOrderMode) { setTransfers([]); return; }
    try {
      const r = await api.get(apiBase + '/' + orderId + '/transfers');
      if (r.data.success) setTransfers(r.data.data || []);
    } catch (_) { setTransfers([]); }
  }, [isOrderMode]);

  const openConvertModal = () => {
    if (!selected || !isOrderMode) return;
    // Onay yalnızca bir onay hiyerarşisi tanımlıysa istenir (backend de aynı kuralı uygular)
    if (approvalInfo?.approval_required && (!approvalInfo?.order_approved_at || !approvalInfo?.accounting_approved_at)) {
      alert('Aktarım için hem Sipariş Onayı hem Muhasebe Onayı gerekli.');
      return;
    }
    const items = (lines || []).filter(l => l.id && l.stock_id).map(l => {
      const total = parseFloat(l.quantity) || 0;
      const transferred = parseFloat(l.transferred_quantity) || 0;
      const remaining = Math.max(0, total - transferred);
      const stock = lookups.stocks?.find(s => String(s.id) === String(l.stock_id));
      const stockLabel = stock ? `${stock.code} - ${stock.name}` : (l.stock_name || '');
      return {
        order_line_id: l.id,
        line_no: l.line_no,
        stock_label: stockLabel,
        unit_code: l.unit_code || '',
        total, transferred, remaining,
        qty: remaining,
      };
    }).filter(x => x.remaining > 0);
    if (items.length === 0) {
      alert('Bu siparişte aktarılabilecek kalan kalem yok.');
      return;
    }
    setConvertTarget('waybill');
    setConvertItems(items);
    setConvertModalOpen(true);
  };
  const closeConvertModal = () => { setConvertModalOpen(false); setConvertItems([]); };

  const submitConvert = async () => {
    const picked = convertItems
      .map(it => ({ order_line_id: it.order_line_id, quantity: parseFloat(it.qty) || 0 }))
      .filter(it => it.quantity > 0);
    if (picked.length === 0) { alert('En az bir kalemden miktar girilmeli.'); return; }
    setConvertingOrder(true);
    try {
      const r = await api.post(apiBase + '/' + selected.id + '/convert', { target: convertTarget, items: picked });
      if (r.data.success) {
        const docNo = r.data.data?.target_document_number || '';
        closeConvertModal();
        // Modal'ı kapat ve listeyi yenile, detayı tekrar yükle
        const sid = selected.id;
        setShowModal(false); setSelected(null); setIsEditing(false); setIsNew(false);
        await fetchInvoices();
        alert(`Aktarıldı: ${convertTarget === 'waybill' ? 'İrsaliye' : 'Fatura'} ${docNo}`);
        // Aynı siparişi tekrar aç (aktarım sonrası kalan miktarları görsün)
        try { const rr = await api.get(apiBase + '/' + sid); if (rr.data.success) handleSelect(rr.data.data); } catch (_) {}
      } else {
        alert('Hata: ' + (r.data.message || 'Aktarım başarısız'));
      }
    } catch (e) { alert('Hata: ' + (e.response?.data?.message || e.message)); }
    finally { setConvertingOrder(false); }
  };

  const toggleApproval = async (type, approve) => {
    if (!selected || !isAdmin) return;
    setApprovalSaving(true);
    try {
      await api.post(apiBase + '/' + selected.id + '/approve', { type, approve });
      await Promise.all([fetchApprovalInfo(selected.id), fetchInvoices()]);
    } catch (e) {
      alert(e.response?.data?.message || 'Onay işlemi başarısız');
    } finally { setApprovalSaving(false); }
  };

  // Sipariş seçildiğinde onay bilgisini + aktarım geçmişini paralel çek
  useEffect(() => {
    if (selected?.id && isOrderMode) {
      fetchApprovalInfo(selected.id);
      fetchTransfers(selected.id);
    } else { setApprovalInfo(null); setTransfers([]); }
  }, [selected?.id, isOrderMode, fetchApprovalInfo, fetchTransfers]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  useEffect(() => { fetchLookups(); }, []);

  // Liste/mod değişince seçimi temizle
  useEffect(() => { setSelectedWaybillIds([]); }, [apiBase, search, isReturnMode]);

  // Deep-link: autoOpenId ile gelirse o belgeyi otomatik aç
  useEffect(() => {
    if (!autoOpenId) return;
    setIsApprovalReadonly(!!viewOnly);
    let cancelled = false;
    api.get(apiBase + '/' + autoOpenId).then(r => {
      if (!cancelled && r.data.success) handleSelect(r.data.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [autoOpenId, apiBase, viewOnly]);

  // Modal kapatınca readonly bayrağını sıfırla
  useEffect(() => { if (!showModal) setIsApprovalReadonly(false); }, [showModal]);

  // Bağlı Belgeler tab'ı tıklanınca lazy yükle
  useEffect(() => {
    if (activeTab !== 6 || !selected?.id) { return; }
    setLinkedDocsLoading(true);
    api.get(apiBase + '/' + selected.id + '/linked-documents')
      .then(r => { if (r.data.success) setLinkedDocs(r.data.data || []); })
      .catch(() => setLinkedDocs([]))
      .finally(() => setLinkedDocsLoading(false));
    // Bağlı tahsilat/tediye makbuzları
    const invType = isPurchaseMode ? 'purchase_invoice' : 'sales_invoice';
    api.get('/invoice-links/' + invType + '/' + selected.id)
      .then(r => {
        if (r.data?.success) {
          setLinkedReceipts(r.data.data.receipts || []);
          setLinkedPayments(r.data.data.payments || []);
        }
      })
      .catch(() => { setLinkedReceipts([]); setLinkedPayments([]); });
  }, [activeTab, selected?.id, apiBase, isPurchaseMode]);

  // Satın alma cilası: 3-way match — alış faturası açıldığında PO sapma kontrolü
  useEffect(() => {
    setThreeWayWarnings([]);
    if (!selected?.id || !isPurchaseMode || isReturnMode || isWaybillMode || isOrderMode || isQuoteMode) return;
    api.get('/purchase-insights/three-way-match/' + selected.id)
      .then(r => { if (r.data?.success) setThreeWayWarnings(r.data.data.warnings || []); })
      .catch(() => {});
  }, [selected?.id, isPurchaseMode, isReturnMode, isWaybillMode, isOrderMode, isQuoteMode]);

  // Manuel aktarım: cariye ait faturalanmamış irsaliyeleri yükle
  const openWaybillImport = async () => {
    if (!header.account_id) { alert('Önce cari seçin'); return; }
    // Hedef irsaliye yönü: sales/sales_return → sales waybill, purchase/purchase_return → purchase waybill
    const wbApi = (header.invoice_type === 'purchase' || header.invoice_type === 'purchase_return')
      ? '/purchase-waybills' : '/sales-waybills';
    const direction = (header.invoice_type === 'sales_return' || header.invoice_type === 'purchase_return') ? 'return' : 'forward';
    setImportingWaybills(true); setImportPickedIds([]);
    try {
      const res = await api.get(wbApi + '/available-for-invoice', { params: { account_id: header.account_id, direction } });
      if (res.data.success) {
        setAvailableWaybills(res.data.data || []);
        setShowWaybillImport(true);
      } else {
        alert(res.data.message || 'İrsaliyeler getirilemedi');
      }
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.message || err.message));
    } finally { setImportingWaybills(false); }
  };

  // Seçili irsaliyelerin satırlarını fatura kalemlerine ekle
  const importPickedWaybillsLines = () => {
    if (importPickedIds.length === 0) return;
    const picked = availableWaybills.filter(w => importPickedIds.includes(w.id));
    const newLines = [];
    let lineNo = lines.length;
    for (const wb of picked) {
      for (const l of (wb.lines || [])) {
        lineNo += 1;
        newLines.push({
          line_no: lineNo,
          stock_id: l.stock_id,
          stock_code: l.stock_code,
          stock_name: l.stock_name,
          unit_id: l.unit_id, unit_type: l.unit_type, unit_ratio: l.unit_ratio,
          quantity: l.quantity, quantity_base: l.quantity_base,
          unit_price: l.unit_price, unit_price_net: l.unit_price_net,
          is_vat_included: l.is_vat_included,
          discount_rate1: l.discount_rate1, discount_rate2: l.discount_rate2,
          discount_rate3: l.discount_rate3, discount_rate4: l.discount_rate4, discount_rate5: l.discount_rate5,
          discount_amount: l.discount_amount,
          otv_rate: l.otv_rate, otv_amount: l.otv_amount,
          vat_rate: l.vat_rate, vat_amount: l.vat_amount,
          withholding_rate: l.withholding_rate, withholding_amount: l.withholding_amount,
          line_subtotal: l.line_subtotal, line_discount: l.line_discount,
          line_net: l.line_net, line_otv: l.line_otv,
          line_vat_base: l.line_vat_base, line_vat: l.line_vat,
          line_withholding: l.line_withholding, line_total: l.line_total,
          barcode: l.barcode || l.stock_barcode, description: l.description,
          warehouse_id: l.warehouse_id,
          personnel_id: l.personnel_id || '',
        });
      }
    }
    // Boş line'ları temizle, sonra ekle
    const filtered = lines.filter(l => l.stock_id || l.quantity);
    const merged = [...filtered, ...newLines];
    setLines(merged);
    calcTotals(merged);
    setSourceWaybillIds(prev => [...new Set([...prev, ...importPickedIds])]);
    setShowWaybillImport(false);
    setMsg({ type: 'success', text: `${picked.length} irsaliye → ${newLines.length} kalem aktarıldı. Kontrol edip Kaydet'e basın.` });
  };

  // İrsaliye → Fatura aktarımı (otomatik akış — listede)
  const handleConvertToInvoice = async () => {
    if (selectedWaybillIds.length === 0) return;
    const selectedWaybills = invoices.filter(w => selectedWaybillIds.includes(w.id));
    const accountIds = [...new Set(selectedWaybills.map(w => w.account_id))];
    if (accountIds.length > 1) {
      alert('Tüm seçili irsaliyeler aynı cariye ait olmalı.');
      return;
    }
    const types = [...new Set(selectedWaybills.map(w => w.invoice_type))];
    if (types.length > 1) {
      alert('Tüm seçili irsaliyeler aynı tip olmalı (sevk veya iade).');
      return;
    }
    const notApproved = selectedWaybills.filter(w => w.status !== 'approved');
    if (notApproved.length) {
      alert('Sadece ONAYLI irsaliyeler aktarılabilir. Sorunlu: ' + notApproved.map(w => w.document_number).join(', '));
      return;
    }
    const accountTitle = selectedWaybills[0].account_title;
    if (!window.confirm(`${selectedWaybillIds.length} irsaliye "${accountTitle}" cari için tek faturaya aktarılacak.\n\nDevam edilsin mi?`)) return;
    setConvertingInvoice(true);
    try {
      const res = await api.post(apiBase + '/convert-to-invoice', { waybill_ids: selectedWaybillIds });
      if (res.data.success) {
        alert(res.data.message || 'Aktarım başarılı');
        setSelectedWaybillIds([]);
        await fetchInvoices();
      } else {
        alert('Hata: ' + (res.data.message || 'Bilinmeyen hata'));
      }
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.message || err.message));
    } finally {
      setConvertingInvoice(false);
    }
  };
  useEffect(() => {
    const defWarehouse = lookups.settings?.default_warehouse_id || '';
    if (defWarehouse && !selected) {
      setHeader(prev => ({ ...prev, warehouse_id: prev.warehouse_id || defWarehouse }));
      setLines(prev => prev.map(l => ({ ...l, warehouse_id: l.warehouse_id || defWarehouse })));
    }
  }, [lookups.settings]);

  // invoice_type / account_id / invoice_date değişince uygun fiyat tanımını çöz ve lookups'ı yeniden yükle
  useEffect(() => {
    if (!showModal) return;
    const it = header.invoice_type;
    const group = (it === 'retail_sale' || it === 'retail_sale_return') ? 'perakende'
                : (it === 'sales' || it === 'sales_return' || it === 'sales_waybill' || it === 'sales_waybill_return') ? 'satis'
                : (it === 'purchase' || it === 'purchase_return' || it === 'purchase_waybill' || it === 'purchase_waybill_return') ? 'alis'
                : null;
    if (!group) return;
    const params = { price_group: group };
    if (header.account_id) params.account_id = header.account_id;
    if (header.invoice_date) params.date = header.invoice_date;
    api.get('/price-definitions/resolve', { params })
      .then(r => {
        const pd = r.data?.success ? r.data.data : null;
        setActivePriceDef(pd);
        if (pd?.id !== activePriceDef?.id) {
          fetchLookups(pd?.id || null);
        }
      })
      .catch(() => {});
  }, [showModal, header.invoice_type, header.account_id, header.invoice_date]);

  const searchStocks = (q) => {
    if (q.length < 2) { setStockResults([]); return; }
    setStockResults(lookups.stocks.filter(s =>
      s.name?.toLowerCase().includes(q.toLowerCase()) ||
      s.code?.toLowerCase().includes(q.toLowerCase()) ||
      (s.barcode||'').includes(q)
    ).slice(0, 20));
  };

  const calcLine = (ln, isVatInc, exchRate) => {
    const qty      = parseFloat(ln.quantity) || 0;
    const price    = parseFloat(ln.unit_price) || 0;
    const er       = parseFloat(exchRate) || 1;
    let subtotal   = qty * price;
    let net        = subtotal;
    [ln.discount_rate1,ln.discount_rate2,ln.discount_rate3,ln.discount_rate4,ln.discount_rate5]
      .forEach(r => { const rv=parseFloat(r)||0; if(rv>0) net=net*(1-rv/100); });
    const discAmount = subtotal - net;
    const otvRate  = parseFloat(ln.otv_rate) || 0;
    const vatRate  = parseFloat(ln.vat_rate) || 0;
    const whRate   = parseFloat(ln.withholding_rate) || 0;
    let lineNet, lineOtv, lineVatBase, lineVat, lineWh, lineTotal, upNet;
    if (isVatInc) {
      const d=1+vatRate/100; lineNet=net/d; lineOtv=lineNet*otvRate/100;
      lineVatBase=lineNet; lineVat=lineVatBase*vatRate/100;
      lineWh=lineVat*whRate/100; lineTotal=lineVatBase+lineVat-lineWh; upNet=price/d;
    } else {
      lineNet=net; lineOtv=lineNet*otvRate/100;
      lineVatBase=lineNet+lineOtv; lineVat=lineVatBase*vatRate/100;
      lineWh=lineVat*whRate/100; lineTotal=lineVatBase+lineVat-lineWh; upNet=price;
    }
    const upLocal        = +(upNet * er).toFixed(4);
    const lineTotalLocal = +(lineTotal * er).toFixed(2);
    const qtyBase      = +(qty * (parseFloat(ln.unit_ratio)||1)).toFixed(3);
    return {
      ...ln,
      unit_price_net:    +upNet.toFixed(4),
      unit_price_local:  upLocal,
      quantity_base:     qtyBase,
      line_subtotal:     +subtotal.toFixed(2),
      line_discount:     +discAmount.toFixed(2),
      line_net:          +lineNet.toFixed(2),
      line_otv:          +lineOtv.toFixed(2),
      line_vat_base:     +lineVatBase.toFixed(2),
      line_vat:          +lineVat.toFixed(2),
      line_withholding:  +lineWh.toFixed(2),
      line_total:        +lineTotal.toFixed(2),
      line_total_local:  lineTotalLocal,
      discount_amount:   +discAmount.toFixed(2),
      otv_amount:        +lineOtv.toFixed(2),
      vat_amount:        +lineVat.toFixed(2),
      withholding_amount:+lineWh.toFixed(2),
    };
  };

  const computeTotals = (lns) => {
    let sub=0,disc=0,net=0,otv=0,vb=0,vt=0,wh=0,gt=0; const vatMap={};
    lns.forEach(l => {
      sub+=l.line_subtotal; disc+=l.line_discount; net+=l.line_net; otv+=l.line_otv;
      vb+=l.line_vat_base; vt+=l.line_vat; wh+=l.line_withholding; gt+=l.line_total;
      const vr=l.vat_rate||0;
      if (!vatMap[vr]) vatMap[vr]={rate:vr,base:0,amount:0};
      vatMap[vr].base+=l.line_vat_base; vatMap[vr].amount+=l.line_vat;
    });
    return {
      subtotal:+sub.toFixed(2), discount_total:+disc.toFixed(2),
      net_total:+net.toFixed(2), otv_total:+otv.toFixed(2),
      vat_base:+vb.toFixed(2), vat_total:+vt.toFixed(2),
      withholding_total:+wh.toFixed(2), grand_total:+gt.toFixed(2),
      vat_breakdown: Object.values(vatMap).map(v=>({rate:v.rate,base:+v.base.toFixed(2),amount:+v.amount.toFixed(2)}))
    };
  };
  const calcTotals = (lns) => setTotals(computeTotals(lns));

  // Bir bundle stoğunu bileşen satırlarına patlat (KDV-hariç orantılı dağıtım, her bileşen kendi KDV'si).
  const explodeBundleIntoLines = async (stk, baseLine) => {
    try {
      const pr = await api.get(`/stock-bundles/${stk.id}/preview`, { params: { quantity: parseFloat(baseLine.quantity) || 1, unit_price: parseFloat(baseLine.unit_price) || 0 } });
      const comps = pr.data?.data?.lines || [];
      if (!comps.length) return null;
      return comps.map(c => {
        const cstk = lookups.stocks.find(s => s.id === c.stock_id) || {};
        let cl = applyStock({ ...emptyLine, warehouse_id: baseLine.warehouse_id || header.warehouse_id || '', personnel_id: baseLine.personnel_id || header.personnel_id || '' },
          { ...cstk, id: c.stock_id, name: c.stock_name || cstk.name, code: c.stock_code || cstk.code, vat_rate: c.vat_rate != null ? c.vat_rate : cstk.vat_rate, pd_price1: 0, pd_vat_included: false }, header.exchange_rate);
        cl = calcLine({ ...cl, unit_price: c.unit_price, is_vat_included: false, quantity: c.quantity, description: (stk.name || 'Paket') + ' içeriği', _from_bundle: true }, false, header.exchange_rate);
        return cl;
      });
    } catch (_) { return null; }
  };

  // validLines içindeki bundle satırlarını bileşenlerine patlat (kaydetmeden önce). Bundle yoksa aynısını döner.
  const explodeBundlesForSave = async (srcLines) => {
    const hasBundle = srcLines.some(l => { const s = lookups.stocks.find(x => x.id === l.stock_id); return s && s.is_bundle && !l._from_bundle; });
    if (!hasBundle) return null;
    const out = [];
    for (const l of srcLines) {
      const stk = lookups.stocks.find(x => x.id === l.stock_id);
      if (stk && stk.is_bundle && !l._from_bundle) {
        const comps = await explodeBundleIntoLines(stk, l);
        if (comps && comps.length) { out.push(...comps); continue; }
      }
      out.push(l);
    }
    return out;
  };

  const getStockUnits = (stockId) => {
    const stk = lookups.stocks.find(s => s.id===stockId); if (!stk) return [];
    const u = [];
    if (stk.unit_id)  u.push({id:stk.unit_id, code:stk.unit_code,  name:stk.unit_name,  type:'unit1', ratio:1});
    if (stk.unit2_id) u.push({id:stk.unit2_id,code:stk.unit2_code, name:stk.unit2_name, type:'unit2', ratio:stk.unit2_ratio||1});
    if (stk.unit3_id) u.push({id:stk.unit3_id,code:stk.unit3_code, name:stk.unit3_name, type:'unit3', ratio:stk.unit3_ratio||1});
    return u;
  };

  // Fiyat seçici aç — fatura türüne uygun fiyat grubundaki tanımları getir
  const openPricePicker = async (lineIdx) => {
    const ln = lines[lineIdx];
    if (!ln?.stock_id) return;
    const stk = lookups.stocks.find(s => s.id === ln.stock_id);
    setPricePicker({ lineIdx, stockId: ln.stock_id, stockName: stk?.name || '', items: [] });
    setPricePickerLoading(true);
    try {
      const r = await api.get('/stocks/' + ln.stock_id + '/prices');
      if (r.data.success) {
        const it = header.invoice_type;
        const targetGroup = (it === 'retail_sale' || it === 'retail_sale_return') ? 'perakende'
                          : (it === 'purchase' || it === 'purchase_return') ? 'alis'
                          : 'satis';
        const items = (r.data.data || []).filter(d => d.price_group === targetGroup);
        setPricePicker(p => ({ ...p, items }));
      }
    } catch (e) { console.error('price picker:', e); }
    finally { setPricePickerLoading(false); }
  };

  // Seçilen fiyat tanımının price1/2/3'ünü o satıra uygula
  const pickPrice = (item, priceField) => {
    if (!pricePicker) return;
    const idx = pricePicker.lineIdx;
    const raw = parseFloat(item[priceField]) || 0;
    if (raw <= 0) return;
    const stk = lookups.stocks.find(s => s.id === pricePicker.stockId);
    const stockCur = item.item_currency_code || item.def_currency_code || stk?.stock_currency || localCurrency;
    const rates = lookups.exchangeRates || {};
    const stockRate = stockCur === localCurrency ? 1 : (parseFloat(rates[stockCur]?.selling_rate) || 1);
    const er = parseFloat(header.exchange_rate) || 1;
    const localP = +(raw * stockRate).toFixed(4);
    const txP = er > 0 ? +(localP / er).toFixed(4) : localP;
    const vatInc = !!item.vat_included;
    setLines(prev => {
      const nl = [...prev];
      nl[idx] = calcLine({ ...nl[idx], unit_price: txP, unit_price_local: localP, is_vat_included: vatInc, price_type: item.code || item.name || '1' }, vatInc, header.exchange_rate);
      calcTotals(nl);
      return nl;
    });
    setPricePicker(null);
  };

  const applyStock = (ln, stk, er) => {
    const u = getStockUnits(stk.id);
    // Aktif fiyat tanımı (alış / satış / perakende) çözüldüyse stk.pd_price1 dolu gelir.
    // Tanım yoksa veya fiyat girilmemişse 0 — kullanıcı fiyat seçici / manuel düzenleme yapar.
    const pdPrice = parseFloat(stk.pd_price1) || 0;
    const salePrice = pdPrice;
    const vatInc = !!stk.pd_vat_included;
    const priceCur = stk.pd_currency_code || activePriceDef?.currency_code || stk.stock_currency || localCurrency;
    const ptCode = activePriceDef?.code || activePriceDef?.name || (isPurchaseMode ? 'purchase' : '1');
    const stockCur  = priceCur;
    const invoiceEr  = parseFloat(er) || parseFloat(header.exchange_rate) || 1;
    const rates = lookups.exchangeRates || {};
    const stockRate    = stockCur === localCurrency ? 1 : (parseFloat(rates[stockCur]?.selling_rate) || 1);
    const stockUnitPriceLocal = +(salePrice * stockRate).toFixed(4);
    const stockUnitPrice      = invoiceEr > 0 ? +(stockUnitPriceLocal / invoiceEr).toFixed(4) : stockUnitPriceLocal;
    // Mevcut satırda kullanıcı bir fiyat girmişse (manuel ürün vs.) stok fiyatını YAZMA.
    const existingPrice = parseFloat(ln?.unit_price) || 0;
    const existingPriceLocal = parseFloat(ln?.unit_price_local) || 0;
    const keepUserPrice = existingPrice > 0;
    const unitPrice      = keepUserPrice ? existingPrice      : stockUnitPrice;
    const unitPriceLocal = keepUserPrice ? existingPriceLocal : stockUnitPriceLocal;
    return calcLine({
      ...ln,
      stock_id: stk.id, stock_name: stk.name, stock_code: stk.code,
      barcode: stk.barcode || '',
      unit_id: stk.unit_id, unit_name: stk.unit_name || '',
      unit2_id: stk.unit2_id||null, unit2_name: stk.unit2_name||'',
      unit3_id: stk.unit3_id||null, unit3_name: stk.unit3_name||'',
      unit_type: 'unit1', unit_ratio: 1,
      quantity: parseFloat(ln?.quantity) > 0 ? ln.quantity : 1,
      unit_price: unitPrice,
      unit_price_local: unitPriceLocal,
      vat_rate: parseFloat(ln?.vat_rate) >= 0 ? ln.vat_rate : (stk.vat_rate||20),
      otv_rate: stk.otv_rate||0,
      withholding_rate: stk.withholding_rate||0,
      units: u, track_serial: stk.track_serial, track_lot: stk.track_lot,
      warehouse_id: ln.warehouse_id || header.warehouse_id || '',
      personnel_id: ln.personnel_id || header.personnel_id || '',
      color_id: stk.color_id || null, size_id: stk.size_id || null,
      color_name: stk.color_name || '', size_name: stk.size_name || '',
      // Kullanıcı fiyatı korunuyorsa kdv-dahil flag'i de değiştirme
      is_vat_included: keepUserPrice ? !!ln.is_vat_included : vatInc,
      price_type: ptCode,
    }, keepUserPrice ? !!ln.is_vat_included : vatInc, er || header.exchange_rate);
  };

  const updateLine = (idx, key, val) => {
    setLines(prev => {
      const nl = [...prev];
      nl[idx] = {...nl[idx], [key]: val};
      if (key==='stock_id') {
        const stk = lookups.stocks.find(s=>s.id===val);
        if (stk) nl[idx] = applyStock(nl[idx], stk, header.exchange_rate);
        // Stok seçilince manuel ürün adını temizle — AMA tekliflerde özel açıklama için sakla.
        // Verilen/Alınan teklifte kullanıcı stok seçtikten sonra teklife özel ad eklemek isteyebilir.
        if (val && !isQuoteMode) nl[idx].manual_stock_name = '';
      } else if (key==='unit_type') {
        const u = nl[idx].units?.find(x=>x.type===val);
        if (u) {
          const oldRatio = parseFloat(nl[idx].unit_ratio) || 1;
          const newRatio = parseFloat(u.ratio) || 1;
          nl[idx].unit_id    = u.id;
          nl[idx].unit_ratio = u.ratio;
          nl[idx].unit_name  = u.name || u.code;
          nl[idx].unit_price = +(parseFloat(nl[idx].unit_price) * newRatio / oldRatio).toFixed(4);
        }
        nl[idx] = calcLine(nl[idx], nl[idx].is_vat_included, header.exchange_rate);
      } else if (key==='unit_price_local') {
        const er = parseFloat(header.exchange_rate)||1;
        nl[idx].unit_price = er > 0 ? +(parseFloat(val) / er).toFixed(4) : parseFloat(val)||0;
        nl[idx] = calcLine(nl[idx], nl[idx].is_vat_included, header.exchange_rate);
      } else {
        nl[idx] = calcLine(nl[idx], nl[idx].is_vat_included, header.exchange_rate);
      }
      // Pergola kaynaklı satırlarda fiyat/quantity/kdv değişince Karlılık % yeniden hesapla
      // Önce source_pergola_calc_id (UUID), yoksa eski description regex'ten doc_no
      if (['unit_price', 'unit_price_local', 'quantity', 'discount_rate1', 'discount_rate2', 'discount_rate3', 'vat_rate', 'is_vat_included'].includes(key)) {
        const calcKey = nl[idx].source_pergola_calc_id
          || (() => { const m = (nl[idx].description || '').match(/Pergola\s+([A-Za-z0-9_-]+)/i); return m ? m[1] : null; })();
        if (calcKey) {
          const costInfo = pergolaCosts[calcKey];
          const cur = (header.currency_code || 'TRY').toUpperCase();
          const costMap = { TRY: costInfo?.cost_try, USD: costInfo?.cost_usd, EUR: costInfo?.cost_eur };
          const cost = costMap[cur];
          if (cost != null && cost > 0) {
            const newSalesNet = parseFloat(nl[idx].unit_price_net) || parseFloat(nl[idx].unit_price) || 0;
            const newProfit = ((newSalesNet - cost) / cost) * 100;
            nl[idx].profit_pct = +newProfit.toFixed(4);
          }
        }
      }
      calcTotals(nl);
      return nl;
    });
  };

  const pickStock = (stk, idx) => {
    if (!header.account_id) { setMsg({type:'error',text:'Lutfen once musteri secin!'}); setActiveTab(0); return; }
    setLines(prev => {
      const nl = [...prev];
      nl[idx] = applyStock(nl[idx], stk, header.exchange_rate);
      calcTotals(nl);
      return nl;
    });
    setStockResults([]); setStockSearch(''); setActiveLineIdx(null);
  };

  const addLine  = () => setLines(prev => [...prev, {...emptyLine, warehouse_id: header.warehouse_id||'', personnel_id: header.personnel_id||''}]);
  const removeLine = (idx) => {
    const nl = lines.filter((_,i)=>i!==idx); setLines(nl); calcTotals(nl);
  };

  // 🎁 Kampanya & Paket uygula — bundle satırlarını bileşenlerine patlatır, satır + sepet
  // kampanyalarını uygular. İdempotent: _from_bundle / _camp_applied / _basket_applied işaretli;
  // tekrar tıklamada katlanmaz, manuel iskontolu (rate dolu) satırlara DOKUNMAZ.
  const [campaignBusy, setCampaignBusy] = useState(false);
  const applyCampaignsAndBundles = async () => {
    if (!header.account_id) { setMsg({ type: 'error', text: 'Önce müşteri seçin' }); return; }
    setCampaignBusy(true);
    try {
      let working = [...lines];
      // 1) Bundle patlatma
      const exploded = [];
      for (const ln of working) {
        const stk = lookups.stocks.find(s => s.id === ln.stock_id);
        if (ln.stock_id && stk && stk.is_bundle && !ln._from_bundle) {
          try {
            const pr = await api.get(`/stock-bundles/${ln.stock_id}/preview`, { params: { quantity: parseFloat(ln.quantity) || 1, unit_price: parseFloat(ln.unit_price) || 0 } });
            const comps = pr.data?.data?.lines || [];
            if (comps.length) {
              for (const c of comps) {
                const cstk = lookups.stocks.find(s => s.id === c.stock_id) || {};
                let cl = applyStock({ ...emptyLine, warehouse_id: ln.warehouse_id || header.warehouse_id || '', personnel_id: ln.personnel_id || header.personnel_id || '' },
                  { ...cstk, id: c.stock_id, name: c.stock_name || cstk.name, code: c.stock_code || cstk.code, vat_rate: c.vat_rate != null ? c.vat_rate : cstk.vat_rate, pd_price1: 0, pd_vat_included: false }, header.exchange_rate);
                cl = calcLine({ ...cl, unit_price: c.unit_price, is_vat_included: false, quantity: c.quantity, description: c.description || ((ln.stock_name || 'Paket') + ' içeriği'), _from_bundle: true }, false, header.exchange_rate);
                exploded.push(cl);
              }
              continue;
            }
          } catch (_) { /* patlatma başarısızsa paketi olduğu gibi bırak */ }
        }
        exploded.push(ln);
      }
      working = exploded;

      // 2) Kampanya değerlendir
      const it = header.invoice_type || '';
      const channel = it.includes('retail') ? 'retail' : 'wholesale';
      const applied = [];
      let nudgeMsg = null;
      try {
        const ev = await api.post('/campaigns/evaluate', {
          lines: working.map(l => ({ stock_id: l.stock_id, quantity: parseFloat(l.quantity) || 0, unit_price: parseFloat(l.unit_price) || 0, line_net: parseFloat(l.line_net) || 0 })),
          channel, date: header.invoice_date || null,
        });
        if (ev.data?.success) {
          const results = ev.data.data.results || [];
          const basket = ev.data.data.basket || null;
          const hasRate = l => [1, 2, 3, 4, 5].some(n => parseFloat(l['discount_rate' + n]) > 0);
          // Satır kampanyaları (yalnız iskontosuz + daha önce uygulanmamış satırlara)
          working = working.map((l, i) => {
            const r = results[i];
            if (!r || !l.stock_id || l._camp_applied || hasRate(l)) return l;
            let nl = { ...l, _camp_applied: true, _camp_name: r.campaignName };
            if (r.apply.kind === 'pct') {
              nl.discount_rate1 = r.apply.rates[0] || 0; nl.discount_rate2 = r.apply.rates[1] || 0;
              nl.discount_rate3 = r.apply.rates[2] || 0; nl.discount_rate4 = r.apply.rates[3] || 0; nl.discount_rate5 = r.apply.rates[4] || 0;
            } else if (r.apply.kind === 'fixed_price') {
              nl.unit_price = r.apply.fixedUnitPrice;
            } else if (r.apply.kind === 'amount') {
              const p = parseFloat(nl.unit_price) || 0;
              nl.discount_rate1 = p > 0 ? +((Math.min(r.apply.unitDiscount, p) / p) * 100).toFixed(4) : 0;
            }
            nl = calcLine(nl, nl.is_vat_included, header.exchange_rate);
            applied.push(r.campaignName);
            return nl;
          });
          // 3) Sepet indirimini line_net'e orantılı dağıt (boş rate slotuna)
          if (basket && basket.discount > 0) {
            const idxs = working.map((l, i) => ({ l, i })).filter(x => !x.l._basket_applied && (parseFloat(x.l.line_net) || 0) > 0);
            const sum = idxs.reduce((s, x) => s + (parseFloat(x.l.line_net) || 0), 0);
            if (sum > 0) {
              let acc = 0;
              idxs.forEach((x, k) => {
                const net = parseFloat(x.l.line_net) || 0;
                const share = k === idxs.length - 1 ? +(basket.discount - acc).toFixed(2) : +(basket.discount * net / sum).toFixed(2);
                if (k !== idxs.length - 1) acc = +(acc + share).toFixed(2);
                if (share <= 0) return;
                let nl = { ...x.l, _basket_applied: true };
                const slot = ['discount_rate1', 'discount_rate2', 'discount_rate3', 'discount_rate4', 'discount_rate5'].find(s => !(parseFloat(nl[s]) > 0));
                if (slot) { nl[slot] = +((share / net) * 100).toFixed(4); nl = calcLine(nl, nl.is_vat_included, header.exchange_rate); }
                working[x.i] = nl;
              });
              applied.push(basket.reason);
            }
          }
          if (basket && basket.nudge && (!basket.discount || basket.discount === 0)) {
            nudgeMsg = `💡 ${basket.nudge.remaining} TL daha → ${basket.nudge.nextReward} indirim kazanırsınız`;
          }
        }
      } catch (_) { /* kampanya servisi hata verirse sadece bundle patlatma uygulanmış olur */ }

      setLines(working); calcTotals(working);
      if (applied.length) setMsg({ type: 'success', text: '🎁 Kampanya uygulandı: ' + [...new Set(applied)].join(', ') });
      else if (nudgeMsg) setMsg({ type: 'success', text: nudgeMsg });
      else setMsg({ type: 'success', text: 'Uygun kampanya bulunamadı (paketler patlatıldı).' });
      setTimeout(() => setMsg(null), 4000);
    } catch (e) {
      setMsg({ type: 'error', text: 'Kampanya uygulanamadı: ' + (e.response?.data?.message || e.message) });
    } finally { setCampaignBusy(false); }
  };

  // Çoklu stok seçim
  const openMultiStock = () => {
    if (!header.account_id) { setMsg({type:'error',text:'Once musteri secin'}); setActiveTab(0); return; }
    setMultiStockSearch('');
    setSelectedStockIds(new Set());
    setShowMultiStock(true);
  };
  const toggleStockSelection = (id) => {
    setSelectedStockIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (stocks) => {
    setSelectedStockIds(prev => {
      const allIds = stocks.map(s => s.id);
      const allSelected = allIds.every(id => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        allIds.forEach(id => next.delete(id));
        return next;
      } else {
        const next = new Set(prev);
        allIds.forEach(id => next.add(id));
        return next;
      }
    });
  };
  const addSelectedStocks = () => {
    if (selectedStockIds.size === 0) { setShowMultiStock(false); return; }
    const selectedStocks = lookups.stocks.filter(s => selectedStockIds.has(s.id));
    setLines(prev => {
      const nl = [...prev];
      let startIdx = 0;
      // Eğer son satır tamamen boşsa (stok seçilmemiş), onu ilk seçimle değiştir
      if (nl.length > 0 && !nl[nl.length - 1].stock_id) {
        nl[nl.length - 1] = applyStock(nl[nl.length - 1], selectedStocks[0], header.exchange_rate);
        startIdx = 1;
      }
      for (let i = startIdx; i < selectedStocks.length; i++) {
        nl.push(applyStock({ ...emptyLine, warehouse_id: header.warehouse_id || '', personnel_id: header.personnel_id || '' }, selectedStocks[i], header.exchange_rate));
      }
      calcTotals(nl);
      return nl;
    });
    setShowMultiStock(false);
    setSelectedStockIds(new Set());
  };
  const filteredMultiStocks = !multiStockSearch
    ? lookups.stocks
    : lookups.stocks.filter(s =>
        (s.code || '').toLowerCase().includes(multiStockSearch.toLowerCase()) ||
        (s.name || '').toLowerCase().includes(multiStockSearch.toLowerCase()) ||
        (s.barcode || '').includes(multiStockSearch)
      );

  const openSeriesPicker = async () => {
    if (!header.account_id) { setMsg({type:'error', text:'Önce müşteri seçin'}); setActiveTab(0); return; }
    setShowSeriesPicker(true);
    setSeriesSearch('');
    setSeriesPickerLoading(true);
    try {
      const r = await api.get('/stocks/series');
      if (r.data.success) setSeriesList(r.data.data || []);
    } catch {} finally { setSeriesPickerLoading(false); }
  };

  const pickSeries = async (sid) => {
    try {
      const r = await api.get('/stocks/series/' + sid);
      if (r.data.success) {
        const series = r.data.data;
        const nl = expandSeriesIntoLines(series.items || [], lines);
        setLines(nl); calcTotals(nl);
        setShowSeriesPicker(false);
        setMsg({type:'success', text:`Seri eklendi: ${series.name} (${(series.items||[]).length} kalem)`});
        setTimeout(()=>setMsg(null), 3000);
      }
    } catch (e) { setMsg({type:'error', text: e.response?.data?.message || e.message}); }
  };

  const expandSeriesIntoLines = (seriesItems, currentLines) => {
    let nl = [...currentLines];
    for (const it of seriesItems) {
      const stk = lookups.stocks.find(s => s.id === it.stock_id);
      if (!stk) continue;
      let newLine = applyStock({...emptyLine, warehouse_id: header.warehouse_id||'', personnel_id: header.personnel_id||''}, stk, header.exchange_rate);
      const qty = parseFloat(it.quantity) || 1;
      newLine = calcLine({...newLine, quantity: qty, quantity_base: +(qty*(parseFloat(newLine.unit_ratio)||1)).toFixed(3)}, newLine.is_vat_included, header.exchange_rate);
      nl.push(newLine);
    }
    return nl;
  };

  const handleBarcodeScan = async (overrideCode = null) => {
    const inputVal = overrideCode != null ? String(overrideCode) : barcodeInput;
    if (!inputVal.trim()) return;
    if (!header.account_id) { setMsg({type:'error',text:'Lutfen once musteri secin!'}); setActiveTab(0); return; }
    const raw = inputVal.trim();
    const useScale = lookups.settings?.use_scale === 'true';
    const startCode = (lookups.settings?.weighted_product_start_code || '').trim();
    let barcode = raw;
    let forcedQty = null;

    if (useScale && startCode && raw.startsWith(startCode)) {
      // Tartili urun: PP(2) + BBBBB(5) + QQQQQ(5) + C(1)
      barcode = raw.substring(startCode.length, startCode.length + 5);
      const qtyRaw = parseInt(raw.substring(startCode.length + 5, startCode.length + 10)) || 0;
      forcedQty = qtyRaw / 1000;
    }

    try {
      const res = await api.get(apiBase + '/stock-by-barcode/' + barcode);
      if (res.data.success) {
        const stkFull = res.data.data;
        const stk = {...(lookups.stocks.find(s=>s.id===stkFull.id)||{}), ...stkFull};
        let newLine = applyStock({...emptyLine, warehouse_id: header.warehouse_id||'', personnel_id: header.personnel_id||''}, stk, header.exchange_rate);
        // 2./3. birim barkodu okutulduysa: miktar = unit ratio (1 koli = 12 adet vb.)
        // matched_unit_level: 1=ana, 2=2.birim, 3=3.birim ; qty_multiplier = ratio
        const mult = parseFloat(stkFull.qty_multiplier) || 1;
        if (forcedQty !== null && stkFull.is_weighted) {
          newLine = calcLine({...newLine, quantity: forcedQty, quantity_base: +(forcedQty*(parseFloat(newLine.unit_ratio)||1)).toFixed(3)}, newLine.is_vat_included, header.exchange_rate);
        } else if (mult > 1 && stkFull.matched_unit_level > 1) {
          // Barkod 2./3. birime ait — miktarı çarpana göre ayarla
          newLine = calcLine({
            ...newLine,
            quantity: mult,
            quantity_base: +(mult * (parseFloat(newLine.unit_ratio) || 1)).toFixed(3),
          }, newLine.is_vat_included, header.exchange_rate);
          setMsg({ type: 'success', text: `${stkFull.code} → ${stkFull.matched_unit_level}. birim barkodu (×${mult}) okutuldu` });
          setTimeout(() => setMsg(null), 2500);
        }
        const nl = [...lines, newLine];
        setLines(nl); calcTotals(nl);
        setBarcodeInput(''); if(barcodeRef.current) barcodeRef.current.focus();
        return;
      }
    } catch(e) {
      // tek stok bulunamadı; renk/beden takibi açıksa seri barkod dene
      if (colorSizeEnabled) {
        try {
          const sr = await api.get('/stocks/series/by-barcode/' + raw);
          if (sr.data.success) {
            const series = sr.data.data;
            const nl = expandSeriesIntoLines(series.items || [], lines);
            setLines(nl); calcTotals(nl);
            setMsg({type:'success', text: `Seri açıldı: ${series.name} (${(series.items||[]).length} kalem)`});
            setTimeout(()=>setMsg(null), 3000);
            setBarcodeInput(''); if(barcodeRef.current) barcodeRef.current.focus();
            return;
          }
        } catch (e2) {}
      }
      setMsg({type:'error', text:'Barkod bulunamadı: '+barcodeInput});
    }
    setBarcodeInput(''); if(barcodeRef.current) barcodeRef.current.focus();
  };

  const handleNew = (retailMode = false) => {
    setSelected(null); setIsNew(true); setIsEditing(true); setMsg(null); setActiveTab(0);
    const defWarehouse = lookups.settings?.default_warehouse_id||'';
    setLines([]);
    setTotals({subtotal:0,discount_total:0,net_total:0,otv_total:0,vat_base:0,vat_total:0,withholding_total:0,grand_total:0,vat_breakdown:[]});
    const n=new Date(), lc=lookups.settings?.local_currency||'TRY';
    const defDays=parseInt(lookups.settings?.default_payment_days)||0;
    const dueDate=defDays>0?new Date(n.getTime()+defDays*86400000).toISOString().substring(0,10):'';
    setHeader(p=>({...p,
      invoice_type: retailMode ? retailType : wholesaleType,
      invoice_number:'', invoice_date:n.toISOString().substring(0,10),
      invoice_time:n.toTimeString().substring(0,5), due_date:dueDate, account_id:'',
      warehouse_id:defWarehouse,
      // Kullanıcı bir personele bağlıysa otomatik gelsin (kullanıcı değiştirebilir)
      personnel_id: currentUser?.personnelId || '',
      project_id:'',
      is_vat_included:lookups.settings?.default_vat_included==='true',
      currency_code:lc, exchange_rate:1, local_currency:lc,
      special_code1:'', special_code2:'', notes:'', internal_note:'',
      dispatch_number:'', status:'draft',
      e_doc_type:'', e_invoice_scenario:lookups.settings?.default_einvoice_scenario||'',
      e_invoice_type_code:'', tax_exemption_code:'', tax_exemption_reason:'',
      bill_to_mode:'same', bill_to_customer_id:'', bill_to_name:'', bill_to_tax_number:'',
      bill_to_address:'', bill_to_city:'', bill_to_district:'', bill_to_country:'Turkiye',
      bill_to_phone:'', bill_to_email:'',
      ship_to_mode:'none', ship_to_name:'', ship_to_address:'', ship_to_city:'',
      ship_to_district:'', ship_to_country:'Turkiye', ship_to_phone:'',
      carrier_id:'', tracking_number:'',
      ecommerce_platform_id:'', ecommerce_order_no:'', ecommerce_order_date:'',
      is_ecommerce_order:false,
      commission_rate:0, commission_amount:0,
      delivery_date: (isWaybillMode || isOrderMode) ? n.toISOString().substring(0,10) : '',
      dispatch_date: isWaybillMode ? n.toISOString().substring(0,10) : '',
      order_status_id: '',
      carrier_name:'', carrier_vkn:'', carrier_tracking_no:'',
      vehicle_plate:'', driver_name:'', driver_tckn:'',
      record_to_stock: true, record_to_account: true,
    }));
    setShowModal(true);
  };

  const handleSelect = async (inv) => {
    try {
      const res = await api.get(apiBase + '/' + inv.id);
      if (res.data.success) {
        const d = res.data.data;
        setSelected(d); setIsNew(false); setIsEditing(false); setMsg(null); setActiveTab(0);
        setHeader({
          invoice_type:d.invoice_type, invoice_number:d.invoice_number||'',
          invoice_date:d.invoice_date?.substring(0,10),invoice_time:d.invoice_time||'',
          // Potansiyel cari ile kaydedilmiş ise UI'da SearchableSelect değerini prospect_account_id ile dolduruyoruz
          due_date:d.due_date?.substring(0,10)||'',
          account_id: d.account_id || d.prospect_account_id || '',
          prospect_account_id: d.prospect_account_id || null,
          is_prospect_account: !!d.prospect_account_id || !!d.is_prospect_account,
          warehouse_id:d.warehouse_id||'', personnel_id:d.personnel_id||'',
          project_id:d.project_id||'', is_vat_included:d.is_vat_included,
          currency_code:d.currency_code, exchange_rate:d.exchange_rate,
          local_currency:d.local_currency||localCurrency,
          special_code1:d.special_code1||'', special_code2:d.special_code2||'',
          special_code3:d.special_code3||'', notes:d.notes||'',
          internal_note:d.internal_notes||'',
          dispatch_number:d.dispatch_number||'', order_number:d.order_number||'',
          status:d.status,
          e_doc_type:d.e_doc_type||'', e_invoice_scenario:d.e_invoice_scenario||'',
          e_invoice_type_code:d.e_invoice_type_code||'',
          tax_exemption_code:d.tax_exemption_code||'', tax_exemption_reason:d.tax_exemption_reason||'',
          bill_to_mode:d.bill_to_account_id?'customer':d.bill_to_name?'manual':'same',
          bill_to_customer_id:d.bill_to_account_id||'', bill_to_name:d.bill_to_name||'',
          bill_to_tax_number:d.bill_to_tax_number||'', bill_to_address:d.bill_to_address||'',
          bill_to_city:d.bill_to_city||'', bill_to_district:d.bill_to_district||'',
          bill_to_country:'Turkiye', bill_to_phone:'', bill_to_email:'',
          ship_to_mode:d.ship_to_address?'general':'none',
          ship_to_name:d.ship_to_name||'', ship_to_address:d.ship_to_address||'',
          ship_to_city:d.ship_to_city||'', ship_to_district:d.ship_to_district||'',
          ship_to_country:d.ship_to_country||'Turkiye', ship_to_phone:d.ship_to_phone||'',
          carrier_id:d.carrier_id||'', tracking_number:d.carrier_tracking_no||'',
          ecommerce_platform_id:d.ecommerce_platform||'',
          ecommerce_order_no:d.ecommerce_order_no||'',
          ecommerce_order_date:d.ecommerce_order_date?.substring(0,10)||'',
          is_ecommerce_order: !!d.is_ecommerce_order,
          commission_rate:d.commission_rate||0, commission_amount:d.commission_amount||0,
          delivery_date: d.delivery_date?.substring(0,10) || '',
          dispatch_date: d.dispatch_date?.substring(0,10) || '',
          carrier_name: d.carrier_name || '',
          carrier_vkn:  d.carrier_vkn  || '',
          carrier_tracking_no: d.carrier_tracking_no || '',
          vehicle_plate: d.vehicle_plate || '',
          driver_name:   d.driver_name  || '',
          driver_tckn:   d.driver_tckn  || '',
          // Stok/Cari hareket flag'ları (DB'den geri oku — checkbox state'i hatırlansın)
          record_to_stock:   !d.skip_stock_movements,
          record_to_account: !d.skip_account_transactions,
          // Teklif alanları — geri oku (form yeniden açılınca dolu gelsin)
          valid_until: d.valid_until?.substring(0,10) || '',
          quote_status_id: d.quote_status_id || '',
        });
        const ls = (d.lines||[]).map(l => ({
          ...emptyLine, ...l,
          unit_name:  l.unit_name  || '',
          unit2_name: l.unit2_name || '',
          unit3_name: l.unit3_name || '',
          units: getStockUnits(l.stock_id),
        }));
        setLines(ls.length ? ls : [{...emptyLine}]);
        // Pergola kaynaklı kalemler varsa maliyet bilgilerini batch olarak çek
        // Öncelik source_pergola_calc_id (UUID); olmayanlarda eski description regex'ten doc_no
        const pergolaIds = [...new Set(ls.map(l => l.source_pergola_calc_id).filter(Boolean))];
        const pergolaDocs = [...new Set(ls.filter(l => !l.source_pergola_calc_id).map(l => {
          const m = (l.description || '').match(/Pergola\s+([A-Za-z0-9_-]+)/i);
          return m ? m[1] : null;
        }).filter(Boolean))];
        const fetchPromises = [];
        if (pergolaIds.length > 0) {
          fetchPromises.push(api.get('/pergola/calculations/costs', { params: { ids: pergolaIds.join(',') } })
            .then(r => r.data?.success ? r.data.data || {} : {})
            .catch(() => ({})));
        }
        if (pergolaDocs.length > 0) {
          fetchPromises.push(api.get('/pergola/calculations/costs', { params: { doc_nos: pergolaDocs.join(',') } })
            .then(r => r.data?.success ? r.data.data || {} : {})
            .catch(() => ({})));
        }
        if (fetchPromises.length > 0) {
          Promise.all(fetchPromises).then(results => {
            const merged = {};
            results.forEach(o => { Object.entries(o).forEach(([k, v]) => { merged[k] = v; }); });
            setPergolaCosts(merged);
          });
        } else {
          setPergolaCosts({});
        }
        setTotals({
          subtotal:d.subtotal, discount_total:d.discount_total,
          net_total:d.net_total, otv_total:d.otv_total||0,
          vat_base:d.vat_base||0, vat_total:d.vat_total,
          withholding_total:d.withholding_total||0, grand_total:d.grand_total,
          vat_breakdown: (() => {
            const v = d.vat_breakdown;
            if (Array.isArray(v)) return v;
            if (typeof v === 'string' && v) {
              try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
            }
            return [];
          })()
        });
        setShowModal(true);
        // Potansiyel cari ile kaydedilmiş belge: prospect lookups.accounts'ta yoksa müşteri alanı boş görünür.
        // Detay yanıtındaki account_title/account_code (COALESCE ile prospect başlığı) ile sentetik kayıt enjekte et.
        if (d.prospect_account_id && !(lookups.accounts || []).find(a => String(a.id) === String(d.prospect_account_id))) {
          setLookups(prev => ({ ...prev, accounts: [...(prev.accounts || []), {
            id: d.prospect_account_id, code: d.account_code || '', title: d.account_title || '', is_prospect: true,
          }] }));
        }
        // Mevcut faturayı açtığımızda da seçili carinin sevk adreslerini yükle
        const aid = d.account_id || d.prospect_account_id;
        if (aid && !d.prospect_account_id) {
          try {
            const ar = await api.get('/accounts/' + aid + '/addresses');
            if (ar.data.success) setAccountAddresses(ar.data.data || []);
          } catch (_) { setAccountAddresses([]); }
        } else { setAccountAddresses([]); }
        // Onay hiyerarşisi kaydı var mı? — yoksa Düzenle/Sil normal çalışsın (Onayı Geri Al gizlensin)
        try {
          const sourceTable = apiBase.slice(1).replace(/-/g, '_');
          const ar = await api.get('/approvals/by-source/' + sourceTable + '/' + d.id);
          setHasApprovalRecord(!!(ar.data?.success && ar.data.data));
        } catch (_) { setHasApprovalRecord(false); }
      }
    } catch(e) { console.error(e); }
  };

  const handleModalClose = () => {
    setShowModal(false); setSelected(null); setIsNew(false); setIsEditing(false); setMsg(null);
    setSourceWaybillIds([]); setShowWaybillImport(false); setAvailableWaybills([]); setImportPickedIds([]);
    setHasApprovalRecord(false);
  };

  // Cari seçim — hem SearchableSelect hem de Cari Liste modal'ı bu fonksiyonu çağırır.
  // Potansiyel cari seçilirse cariye aktarmadan, prospect_account_id alanına yazılır.
  const selectAccount = async (id) => {
    const acc = lookups.accounts.find(a => String(a.id) === String(id));
    if (!acc) { h('account_id', id); return; }
    const cur = acc.currency_code || localCurrency;
    const er  = cur === localCurrency ? 1 : (parseFloat(((lookups.exchangeRates||{})[cur])?.selling_rate) || 1);
    const days = parseInt(acc.payment_term_days) || 0;
    const baseDate = new Date(header.invoice_date || new Date());
    baseDate.setDate(baseDate.getDate() + days);
    const dueDate = baseDate.toISOString().substring(0, 10);
    const nl = lines.map(l => calcLine(l, header.is_vat_included, er));
    setLines(nl); calcTotals(nl);
    // Potansiyel cari seçildiyse account_id boş, prospect_account_id'ye yazılır
    if (acc.is_prospect) {
      setHeader(prev => ({ ...prev, account_id: id, prospect_account_id: id, is_prospect_account: true,
                                    currency_code: cur, exchange_rate: er, due_date: dueDate }));
      setAccountAddresses([]); // potansiyellerde adres yok
    } else {
      setHeader(prev => ({ ...prev, account_id: id, prospect_account_id: null, is_prospect_account: false,
                                    currency_code: cur, exchange_rate: er, due_date: dueDate }));
      // Sevk adresleri kalem-bazlı seçim için yüklenir
      try {
        const r = await api.get('/accounts/' + id + '/addresses');
        if (r.data.success) setAccountAddresses(r.data.data || []);
      } catch (_) { setAccountAddresses([]); }
    }
  };

  const handleSave = async (asStatus, opts = {}) => {
    if (!header.account_id) { setMsg({type:'error',text:'Musteri secimi zorunludur'}); setActiveTab(0); return; }
    // Stok seçilmemişse manuel ürün adı yazılı olmalı (sipariş/teklif kalemlerinde stoksuz giriş)
    const validLines = lines.filter(l => (l.stock_id || (l.manual_stock_name && l.manual_stock_name.trim())) && parseFloat(l.quantity) > 0);
    if (!validLines.length) { setMsg({type:'error',text:'En az bir gecerli kalem ekleyin'}); setActiveTab(1); return; }
    setSaving(true); setMsg(null);
    try {
      // Paket (bundle) satırlarını bileşenlerine patlat — paket SKU'sunun kendi stoğu yoktur,
      // stok bileşenlerden düşer. Patlatma sonrası toplamlar yeniden hesaplanır.
      let finalLines = validLines, finalTotals = totals;
      const exploded = await explodeBundlesForSave(validLines);
      if (exploded) { finalLines = exploded; finalTotals = computeTotals(exploded); }

      const commAmt = +((header.commission_rate||0)/100*(finalTotals.grand_total||0)).toFixed(2);
      // Potansiyel cari seçildiyse account_id null, prospect_account_id dolu gönderilir
      const isProspect = !!header.is_prospect_account;
      const payload = { ...header, ...finalTotals, status:asStatus||'draft',
        account_id: isProspect ? null : header.account_id,
        prospect_account_id: isProspect ? header.account_id : null,
        lines:finalLines, local_currency:localCurrency, commission_amount:commAmt,
        confirm_credit_exceed: !!opts.confirmCreditExceed,
        // Stok/cari hareket atlama (e-Belge'ye sadece kayıt amaçlı durum)
        skip_stock_movements: header.record_to_stock === false,
        skip_account_transactions: header.record_to_account === false,
        // Manuel aktarımdan gelen irsaliyeler — backend irsaliye stok hareketini silip yenisini yazar, irsaliyeleri faturalı işaretler
        ...(sourceWaybillIds.length > 0 ? { source_waybill_ids: sourceWaybillIds } : {}),
      };
      let res;
      if (isNew) res = await api.post(apiBase, payload);
      else       res = await api.put(apiBase + '/' + selected.id, payload);
      // Quote modunda valid_until + quote_status_id alanları base save'e dahil değil → PATCH
      if (res.data.success && isQuoteMode) {
        try {
          const newId = res.data.data?.id || selected?.id;
          if (newId && (header.valid_until !== undefined || header.quote_status_id !== undefined)) {
            await api.patch(apiBase + '/' + newId + '/quote-meta', {
              valid_until: header.valid_until || null,
              quote_status_id: header.quote_status_id || null,
            });
          }
        } catch (e) { console.warn('quote-meta PATCH başarısız:', e); }
      }
      if (res.data.success) {
        const docMsg = res.data.data?.document_number?' - '+res.data.data.document_number:'';
        if (res.data.warning) {
          setMsg({type:'warning',text:'Fatura kaydedildi'+docMsg+' — UYARI: '+res.data.warning});
        } else {
          setMsg({type:'success',text:'Fatura kaydedildi'+docMsg});
        }
        setIsNew(false); setIsEditing(false); fetchInvoices();
        if (res.data.data?.id) handleSelect({id:res.data.data.id});
        else if (selected) handleSelect(selected);
      }
    } catch(e) {
      console.error('Fatura kayıt hatası:', e.response?.status, e.response?.data, e);
      const data = e.response?.data;
      if (data?.code === 'credit_limit_warn') {
        if (window.confirm(data.message + '\n\nDevam edilsin mi?')) {
          setSaving(false);
          return handleSave(asStatus, { confirmCreditExceed: true });
        }
        setMsg({type:'error',text:'Kredi limit asimi onaylanmadi, fatura kaydedilmedi.'});
      } else if (data?.code === 'credit_limit_block') {
        setMsg({type:'error',text:data.message});
      } else {
        const detail = data?.message || data?.error || (typeof data === 'string' ? data : '') || e.message || 'Bilinmeyen hata';
        setMsg({type:'error',text:'Hata: ' + detail});
      }
    }
    finally { setSaving(false); }
  };

  // Onayı geri al — onaylanmış belge düzenlemek için bu yapılmalı.
  // Hareketler reverse edilir, status='draft' olur, document_approvals kaydı silinir.
  const handleRevokeApproval = async () => {
    if (!selected) return;
    const reason = window.prompt('Onayı geri alma sebebi (opsiyonel):', '');
    if (reason === null) return;  // user cancelled
    if (!window.confirm('Onay geri alınacak: stok/cari/muhasebe hareketleri reverse edilecek ve belge taslağa dönecek. Devam edilsin mi?')) return;
    try {
      const r = await api.post(apiBase + '/' + selected.id + '/revoke-approval', { reason: reason || null });
      if (r.data.success) {
        setMsg({ type: 'success', text: r.data.message || 'Onay geri alındı' });
        fetchInvoices();
        handleSelect(selected);  // formu yeniden yükle (status='draft' görünür)
      }
    } catch (e) {
      const data = e.response?.data;
      if (data?.code === 'edoc_sent') alert(data.message);
      else setMsg({ type: 'error', text: data?.message || 'Hata' });
    }
  };

  // Onay geçmişi modalını aç — apiBase'den source_table türetilir
  const openApprovalHistory = async (sourceId) => {
    if (!sourceId) return;
    const sourceTable = apiBase.slice(1).replace(/-/g, '_');  // /sales-invoices → sales_invoices
    setApprovalHistory({ source_table: sourceTable, source_id: sourceId, loading: true, data: null });
    try {
      const r = await api.get('/approvals/by-source/' + sourceTable + '/' + sourceId);
      setApprovalHistory({ source_table: sourceTable, source_id: sourceId, loading: false, data: r.data?.success ? r.data.data : null });
    } catch (e) {
      setApprovalHistory({ source_table: sourceTable, source_id: sourceId, loading: false, data: null, error: e.response?.data?.message || e.message });
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    // Bağlı tahsilat/tediye makbuzları kontrol et — VARSA SİLMEYİ ENGELLE
    try {
      const invType = isPurchaseMode ? 'purchase_invoice' : 'sales_invoice';
      const linksRes = await api.get('/invoice-links/' + invType + '/' + selected.id);
      if (linksRes.data?.success && linksRes.data.data.has_any) {
        const rs = linksRes.data.data.receipts || [];
        const ps = linksRes.data.data.payments || [];
        const parts = [];
        if (rs.length) parts.push(`${rs.length} tahsilat makbuzu (${rs.map(r => r.receipt_no).join(', ')})`);
        if (ps.length) parts.push(`${ps.length} tediye makbuzu (${ps.map(p => p.payment_no).join(', ')})`);
        const firstRid = rs[0]?.id;
        const firstPid = ps[0]?.id;
        const firstNo = rs[0]?.receipt_no || ps[0]?.payment_no;
        const goOpen = window.confirm(
          `⚠ Bu fatura silinemez!\n\n` +
          `Bu faturaya bağlı:\n  • ${parts.join('\n  • ')}\n\n` +
          `Önce bu makbuzu silmeniz gerekir. Sonra faturayı silebilirsiniz.\n\n` +
          `${firstNo} no'lu makbuzu şimdi açmak ister misiniz?`
        );
        if (goOpen && onNavigate) {
          if (firstRid) {
            // SalesInvoicePage modal'ı kapat, tahsilat makbuzu sayfasında o makbuzu aç
            handleModalClose();
            onNavigate('cari-tahsilat', firstRid);
          } else if (firstPid) {
            handleModalClose();
            onNavigate('cari-tediye', firstPid);
          }
        }
        return; // Silme akışı durdu
      }
    } catch (e) { console.warn('invoice-links check:', e.message); }

    // Bağlı makbuz yoksa normal silme akışı
    const isApproved = header.status === 'approved';
    const msg = (isApproved
      ? `Bu fatura ONAYLI. Silindiğinde stok hareketleri, cari hareketleri ve muhasebe yevmiyesi geri alınacak.\n\nDevam edilsin mi?`
      : `Fatura silinsin mi?`);
    if (!window.confirm(msg)) return;

    try {
      const res = await api.delete(apiBase + '/' + selected.id);
      if (res.data.success) { handleModalClose(); fetchInvoices(); }
    } catch(e) {
      const data = e.response?.data;
      if (data?.code === 'edoc_sent') {
        alert(data.message);
      } else {
        setMsg({type:'error',text:data?.message||'Hata'});
      }
    }
  };

  const handleCancel = async () => {
    if (!selected||!window.confirm('İptal edilsin mi?')) return;
    try {
      const res = await api.post(apiBase + '/' + selected.id + '/cancel', { reason: 'Kullanıcı iptali' });
      if (res.data.success) { fetchInvoices(); handleSelect(selected); setMsg({type:'success',text:res.data.message}); }
    } catch(e) { setMsg({type:'error',text:e.response?.data?.message||'Hata'}); }
  };

  const qlmConfig = (() => {
    if (showQuick === 'project')  return { title:'Proje',     items:lookups.projects||[],         apiBase:'/invoices/projects',      extraBody:{} };
    if (showQuick === 'special1') return { title:'Özel Kod 1', items:lookups.specialCodes1||[],   apiBase:'/invoices/special-codes', extraBody:{ code_type:'special1' } };
    if (showQuick === 'special2') return { title:'Özel Kod 2', items:lookups.specialCodes2||[],   apiBase:'/invoices/special-codes', extraBody:{ code_type:'special2' } };
    return null;
  })();

  const h = (key,val) => {
    setHeader(prev => {
      const nh = {...prev, [key]:val};
      if (key==='is_vat_included'||key==='exchange_rate') {
        const er = key==='exchange_rate' ? parseFloat(val)||1 : parseFloat(prev.exchange_rate)||1;
        const vat = key==='is_vat_included' ? val : prev.is_vat_included;
        const nl  = lines.map(l => calcLine({...l, is_vat_included: vat}, vat, er));
        setLines(nl); calcTotals(nl);
      } else if (key==='currency_code') {
        const er    = val === localCurrency ? 1 : (parseFloat((lookups.exchangeRates||{})[val]?.selling_rate) || 1);
        const oldEr = parseFloat(prev.exchange_rate) || 1;
        nh.exchange_rate = er;
        const nl = lines.map(l => {
          const localPrice   = +(parseFloat(l.unit_price) * oldEr).toFixed(4);
          const newUnitPrice = er > 0 ? +(localPrice / er).toFixed(4) : localPrice;
          return calcLine({...l, unit_price: newUnitPrice}, prev.is_vat_included, er);
        });
        setLines(nl); calcTotals(nl);
      }
      return nh;
    });
  };

  // Faturalanmış irsaliye / aktarım yapılmış sipariş kilidi — düzenleme/silme yapılamaz
  const orderHasTransfers = isOrderMode && (selected?.fulfillment_status === 'partial' || selected?.fulfillment_status === 'closed' || transfers.length > 0);
  const isLockedInvoiced = (isWaybillMode && !!(selected?.invoice_id || selected?.invoice_document_number))
                         || orderHasTransfers;
  // Soft-lock: aynı kayıt üzerinde başka kullanıcı düzenleme yapıyorsa read-only
  const lockTableName = apiBase ? apiBase.replace(/^\//, '').replace(/-/g, '_') : null;
  const docLock = useDocumentLock(lockTableName, selected?.id || null, {
    enabled: showModal && !isNew && !!selected?.id && isEditing,
  });
  const isLockedByOther = docLock.state === 'locked';
  const canEdit   = (isNew || isEditing) && !isLockedInvoiced && !isApprovalReadonly && !isLockedByOther;
  // Renk/Beden takibi — string 'true' / boolean true / 1 hepsini kabul et (defensive)
  const colorSizeEnabled = (() => {
    const v = lookups.settings?.color_size_tracking;
    if (v === true || v === 1) return true;
    return String(v || '').toLowerCase() === 'true';
  })();
  const fmt       = (n) => fmtAmount(n);
  const fmtQ      = (n) => fmtQty(n);
  const isDiffCur = header.currency_code !== localCurrency;
  const statusLabel = (s) => s==='draft'?'Taslak':s==='approved'?'Onayli':s==='cancelled'?'İptal':s==='invoiced'?'Faturalandı':s==='pending_approval'?'Onay Bekliyor':'?';
  const statusColor = (s) => s==='draft'?'var(--warning)':s==='approved'?'var(--success)':s==='invoiced'?'#7c3aed':s==='pending_approval'?'#b45309':'var(--danger)';

  const iStyle = {width:'100%',padding:'7px 12px',background:'var(--bg-input)',border:'1px solid var(--border-input)',borderRadius:8,color:'var(--text-primary)',fontSize:'0.82rem',boxSizing:'border-box'};
  const tabStyle = (i) => ({padding:'8px 16px',border:'none',background:'transparent',cursor:'pointer',color:activeTab===i?'var(--accent-primary)':'var(--text-secondary)',borderBottom:activeTab===i?'2px solid var(--accent-primary)':'2px solid transparent',fontWeight:activeTab===i?600:400,fontSize:'0.85rem',whiteSpace:'nowrap',fontFamily:'Outfit'});

  return (
    <RenderBoundary>
    <>
    {/* PriceHistoryModal — alış modunda kalem üstünde fiyat geçmişi */}
    {priceHistoryFor && (
      <PriceHistoryModal
        open={!!priceHistoryFor}
        stockId={priceHistoryFor.stock_id}
        stockName={priceHistoryFor.stock_name}
        accountId={header?.account_id}
        onClose={() => setPriceHistoryFor(null)}
      />
    )}
    <div className="stocks-page">
      <ListPageHeader
        title={pageTitle}
        count={total}
        unit={isQuoteMode ? 'teklif' : isOrderMode ? 'sipariş' : isWaybillMode ? 'irsaliye' : 'fatura'}
        search={search}
        onSearch={setSearch}
        searchPlaceholder={isQuoteMode ? 'Teklif/belge no, cari ara...' : isOrderMode ? 'Sipariş/belge no, cari ara...' : isWaybillMode ? 'İrsaliye/belge no, cari ara...' : 'Fatura/belge no, cari ara...'}
        onRefresh={fetchInvoices}
        refreshing={loading}
        actions={<>
          {isWaybillMode && selectedWaybillIds.length > 0 && (
            <Button size="sm" onClick={handleConvertToInvoice} loading={convertingInvoice}
              style={{ background: 'rgba(124,58,237,0.15)', color: '#7c3aed', borderColor: 'rgba(124,58,237,0.4)' }}>
              📄 Faturaya Aktar ({selectedWaybillIds.length})
            </Button>
          )}
          <Button size="sm" onClick={() => handleNew(false)}>
            + Yeni {isQuoteMode ? 'Teklif' : isOrderMode ? 'Sipariş' : isWaybillMode ? (isReturnMode ? 'İade İrsaliyesi' : 'İrsaliye') : (isReturnMode ? 'İade' : 'Fatura')}
          </Button>
          {supportsRetail && (
            <Button size="sm" onClick={() => handleNew(true)} style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', borderColor: 'rgba(245,158,11,0.3)' }}>+ Yeni Perakende {isReturnMode ? 'İade' : 'Fatura'}</Button>
          )}
        </>}
      />

      {isInvoiceMode && (
        <ListPageStats
          loading={stats == null}
          stats={[
            {
              key: 'month_total',
              label: 'Bu Ay Toplam',
              value: stats?.month_total,
              sublabel: `${stats?.month_count || 0} fatura`,
              color: 'primary',
              icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
            },
            {
              key: 'month_paid',
              label: 'Bu Ay Tahsil/Ödenen',
              value: stats?.month_paid,
              color: 'success',
              sublabel: stats?.month_total > 0 ? `%${((stats.month_paid / stats.month_total) * 100).toFixed(0)} oran` : null,
              icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
            },
            {
              key: 'open',
              label: 'Açık Bakiye',
              value: stats?.open_balance,
              color: 'warning',
              sublabel: `${stats?.open_count || 0} fatura bekliyor`,
              icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
            },
            {
              key: 'cancelled',
              label: 'İptal Edilen',
              value: stats?.cancelled_count,
              color: 'danger',
              sublabel: 'tüm dönemler',
              icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>,
            },
          ]}
        />
      )}

      {/* Açılır filtre çubuğu — fatura ve irsaliye modunda */}
      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'date_from', type: 'date', label: 'İlk Tarih' },
          { key: 'date_to',   type: 'date', label: 'Son Tarih' },
          { key: 'account_id', type: 'select', label: 'Cari', flex: '1 1 240px', minWidth: 200,
            options: [{ value: '', label: 'Tüm Cariler' }, ...(lookups.accounts || []).map(a => ({ value: a.id, label: (a.code ? a.code + ' - ' : '') + a.title }))] },
          { key: 'personnel_id', type: 'select', label: 'Personel', flex: '1 1 200px', minWidth: 180,
            options: [{ value: '', label: 'Tüm Personel' }, ...(lookups.personnel || []).map(p => ({ value: p.id, label: (p.code ? p.code + ' - ' : '') + (p.full_name || p.name || '') }))] },
          { key: 'branch_id', type: 'select', label: 'Şube', flex: '1 1 200px', minWidth: 180,
            options: [{ value: '', label: 'Tüm Şubeler' }, ...(lookups.branches || []).map(b => ({ value: b.id, label: (b.code ? b.code + ' - ' : '') + b.name }))] },
          ...(isQuoteMode ? [
            { key: 'valid_from', type: 'date', label: 'Geçerlilik Başlangıç' },
            { key: 'valid_to',   type: 'date', label: 'Geçerlilik Bitiş' },
            { key: 'quote_status', type: 'select', label: 'Teklif Durumu', flex: '1 1 180px', minWidth: 160,
              options: [
                { value: '', label: 'Tüm Durumlar' },
                { value: 'draft', label: 'Taslak' },
                { value: 'sent', label: 'Gönderildi' },
                { value: 'seen', label: 'Görüldü' },
                { value: 'negotiation', label: 'Müzakere' },
                { value: 'accepted', label: 'Kabul Edildi' },
                { value: 'rejected', label: 'Reddedildi' },
                { value: 'expired', label: 'Süresi Doldu' },
              ] },
          ] : []),
          ...(isOrderMode
            ? [
                { key: 'fulfillment_status', type: 'select', label: 'Aktarım Durumu',
                  options: [{ value: '', label: 'Tümü' }, { value: 'open', label: 'Aktarılmadı' }, { value: 'partial', label: 'Kısmi aktarıldı' }, { value: 'closed', label: 'Aktarıldı' }] },
              ]
            : isWaybillMode
            ? [{ key: 'invoiced', type: 'select', label: 'Aktarım Durumu',
                options: [{ value: 'all', label: 'Tümü' }, { value: 'no', label: 'Faturaya Aktarılmayanlar' }, { value: 'yes', label: 'Faturaya Aktarılanlar' }] }]
            : [{ key: 'status', type: 'select', label: 'Durum',
                options: [{ value: '', label: 'Tümü' }, { value: 'draft', label: 'Taslak' }, { value: 'approved', label: 'Onaylı' }, { value: 'cancelled', label: 'İptal' }] }]
          ),
        ]}
      />

      {/* TABLO */}
      <div className="stocks-table-wrap">
        <DataTable
          pageKey="sales-invoices-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText={isQuoteMode ? 'Teklif bulunamadı' : isOrderMode ? 'Sipariş bulunamadı' : isWaybillMode ? 'İrsaliye bulunamadı' : 'Fatura bulunamadı'}
          data={invoices}
          rowKey="id"
          onRowClick={inv => handleSelect(inv)}
          columns={[
            ...(isWaybillMode ? [{
              id: 'select', header: '', sortable: false, hideable: false, draggable: false, filterable: false, width: 36,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: inv => (
                <input type="checkbox"
                  checked={selectedWaybillIds.includes(inv.id)}
                  disabled={inv.status === 'invoiced' || inv.status === 'cancelled'}
                  onClick={e => e.stopPropagation()}
                  onChange={e => {
                    setSelectedWaybillIds(prev => e.target.checked
                      ? [...prev, inv.id]
                      : prev.filter(id => id !== inv.id));
                  }}
                />
              ),
            }] : []),
            { id: 'doc_no', header: 'Belge No', accessor: inv => inv.document_number || inv.invoice_number || '', sortable: true, width: 150,
              cell: inv => (
                <span>
                  <span className="stock-code">{inv.document_number || inv.invoice_number || '-'}</span>
                  {isWaybillMode && inv.invoice_document_number && (
                    <div style={{ fontSize: '0.68rem', color: '#7c3aed', marginTop: 2 }} title="Aktarıldığı fatura">→ {inv.invoice_document_number}</div>
                  )}
                  {isOrderMode && (inv.fulfillment_status === 'partial' || inv.fulfillment_status === 'closed') && (
                    <div style={{ fontSize: '0.68rem', color: inv.fulfillment_status === 'closed' ? '#7c3aed' : '#b45309', marginTop: 2, fontWeight: 600 }}
                         title={inv.fulfillment_status === 'closed' ? 'Tamamen aktarıldı' : 'Kısmen aktarıldı'}>
                      → {inv.fulfillment_status === 'closed' ? 'Aktarıldı' : 'Kısmi aktarım'}
                    </div>
                  )}
                </span>
              ) },
            ...(supportsRetail ? [{ id: 'type', header: 'Tip', accessor: inv => (inv.invoice_type === 'retail_sale' || inv.invoice_type === 'retail_sale_return') ? 'Perakende' : 'Toptan', sortable: true, width: 110,
              cell: inv => { const isRetail = inv.invoice_type === 'retail_sale' || inv.invoice_type === 'retail_sale_return'; return <span style={{fontSize:'0.7rem', padding:'2px 8px', borderRadius:'var(--r-full)', fontWeight:600, background: isRetail ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.1)', color: isRetail ? '#f59e0b' : 'var(--success)'}}>{isRetail ? 'Perakende' : 'Toptan'}</span>; } }] : []),
            { id: 'account', header: 'Cari', accessor: 'account_title', sortable: true,
              cell: inv => <span className="stock-name-cell">{inv.account_title}</span> },
            { id: 'phone', header: 'Telefon', accessor: inv => inv.account_phone || inv.account_mobile || '', sortable: true, width: 130,
              cell: inv => <span className="stock-secondary">{inv.account_phone || inv.account_mobile || '-'}</span> },
            { id: 'tax', header: 'VD / TC No', accessor: inv => inv.account_identity_type === 'individual' ? (inv.account_id_number || '') : `${inv.account_tax_office||''} ${inv.account_tax_number||''}`, sortable: true, width: 160,
              cell: inv => <span className="stock-secondary" style={{fontSize:'0.78rem'}}>{inv.account_identity_type === 'individual' ? (inv.account_id_number || '-') : (inv.account_tax_office || inv.account_tax_number ? `${inv.account_tax_office || ''}${inv.account_tax_office && inv.account_tax_number ? ' / ' : ''}${inv.account_tax_number || ''}` : '-')}</span> },
            { id: 'date', header: 'Tarih', accessor: 'invoice_date', sortable: true, width: 110,
              sortFn: (a,b) => new Date(a.invoice_date||0)-new Date(b.invoice_date||0),
              cell: inv => <span className="stock-secondary">{inv.invoice_date?.substring(0,10)}</span> },
            ...(!isWaybillMode ? [{
              id: 'due_date',
              header: isQuoteMode ? 'Geçerlilik Tarihi' : (isOrderMode ? 'Teslim Tarihi' : 'Vade'),
              accessor: inv => isQuoteMode ? inv.valid_until : (isOrderMode ? inv.delivery_date : inv.due_date),
              sortable: true, width: 140,
              sortFn: (a,b) => {
                const av = isQuoteMode ? a.valid_until : (isOrderMode ? a.delivery_date : a.due_date);
                const bv = isQuoteMode ? b.valid_until : (isOrderMode ? b.delivery_date : b.due_date);
                return new Date(av||0) - new Date(bv||0);
              },
              cell: inv => {
                const v = isQuoteMode ? inv.valid_until : (isOrderMode ? inv.delivery_date : inv.due_date);
                if (!v) return <span className="stock-secondary">-</span>;
                if (isQuoteMode) {
                  const days = Math.floor((new Date(v) - new Date()) / 86400000);
                  const expired = days < 0;
                  const soon = days >= 0 && days <= 3;
                  return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span className="stock-secondary">{v.substring(0,10)}</span>
                      {expired && <span style={{ fontSize: '0.65rem', padding: '1px 6px', borderRadius: 4, background: 'rgba(239,68,68,0.12)', color: '#dc2626', fontWeight: 700 }}>Süresi geçti</span>}
                      {soon && <span style={{ fontSize: '0.65rem', padding: '1px 6px', borderRadius: 4, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: 700 }}>{days === 0 ? 'Bugün' : days + ' gün'}</span>}
                    </span>
                  );
                }
                return <span className="stock-secondary">{v.substring(0,10)}</span>;
              },
            }] : []),
            { id: 'warehouse', header: 'Depo', accessor: 'warehouse_name', sortable: true, width: 140,
              cell: inv => <span className="stock-secondary">{inv.warehouse_name || '-'}</span> },
            { id: 'personnel', header: 'Personel', accessor: 'personnel_name', sortable: true, width: 150,
              cell: inv => <span className="stock-secondary">{inv.personnel_name || '-'}</span> },
            { id: 'grand_total', header: 'Genel Toplam', accessor: inv => parseFloat(inv.grand_total)||0, sortable: true, width: 150,
              headerStyle: {textAlign:'right'}, cellStyle: {textAlign:'right'},
              cell: inv => <span className="stock-price-cell">{fmt(inv.grand_total)} {inv.currency_code}</span> },
            // Persan kaynaklı tekliflerde ortalama karlılık — sadece quote-outgoing modunda göster
            ...(isQuoteOutgoingMode ? [{
              id: 'avg_profit_pct', header: 'Ort. Kâr %',
              accessor: inv => inv.avg_profit_pct != null ? parseFloat(inv.avg_profit_pct) : null,
              sortable: true, width: 110,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: inv => {
                if (inv.avg_profit_pct == null) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
                const v = parseFloat(inv.avg_profit_pct);
                const color = v < 0 ? 'var(--danger)' : v < 10 ? 'var(--warning)' : 'var(--success)';
                return <span style={{ fontWeight: 700, color, fontFamily: 'monospace' }}>%{new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)}</span>;
              },
            }] : []),
            // Teklif Durumu — sadece quote modunda göster
            ...(isQuoteMode ? [{
              id: 'quote_status', header: 'Teklif Durumu',
              accessor: inv => inv.quote_status || 'draft',
              sortable: true, width: 130,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: inv => {
                // Öncelik: özel teklif durumu (quote_status_id → quoteStatuses). Yoksa enum quote_status.
                const custom = inv.quote_status_id ? quoteStatuses.find(s => String(s.id) === String(inv.quote_status_id)) : null;
                if (custom) {
                  const col = custom.color || '#64748b';
                  return <span style={{ fontSize: '0.7rem', padding: '2px 9px', borderRadius: 'var(--r-full)', fontWeight: 700, background: col + '26', color: col }}>{custom.name}</span>;
                }
                const qs = inv.quote_status || 'draft';
                const labels = { draft: 'Taslak', sent: 'Gönderildi', seen: 'Görüldü', negotiation: 'Müzakere', accepted: 'Kabul', rejected: 'Red', expired: 'Süresi Doldu' };
                const colors = {
                  draft:       { bg: 'rgba(148,163,184,0.15)', fg: '#64748b' },
                  sent:        { bg: 'rgba(59,130,246,0.15)',  fg: '#3b82f6' },
                  seen:        { bg: 'rgba(139,92,246,0.15)',  fg: '#8b5cf6' },
                  negotiation: { bg: 'rgba(245,158,11,0.15)',  fg: '#f59e0b' },
                  accepted:    { bg: 'rgba(16,185,129,0.15)',  fg: '#10b981' },
                  rejected:    { bg: 'rgba(239,68,68,0.15)',   fg: '#ef4444' },
                  expired:     { bg: 'rgba(115,115,115,0.15)', fg: '#737373' },
                };
                const c = colors[qs] || colors.draft;
                return <span style={{ fontSize: '0.7rem', padding: '2px 9px', borderRadius: 'var(--r-full)', fontWeight: 700, background: c.bg, color: c.fg }}>{labels[qs] || qs}</span>;
              },
            }] : []),
            { id: 'status', header: 'Durum', accessor: inv => statusLabel(inv.status), sortable: true, width: 110,
              headerStyle: {textAlign:'center'}, cellStyle: {textAlign:'center'},
              cell: inv => (
                // Tüm durumlar tıklanabilir — onay geçmişi yoksa modal "kayıt yok" gösterir
                <span
                  onClick={(e) => { e.stopPropagation(); openApprovalHistory(inv.id); }}
                  title="Onay geçmişini gör"
                  style={{
                    fontSize:'0.67rem',padding:'3px 9px',borderRadius:'var(--r-full)',fontWeight:700,display:'inline-block',
                    background: inv.status==='approved'?'rgba(16,185,129,0.1)':inv.status==='cancelled'?'rgba(239,68,68,0.1)':inv.status==='pending_approval'?'rgba(245,158,11,0.12)':'rgba(245,158,11,0.1)',
                    color: statusColor(inv.status),
                    cursor: 'pointer',
                    textDecoration: 'underline dotted',
                  }}>{statusLabel(inv.status)}</span>
              ) },
            { id: 'audit', header: '', sortable: false, hideable: false, draggable: false, filterable: false, width: 32,
              cellStyle: {textAlign:'center'},
              cell: inv => <span onClick={e => e.stopPropagation()}><AuditPopover endpoint={`${apiBase}/${inv.id}/audit`}/></span> },
            // Görüşme ekle — sadece quote modunda, onay durumundan bağımsız
            ...(isQuoteMode ? [{
              id: 'activity', header: '', sortable: false, hideable: false, draggable: false, filterable: false, width: 44,
              cellStyle: { textAlign: 'center' },
              cell: inv => (
                <button
                  onClick={(e) => { e.stopPropagation(); setActivityForQuote({ id: inv.id, doc_no: inv.document_number, account_title: inv.account_title }); }}
                  title="Görüşme / takip ekle"
                  style={{
                    background: 'rgba(124,58,237,0.12)', color: '#7c3aed',
                    border: '1px solid rgba(124,58,237,0.3)', borderRadius: 4,
                    padding: '3px 8px', cursor: 'pointer', fontSize: '13px',
                  }}>💬</button>
              ),
            }] : []),
          ]}
        />
      </div>

      {/* FATURA MODAL (Geniş) */}
      {showModal && (() => {
        // Belge tipi etiketi ve ikon
        // Mod öncelik: quote/order modu invoice_type'tan önce gelir (backend invoice_type'ı bazı durumlarda
        // 'sales_waybill' olarak döndürdüğü için aşağıdaki invoice_type switch'i yanıltıcı olabilir).
        const docLabel = (() => {
          if (isQuoteOutgoingMode) return 'Verilen Teklif';
          if (isQuoteIncomingMode) return 'Alınan Teklif';
          if (isOrderOutgoingMode) return 'Verilen Sipariş';
          if (isOrderIncomingMode) return 'Alınan Sipariş';
          const t = header.invoice_type;
          if (t === 'sales_waybill')           return 'Satış İrsaliyesi';
          if (t === 'sales_waybill_return')    return 'Satış İade İrsaliyesi';
          if (t === 'purchase_waybill')        return 'Alış İrsaliyesi';
          if (t === 'purchase_waybill_return') return 'Alış İade İrsaliyesi';
          if (t === 'purchase')        return 'Alış Faturası';
          if (t === 'purchase_return') return 'Alış İade Faturası';
          const isRetail = t === 'retail_sale' || t === 'retail_sale_return';
          const isRet    = t === 'sales_return' || t === 'retail_sale_return';
          return `${isRetail ? 'Perakende ' : ''}Satış ${isRet ? 'İade ' : ''}Faturası`;
        })();
        const docIcon = isWaybillMode ? '🚚' : isOrderMode ? '🛒' : isQuoteMode ? '📝' : '🧾';
        const modalTitle = isNew ? `Yeni ${docLabel}` : docLabel;
        return (
        <>
        <Modal
          title={modalTitle}
          size="full"
          onClose={handleModalClose}
          closeOnOverlay={false}
          bodyStyle={{padding:0, display:'flex', flexDirection:'column'}}
          headerActions={
            <span style={{display:'inline-flex', gap:6, alignItems:'center'}}>
              {!isWaybillMode && !isOrderMode && !isQuoteMode && (
                <Button size="sm" variant="ghost" onClick={() => setShowDocSettings(true)}
                  title="Stok/cari hareket yazımı ayarları"
                  style={{
                    color: (header.record_to_stock === false || header.record_to_account === false) ? '#f59e0b' : undefined,
                    borderColor: (header.record_to_stock === false || header.record_to_account === false) ? 'rgba(245,158,11,0.4)' : undefined,
                  }}>
                  ⚙ Belge Ayarları
                  {(header.record_to_stock === false || header.record_to_account === false) && (
                    <span style={{marginLeft:6, fontSize:'0.7rem', padding:'1px 6px', background:'rgba(245,158,11,0.2)', borderRadius:4, fontWeight:700}}>!</span>
                  )}
                </Button>
              )}
              {!isNew && selected && !isWaybillMode && !isOrderMode && !isQuoteMode && (
                <EDocumentBadge sourceTable={isPurchaseMode ? 'purchase_invoices' : 'sales_invoices'} sourceId={selected.id}
                  canSend={!isLockedInvoiced && header.status === 'approved'}
                  blockReason={header.status !== 'approved' ? 'Belge önce onaylanmalı (durum: ' + (statusLabel(header.status) || header.status) + ')' : null} />
              )}
              {/* Yazdır butonu — quote ve order modlarında */}
              {!isNew && selected && (isQuoteMode || isOrderMode) && (
                <Button size="sm" variant="ghost"
                  title="Şablon seç ve yazdır"
                  onClick={() => {
                    const account = (lookups.accounts || []).find(a => String(a.id) === String(header.account_id)) || null;
                    const personnel = (lookups.personnel || []).find(p => String(p.id) === String(header.personnel_id)) || null;
                    // doc_type belirle (sales_quote, purchase_quote, sales_order, purchase_order)
                    const docType = isQuoteOutgoingMode ? 'sales_quote'
                      : isQuoteIncomingMode ? 'purchase_quote'
                      : isOrderOutgoingMode ? 'purchase_order'
                      : isOrderIncomingMode ? 'sales_order'
                      : '';
                    setShowPrintPicker({ docLabel, lines, header, totals, account, personnel, selected, docType });
                  }}>
                  🖨 Yazdır
                </Button>
              )}
              {/* Verilen Teklif Onaylı ise: Siparişe Aktar butonu */}
              {!isNew && selected && isQuoteOutgoingMode && header.status === 'approved' && (
                <Button size="sm" variant="ghost"
                  title="Bu teklifi Alınan Sipariş'e aktar"
                  onClick={() => setShowConvertToOrder(true)}>
                  🛒 Siparişe Aktar
                </Button>
              )}
            </span>
          }
          footer={
            <>
              <div className="ui-modal-footer-left">
                {!isLockedInvoiced && !isEditing && selected && !isNew && (
                  header.status === 'approved' && hasApprovalRecord
                    ? <Button variant="danger" onClick={handleRevokeApproval}>↩ Onayı Geri Al</Button>
                    : <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>
                )}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {(isEditing && !isNew) && (
                  <Button variant="ghost" onClick={() => handleSelect(selected)} disabled={saving}>İptal</Button>
                )}
                {isNew && (
                  <Button variant="ghost" onClick={handleModalClose} disabled={saving}>Vazgeç</Button>
                )}
                {!isLockedInvoiced && canEdit && (isEditing || isNew) && (
                  <CardSplitButton
                    primaryLabel={isOrderMode ? '💾 Onayla & Kaydet' : '✓ Onayla & Kaydet'}
                    primaryAction={() => handleSave('approved')}
                    loading={saving}
                    options={[
                      ...(!isOrderMode ? [{ label: 'Taslak Kaydet', icon: '📝', onClick: () => handleSave('draft') }] : []),
                      { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: async () => { await handleSave('approved'); setTimeout(() => handleNew?.(), 400); } },
                      { label: 'Kaydet ve Kapat',  icon: '✓', onClick: () => handleSave('approved') },
                    ]}
                  />
                )}
                {!isLockedInvoiced && !isEditing && !isNew && selected && !(header.status === 'approved' && hasApprovalRecord) && (
                  <Button onClick={() => setIsEditing(true)}>✎ Düzenle</Button>
                )}
              </div>
            </>
          }
        >
            {/* Mesaj barı — sadece mesaj varsa */}
            {msg && (
              <div style={{padding:'8px 16px',borderBottom:'1px solid var(--border-secondary)',flexShrink:0}}>
                <span style={{fontSize:'0.8rem',color:msg?.type==='error'?'var(--danger)':msg?.type==='warning'?'#d97706':'var(--success)'}}>
                  {msg?.text || ''}
                </span>
              </div>
            )}
            {/* Başka kullanıcı düzenliyor uyarısı (soft-lock) */}
            {isLockedByOther && docLock.lockedBy && (
              <div style={{ padding: '10px 16px', background: 'rgba(245,158,11,0.12)', borderBottom: '1px solid rgba(245,158,11,0.35)', flexShrink: 0, fontSize: '0.85rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '1.1rem' }}>🔒</span>
                <div style={{ flex: 1 }}>
                  Bu kaydı şu an <strong>{docLock.lockedBy.user_full_name || 'başka bir kullanıcı'}</strong> düzenliyor.
                  Düzenlemenin bitmesini bekleyin — sadece görüntüleyebilirsiniz.
                </div>
                <Button size="sm" variant="ghost" onClick={docLock.retry}>↻ Tekrar dene</Button>
              </div>
            )}
            {/* Faturalanmış irsaliye / aktarılmış sipariş uyarısı (kilitli) */}
            {isLockedInvoiced && (() => {
              if (isOrderMode) {
                const fst = selected?.fulfillment_status || 'partial';
                const label = fst === 'closed' ? 'tamamen aktarılmış' : 'kısmen aktarılmış';
                return (
                  <div style={{ padding: '10px 16px', background: 'rgba(124,58,237,0.1)', borderBottom: '1px solid rgba(124,58,237,0.3)', flexShrink: 0, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                    🔒 Bu sipariş {label} — düzenlenemez ve silinemez. "Aktarım Geçmişi" sekmesinden hedef belgeleri görebilirsin; değişiklik için ilgili hedef belge(ler)i silmen gerekir.
                  </div>
                );
              }
              return (
                <div style={{ padding: '10px 16px', background: 'rgba(124,58,237,0.1)', borderBottom: '1px solid rgba(124,58,237,0.3)', flexShrink: 0, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                  🔒 Bu irsaliye <strong>{selected?.invoice_document_number}</strong> faturasına aktarılmış — düzenlenemez ve silinemez. Değişiklik için önce ilgili faturayı silin.
                </div>
              );
            })()}
            {/* HERO STRIP */}
            {!isNew && selected && (
              <div style={{ padding: '12px 16px 0', flexShrink: 0 }}>
                <CardHero
                  icon={docIcon}
                  code={selected?.document_number || header.invoice_number}
                  title={header.account_title || header.account_code || docLabel}
                  subtitle={[
                    header.invoice_date && new Date(header.invoice_date).toLocaleDateString('tr-TR'),
                    activePriceDef && ('Fiyat: ' + activePriceDef.name),
                  ].filter(Boolean).join(' · ')}
                  statusBadge={
                    header.status
                      ? <CardStatusBadge
                          label={statusLabel(header.status)}
                          color={statusColor(header.status)}
                          bg={statusColor(header.status) + '22'}
                        />
                      : null
                  }
                  kpis={[
                    totals.grand_total > 0 && {
                      label: 'Toplam', value: totals.grand_total.toLocaleString('tr-TR', { minimumFractionDigits: 2 }),
                      color: 'primary', sublabel: header.currency_code || 'TRY',
                    },
                    totals.vat_total > 0 && { label: 'KDV', value: totals.vat_total.toLocaleString('tr-TR', { minimumFractionDigits: 2 }), color: 'neutral' },
                    lines.length > 0 && { label: 'Satır', value: lines.length, color: 'info' },
                  ].filter(Boolean)}
                  compact
                />
              </div>
            )}

            {/* TAB BAR */}
            <div style={{ padding: '0 16px', flexShrink: 0 }}>
              <CardTabs
                items={(() => {
                  let visibleTabs = ALL_TABS.filter(t => t.id !== 7 && t.id !== 9);
                  if (isQuoteMode) visibleTabs = ALL_TABS.filter(t => ![2,3,4,6,7].includes(t.id));
                  else if (isReturnMode && !isWaybillMode) visibleTabs = visibleTabs.filter(t => ![2,3].includes(t.id));
                  else if (isOrderMode) visibleTabs = ALL_TABS.filter(t => ![2,4,6,9].includes(t.id));
                  else if (isWaybillMode) visibleTabs = visibleTabs.filter(t => ![3,6].includes(t.id));
                  if (isNew) visibleTabs = visibleTabs.filter(t => t.id !== 6 && t.id !== 7 && t.id !== 9 && t.id !== 10);
                  return visibleTabs.map(tab => {
                    // Mode-specific override key öncelikli, yoksa varsayılan labelKey
                    let labelKey = tab.labelKey;
                    let fallback = tab.label;
                    if (isQuoteMode && tab.id === 0)         { labelKey = 'tabs.quoteHeader';   fallback = 'Teklif Başlığı'; }
                    else if (isOrderMode && tab.id === 0)    { labelKey = 'tabs.orderHeader';   fallback = 'Sipariş Başlığı'; }
                    else if (isWaybillMode) {
                      if (tab.id === 0)      { labelKey = 'tabs.waybillHeader';   fallback = 'İrsaliye Başlığı'; }
                      else if (tab.id === 2) { labelKey = 'tabs.shipmentCarrier'; fallback = 'Sevkiyat / Taşıyıcı'; }
                      else if (tab.id === 4) { labelKey = 'tabs.eWaybill';        fallback = 'e-İrsaliye'; }
                    }
                    const label = tt(labelKey, { defaultValue: fallback });
                    return { key: tab.id, label, icon: <TabIcon name={tab.icon}/> };
                  });
                })()}
                active={activeTab}
                onChange={setActiveTab}
                compact
              />
            </div>

            <div style={{flex:1,overflow:'hidden',display:'flex',flexDirection:'column'}}>
              <div style={{flex:1,overflowY:'auto',padding:16}}>
                {/* 3-WAY MATCH UYARI BANDI — alış faturasında PO ile sapma var ise */}
                {threeWayWarnings.length > 0 && (
                  <div style={{
                    marginBottom: 12,
                    padding: 12,
                    border: '1px solid ' + (threeWayWarnings.some(w => w.severity === 'high') ? 'var(--danger)' : 'var(--warning)'),
                    background: threeWayWarnings.some(w => w.severity === 'high') ? 'var(--danger-bg)' : 'var(--warning-bg)',
                    borderRadius: 'var(--r-md)',
                    fontSize: '0.85rem',
                  }}>
                    <div style={{ fontWeight: 700, color: threeWayWarnings.some(w => w.severity === 'high') ? 'var(--danger)' : 'var(--warning)', marginBottom: 6 }}>
                      ⚠ 3-Way Match Uyarısı — {threeWayWarnings.length} kalemde sapma
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                      {threeWayWarnings.slice(0, 5).map((w, i) => (
                        <li key={i} style={{ marginBottom: 2 }}>
                          <strong>{w.stock_name}</strong> · Sipariş {w.order_number}:
                          {Math.abs(w.qty_diff_pct) > 5 && <> miktar <strong>{w.invoiced_qty}</strong> vs <strong>{w.ordered_qty}</strong> ({w.qty_diff_pct > 0 ? '+' : ''}{w.qty_diff_pct.toFixed(1)}%)</>}
                          {Math.abs(w.price_diff_pct) > 5 && <>, fiyat <strong>{w.invoiced_price.toFixed(2)}</strong> vs <strong>{w.ordered_price.toFixed(2)}</strong> ({w.price_diff_pct > 0 ? '+' : ''}{w.price_diff_pct.toFixed(1)}%)</>}
                        </li>
                      ))}
                      {threeWayWarnings.length > 5 && <li style={{ fontStyle: 'italic' }}>...+{threeWayWarnings.length - 5} daha</li>}
                    </ul>
                  </div>
                )}

                {/* FİYAT GEÇMİŞİ HIZLI ERİŞİM — alış modunda + cari seçili */}
                {isPurchaseMode && !isReturnMode && header.account_id && lines.some(l => l.stock_id) && (
                  <div style={{ marginBottom: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', alignSelf: 'center' }}>📜 Fiyat geçmişi:</span>
                    {lines.filter(l => l.stock_id).slice(0, 5).map((l, i) => (
                      <button key={i} type="button"
                        onClick={() => setPriceHistoryFor({ stock_id: l.stock_id, stock_name: l.stock_name || l.manual_stock_name || 'Ürün' })}
                        style={{
                          padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600,
                          border: '1px solid var(--border-input)', background: 'var(--bg-card)',
                          color: 'var(--accent-primary)', borderRadius: 'var(--r-full)', cursor: 'pointer',
                        }}>
                        📊 {(l.stock_name || l.manual_stock_name || '').substring(0, 22)}
                      </button>
                    ))}
                  </div>
                )}

                <RenderBoundary>
                {activeTab===0&&(
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:10}}>
                    <div className="form-row"><label>Belge No</label><input value={selected?.document_number||'Otomatik'} disabled style={{...iStyle,fontFamily:'monospace',opacity:0.7}}/></div>
                    {!isQuoteMode && (
                      <div className="form-row"><label>{isOrderMode ? 'Sipariş No' : (isWaybillMode ? 'İrsaliye No' : 'Fatura No')}</label><input value={header.invoice_number} onChange={e=>h('invoice_number',e.target.value)} disabled={!canEdit} style={iStyle}/></div>
                    )}
                    <div className="form-row"><label>Tarih</label><input type="date" value={header.invoice_date} onChange={e=>h('invoice_date',e.target.value)} disabled={!canEdit} style={iStyle}/></div>
                    <div className="form-row"><label>Saat</label><input type="time" value={header.invoice_time} onChange={e=>h('invoice_time',e.target.value)} disabled={!canEdit} style={iStyle}/></div>
                    {isQuoteIncomingMode && (
                      <div className="form-row"><label>Teklif Numarası</label>
                        <input value={header.invoice_number||''} onChange={e=>h('invoice_number',e.target.value)} disabled={!canEdit}
                          placeholder="Tedarikçi teklif no" style={iStyle}/>
                      </div>
                    )}
                    <div className="form-row" style={{gridColumn:'1/-1'}}>
                      <CariCard
                        cari={(lookups.accounts || []).find(a => String(a.id) === String(header.account_id)) || null}
                        addresses={accountAddresses}
                        isProspect={!!header.is_prospect_account}
                        disabled={!canEdit}
                        onPick={() => { setAccountListSearch(''); setShowAccountList(true); }}
                        onQuickAddProspect={() => setQuickAddOpen('prospect')}
                        allowQuickAddProspect={isQuoteMode}
                        required
                      />
                    </div>
                    {!isWaybillMode && !isOrderMode && !isQuoteMode && <div className="form-row"><label>Vade</label><input type="date" value={header.due_date} onChange={e=>h('due_date',e.target.value)} disabled={!canEdit} style={iStyle}/></div>}
                    {isWaybillMode && <div className="form-row"><label>Sevk Tarihi</label><input type="date" value={header.dispatch_date} onChange={e=>h('dispatch_date',e.target.value)} disabled={!canEdit} style={iStyle}/></div>}
                    {(isWaybillMode || isOrderMode) && <div className="form-row"><label>Teslim Tarihi</label><input type="date" value={header.delivery_date} onChange={e=>h('delivery_date',e.target.value)} disabled={!canEdit} style={iStyle}/></div>}
                    {isOrderMode && (
                      <div className="form-row"><label>Sipariş Durumu</label>
                        <div style={{display:'flex',gap:6}}>
                          <select value={header.order_status_id || ''} onChange={e=>h('order_status_id', e.target.value || null)} disabled={!canEdit} style={{...iStyle,flex:1}}>
                            <option value="">Seçiniz</option>
                            {orderStatuses.map(s=><option key={s.id} value={s.id} style={s.color ? { color: s.color } : undefined}>{s.name}</option>)}
                          </select>
                          {canEdit && <button type="button" className="btn-ghost" title="Sipariş durumlarını yönet" onClick={()=>setShowOrderStatusMgr(true)} style={{padding:'4px 10px',fontSize:'1.1rem',fontWeight:700}}>+</button>}
                        </div>
                      </div>
                    )}
                    {isQuoteMode && (
                      <div className="form-row"><label>Geçerlilik Tarihi</label>
                        <input type="date" value={header.valid_until || ''} onChange={e=>h('valid_until', e.target.value || null)} disabled={!canEdit} style={iStyle}/>
                      </div>
                    )}
                    {isQuoteMode && (
                      <div className="form-row"><label>Teklif Durumu</label>
                        <div style={{display:'flex',gap:6}}>
                          <select value={header.quote_status_id || ''} onChange={e=>h('quote_status_id', e.target.value || null)} disabled={!canEdit} style={{...iStyle,flex:1}}>
                            <option value="">Seçiniz</option>
                            {quoteStatuses.map(s=><option key={s.id} value={s.id} style={s.color ? { color: s.color } : undefined}>{s.name}</option>)}
                          </select>
                          {canEdit && <button type="button" className="btn-ghost" title="Teklif durumlarını yönet" onClick={()=>setShowQuoteStatusMgr(true)} style={{padding:'4px 10px',fontSize:'1.1rem',fontWeight:700}}>+</button>}
                        </div>
                      </div>
                    )}
                    {/* ── Aktarım Durumu (kayıtlı + onaylı sipariş) ───────────── */}
                    {isOrderMode && selected && (() => {
                      const fst = selected.fulfillment_status || 'open';
                      const totalQty = lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0), 0);
                      const trQty    = lines.reduce((s, l) => s + (parseFloat(l.transferred_quantity) || 0), 0);
                      const remQty   = Math.max(0, totalQty - trQty);
                      // Yeni hiyerarşi sistemi: belge status='approved' ise aktarıma hazır,
                      // 'pending_approval' ise Bekleyen Onaylar'da işlem bekliyor
                      const isApproved = header.status === 'approved';
                      const isPendingApproval = header.status === 'pending_approval';
                      return (
                        <div style={{ gridColumn: '1 / -1', marginTop: 6 }}>
                          {fst === 'closed' ? (
                            <div style={{ padding: '10px 14px', background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 10, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                              <div style={{ fontWeight: 700, color: '#7c3aed', marginBottom: 2 }}>✓ Sipariş Tamamen Aktarıldı</div>
                              <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
                                Toplam {fmtQty(totalQty)} miktar dağıtıldı. Detay için "Aktarım Geçmişi" sekmesine bakın.
                              </div>
                            </div>
                          ) : fst === 'partial' ? (
                            <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                              <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                                <span style={{ fontWeight: 700, color: '#b45309' }}>◐ Kısmi Aktarım</span>
                                <span style={{ marginLeft: 10, fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
                                  Aktarılan {fmtQty(trQty)} / Toplam {fmtQty(totalQty)} · Kalan {fmtQty(remQty)}
                                </span>
                              </div>
                              {isApproved && remQty > 0 && (
                                <Button size="sm" onClick={openConvertModal} loading={convertingOrder}>📦 Kalanı Aktar</Button>
                              )}
                            </div>
                          ) : isApproved ? (
                            <div style={{ padding: '10px 14px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                              <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                                Sipariş aktarım için <strong>hazır</strong> — irsaliye veya faturaya kalem-bazlı kısmi aktarım yapılabilir.
                              </div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <Button size="sm" onClick={openConvertModal} loading={convertingOrder}>📦 Aktarım Yap</Button>
                              </div>
                            </div>
                          ) : isPendingApproval ? (
                            <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                              <div style={{ fontWeight: 700, color: '#b45309' }}>⏳ Onay Bekliyor</div>
                              <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                                Bu sipariş onay hiyerarşisinde işlem bekliyor. Sol menü → <strong>Bekleyen Onaylar</strong>'dan ilerletilmeli.
                              </div>
                            </div>
                          ) : (
                            <div style={{ padding: '8px 14px', background: 'var(--bg-secondary)', border: '1px dashed var(--border-secondary)', borderRadius: 10, fontSize: '0.82rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                              Sipariş henüz onaylanmadı — aktarım için belgenin onaylanmış olması gerekir.
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <div className="form-row"><label>Depo</label><select value={header.warehouse_id} onChange={e=>h('warehouse_id',e.target.value)} disabled={!canEdit} style={iStyle}><option value="">Seciniz</option>{lookups.warehouses.map(w=><option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
                    <div className="form-row" style={{gridColumn:'span 2',maxWidth:280}}><label>Personel</label>
                      <div style={{display:'flex',gap:6}}>
                        <select value={header.personnel_id} onChange={e=>h('personnel_id',e.target.value)} disabled={!canEdit} style={{...iStyle,flex:1}}>
                          <option value="">Seciniz</option>
                          {lookups.personnel.map(p=><option key={p.id} value={p.id}>{p.full_name}</option>)}
                        </select>
                        {canEdit && <button type="button" className="btn-ghost" title="Yeni personel ekle" onClick={()=>setQuickAddOpen('personel')} style={{padding:'4px 10px',fontSize:'1.1rem',fontWeight:700}}>+</button>}
                      </div>
                    </div>
                    <div className="form-row"><label>Proje <button className="quick-add-btn" onClick={()=>setShowQuick('project')}>+</button></label><select value={header.project_id} onChange={e=>h('project_id',e.target.value)} disabled={!canEdit} style={iStyle}><option value="">Seciniz</option>{lookups.projects.map(p=><option key={p.id} value={p.id}>{p.code}-{p.name}</option>)}</select></div>
                    <div className="form-row"><label>Döviz {header.currency_code&&<span style={{color:'var(--accent-primary)',fontWeight:600}}>({header.currency_code})</span>}</label><select value={header.currency_code} onChange={e=>h('currency_code',e.target.value)} disabled={!canEdit} style={iStyle}>{currencyList.map(c=><option key={c.id||c.code} value={c.code}>{c.code} - {c.name}</option>)}</select></div>
                    {isDiffCur&&<div className="form-row"><label>Kur</label><input type="number" step={rateStep()} value={header.exchange_rate} onChange={e=>h('exchange_rate',e.target.value)} disabled={!canEdit} style={iStyle}/></div>}
                    <div style={{gridColumn:'1 / -1',display:'flex',flexWrap:'wrap',gap:10,paddingTop:10,marginTop:6,borderTop:'1px solid var(--border-secondary)'}}>
                      <div className="form-row" style={{flex:'0 1 180px',minWidth:140}}><label>Özel Kod 1 <button className="quick-add-btn" onClick={()=>setShowQuick('special1')}>+</button></label><select value={header.special_code1} onChange={e=>h('special_code1',e.target.value)} disabled={!canEdit} style={iStyle}><option value="">Seciniz</option>{(lookups.specialCodes1||[]).map(s=><option key={s.id} value={s.code}>{s.name}</option>)}</select></div>
                      <div className="form-row" style={{flex:'0 1 180px',minWidth:140}}><label>Özel Kod 2 <button className="quick-add-btn" onClick={()=>setShowQuick('special2')}>+</button></label><select value={header.special_code2} onChange={e=>h('special_code2',e.target.value)} disabled={!canEdit} style={iStyle}><option value="">Seciniz</option>{(lookups.specialCodes2||[]).map(s=><option key={s.id} value={s.code}>{s.name}</option>)}</select></div>
                      {!isWaybillMode && !isOrderMode && !isQuoteMode && <div className="form-row" style={{flex:'0 1 180px',minWidth:140}}><label>Irsaliye No</label><input value={header.dispatch_number} onChange={e=>h('dispatch_number',e.target.value)} disabled={!canEdit} style={iStyle}/></div>}
                      <div className="form-row checkbox-row" style={{flex:'0 0 auto',alignSelf:'end',paddingBottom:8}}><label style={{display:'flex',gap:6,alignItems:'center',cursor:'pointer'}}><input type="checkbox" checked={header.is_vat_included} onChange={e=>h('is_vat_included',e.target.checked)} disabled={!canEdit}/> KDV Dahil Fiyat</label></div>
                    </div>
                  </div>
                )}

                {activeTab===1&&(() => {
                  const lineEntryEnabled = !!header.account_id && !!header.warehouse_id;
                  const lineDisabledReason = !header.account_id ? 'Önce Fatura Başlığı sekmesinden müşteri seçin'
                    : !header.warehouse_id ? 'Önce Fatura Başlığı sekmesinden depo seçin'
                    : '';
                  return (
                  <div>
                    {canEdit && !lineEntryEnabled && (
                      <div style={{
                        background: 'rgba(245,158,11,0.1)',
                        border: '1px solid rgba(245,158,11,0.3)',
                        color: '#b45309',
                        padding: '10px 14px',
                        borderRadius: 8,
                        marginBottom: 12,
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}>
                        <span style={{fontSize:'1.1rem'}}>⚠</span>
                        <span>{lineDisabledReason} — kalem ekleyebilmek için</span>
                      </div>
                    )}
                    {canEdit&&(
                      <div className="kalemler-header-card" style={{
                        background:'var(--bg-secondary)',
                        border:'1px solid var(--border-secondary)',
                        borderRadius:10,
                        marginBottom:12,
                        overflow:'hidden',
                        position:'sticky',
                        top:0,
                        zIndex:20,
                        boxShadow:'0 2px 8px rgba(0,0,0,0.06)',
                      }}>
                        <div className="kalemler-toolbar" style={{display:'flex',gap:8,padding:'10px 14px',alignItems:'center',flexWrap:'wrap'}}>
                          {sourceWaybillIds.length > 0 && (
                            <span style={{ fontSize:'0.78rem', color:'#7c3aed', fontWeight:600 }}>
                              {sourceWaybillIds.length} irsaliyeden aktarıldı
                            </span>
                          )}
                          {/* Barkod + Kamera (eşit yükseklik) */}
                          <div style={{position:'relative',display:'flex',gap:4,alignItems:'stretch'}}>
                            <div style={{position:'relative',flex:'1 1 auto',display:'flex'}}>
                              <input ref={barcodeRef} value={barcodeInput} onChange={e=>setBarcodeInput(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')handleBarcodeScan();}} placeholder="Barkod okut / Enter" disabled={!lineEntryEnabled} title={!lineEntryEnabled?lineDisabledReason:''} style={{...iStyle,width:200,height:34,paddingTop:0,paddingBottom:0,paddingLeft:36,fontFamily:'monospace',opacity:lineEntryEnabled?1:0.5}}/>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',opacity:0.4}}><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="7" y1="8" x2="7" y2="16"/><line x1="11" y1="8" x2="11" y2="16"/><line x1="15" y1="8" x2="15" y2="16"/></svg>
                            </div>
                            <button type="button" onClick={() => setBarcodeScannerOpen(true)} disabled={!lineEntryEnabled} title="Kamera ile tara"
                              style={{
                                height:34,
                                width:34,
                                padding:0,
                                background:'var(--bg-secondary)',
                                border:'1px solid var(--border-input)',
                                borderRadius:8,
                                cursor:lineEntryEnabled?'pointer':'not-allowed',
                                opacity:lineEntryEnabled?1:0.5,
                                fontSize:'0.95rem',
                                lineHeight:1,
                                boxSizing:'border-box',
                                display:'inline-flex',
                                alignItems:'center',
                                justifyContent:'center',
                                flexShrink:0,
                              }}>📷</button>
                          </div>
                          {/* İrsaliyeden Aktar (sadece fatura modunda + cari seçilince — teklif/sipariş hariç) */}
                          {!isWaybillMode && !isQuoteMode && !isOrderMode && header.account_id && lineEntryEnabled
                           && ['sales','sales_return','purchase','purchase_return'].includes(header.invoice_type) && (
                            <Button size="sm" variant="ghost" onClick={openWaybillImport} loading={importingWaybills}
                              style={{ borderColor: 'rgba(124,58,237,0.4)', color: '#7c3aed' }}>
                              📦 İrsaliyeden Aktar
                            </Button>
                          )}
                          <button className="btn-ghost" onClick={()=>setQuickAddOpen('stok')} disabled={!canEdit||!lineEntryEnabled} title={!lineEntryEnabled?lineDisabledReason:''}>+ Yeni Stok</button>
                          <button className="btn-ghost" onClick={openMultiStock} disabled={!canEdit||!lineEntryEnabled} title={!lineEntryEnabled?lineDisabledReason:''}>📋 Çoklu Stok Seç</button>
                          <button className="btn-ghost" onClick={applyCampaignsAndBundles} disabled={!canEdit||!lineEntryEnabled||campaignBusy} title="Paketleri bileşenlerine patlat + satır/sepet kampanyalarını uygula">{campaignBusy?'⏳ Uygulanıyor…':'🎁 Kampanya Uygula'}</button>
                          {colorSizeEnabled && (
                            <button className="btn-ghost" onClick={openSeriesPicker} disabled={!canEdit||!lineEntryEnabled} title={!lineEntryEnabled?lineDisabledReason:''}>📦 Seri Listesi</button>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Mobil compact liste — sadece mobilde görünür */}
                    <div className="invoice-mobile-lines mobile-only">
                      {lines.length === 0 ? (
                        <div style={{padding:24,textAlign:'center',color:'var(--text-tertiary)',fontSize:'0.85rem'}}>Henüz kalem yok — "+ Satır Ekle" ile başla</div>
                      ) : lines.map((ln, idx) => {
                        const stock = lookups.stocks.find(s => String(s.id) === String(ln.stock_id));
                        const unitName = (ln.units || []).find(u => u.type === ln.unit_type)?.name || 'Adet';
                        const stockName = stock?.name || ln.stock_name || (ln.stock_id ? '...' : '');
                        return (
                          <div key={idx} className="invoice-mobile-line" onClick={() => canEdit && setMobileEditIdx(idx)}>
                            <div className="iml-row1">
                              <span className="iml-num">{idx+1}</span>
                              <span className="iml-name">
                                {stockName || <em style={{color:'var(--text-tertiary)'}}>Stok seçin</em>}
                                {colorSizeEnabled && (ln.color_name || ln.size_name) && (
                                  <span style={{color:'var(--text-tertiary)',fontWeight:400,fontSize:'0.78rem',marginLeft:6}}>
                                    ({[ln.color_name, ln.size_name].filter(Boolean).join(' / ')})
                                  </span>
                                )}
                              </span>
                              {canEdit && <button className="iml-del" onClick={e => { e.stopPropagation(); removeLine(idx); }}>✕</button>}
                            </div>
                            <div className="iml-row2">
                              <span className="iml-qty">{fmtQ(ln.quantity || 0)} {unitName} × {fmt(ln.unit_price || 0)}</span>
                              <span className="iml-total">{fmt(ln.line_total || 0)} {header.currency_code}</span>
                            </div>
                          </div>
                        );
                      })}
                      {canEdit && (
                        <div style={{padding:'10px 12px',display:'flex',justifyContent:'flex-start'}}>
                          <button className="btn-primary-sm"
                            onClick={() => { const newIdx = lines.length; addLine(); setMobileEditIdx(newIdx); }}
                            disabled={!canEdit||!lineEntryEnabled}
                            style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:'0.84rem',fontWeight:600}}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                            Satır Ekle
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="invoice-table-wrap desktop-only-block" style={{overflowX:'auto',WebkitOverflowScrolling:'touch'}}>
                      <table className="invoice-line-table" style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8rem',minWidth:1500}}>
                        <thead>
                          <tr style={{borderBottom:'2px solid var(--border-secondary)'}}>
                            <th style={{padding:'5px 3px',width:28,color:'var(--text-tertiary)',fontWeight:500}}>#</th>
                            <th style={{width:28}}/>
                            <th style={{padding:'5px 10px',minWidth:340,width:340,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap',textAlign:'left'}}>Stok / Açıklama</th>
                            <th style={{padding:'5px 3px',width:75,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}}>Birim Mkt</th>
                            <th style={{padding:'5px 3px',width:140,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}}>Birim</th>
                            <th style={{padding:'5px 3px',width:75,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}}>Miktar</th>
                            <th style={{padding:'5px 3px',width:140,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}}>{isDiffCur?`Yerel Fiyat (${localCurrency})`:'Birim Fiyat'}</th>
                            {isDiffCur&&<th style={{padding:'5px 3px',width:120,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}}>{`İşlem Fiyatı (${header.currency_code})`}</th>}
                            <th style={{padding:'5px 3px',width:45,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}}>KDV D/H</th>
                            <th style={{padding:'5px 3px',width:130,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}} title="İskonto 1-5 (toplam 5 kademeli iskonto)">İskonto %</th>
                            <th style={{padding:'5px 3px',width:52,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}}>KDV%</th>
                            <th style={{padding:'5px 3px',width:52,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}}>OTV%</th>
                            <th style={{padding:'5px 3px',width:52,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}}>Tvk%</th>
                            <th style={{padding:'5px 3px',width:170,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}}>Depo</th>
                            <th style={{padding:'5px 3px',width:150,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}}>Personel</th>
                            <th style={{padding:'5px 3px',width:90,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}}>İsk.Tutar</th>
                            <th style={{padding:'5px 3px',width:90,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}}>KDV Tutar</th>
                            <th style={{padding:'5px 3px',width:105,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}}>{isDiffCur?`İşlem Dövizi (${header.currency_code})`:`Yerel Tutar (${localCurrency})`}</th>
                            {isDiffCur&&<th style={{padding:'5px 3px',width:105,color:'var(--text-tertiary)',fontWeight:500,whiteSpace:'nowrap'}}>{`Yerel Tutar (${localCurrency})`}</th>}
                            {isQuoteMode&&<th style={{padding:'5px 3px',width:80,color:'#7c3aed',fontWeight:600,whiteSpace:'nowrap'}} title="Bilgi amaçlı kar yüzdesi (manuel girilir)">Kâr %</th>}
                            {isOrderMode&&<th style={{padding:'5px 3px',width:90,color:'var(--info)',fontWeight:600,whiteSpace:'nowrap'}} title="Bu kalemden irsaliye/faturaya aktarılan miktar">Aktarılan</th>}
                            {isOrderMode&&<th style={{padding:'5px 3px',width:90,color:'var(--success)',fontWeight:600,whiteSpace:'nowrap'}} title="Aktarılması gereken kalan miktar">Kalan</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {lines.map((ln,idx)=>{
                            const is2={width:'100%',padding:'4px',background:'var(--bg-input)',border:'1px solid var(--border-input)',borderRadius:4,color:'var(--text-primary)',fontSize:'0.8rem'};
                            const colorSize = colorSizeEnabled;
                            const r3w = isDiffCur ? 'mob-w25' : 'mob-w33';
                            const r7w = isDiffCur ? 'mob-w25' : 'mob-w50';
                            const r6w = colorSize  ? 'mob-w33' : 'mob-w100';
                            return (
                              <React.Fragment key={idx}>
                              <tr style={{borderBottom:'none'}}>
                                <td className="col-num" style={{padding:'2px',width:28,textAlign:'center',verticalAlign:'top',paddingTop:14}}>{idx+1}</td>
                                <td className="col-del" style={{padding:'2px',width:28,verticalAlign:'top',paddingTop:10}}>
                                  {canEdit&&<button onClick={()=>removeLine(idx)} style={{background:'none',border:'none',color:'var(--danger)',cursor:'pointer',fontSize:'1rem'}}>✕</button>}
                                </td>
                                {/* STOK KARTI — rowSpan=2, sol kolonu kaplar, sağda numerikler + altında açıklama */}
                                <td rowSpan={2} className="stok-card-cell" style={{padding:'4px',verticalAlign:'top',minWidth:340,width:340}}>
                                  <div style={{
                                    padding:'7px 9px',
                                    background:'var(--bg-secondary)',
                                    border: ln.stock_id ? '1px solid var(--border-secondary)' : '1px dashed var(--border-input)',
                                    borderRadius:8,
                                    minHeight:54
                                  }}>
                                    <div style={{minWidth:0}}>
                                      <select
                                        value={ln.stock_id}
                                        onChange={e=>updateLine(idx,'stock_id',e.target.value)}
                                        onFocus={()=>setActiveLineIdx(idx)}
                                        disabled={!canEdit}
                                        style={{...is2, fontWeight: ln.stock_id ? 600 : 400, fontSize:'0.82rem', padding:'5px 6px'}}>
                                        <option value="">Stok seç...</option>
                                        {lookups.stocks.map(s => {
                                          const cs = colorSizeEnabled;
                                          const extras = cs ? [s.color_name, s.size_name].filter(Boolean).join(' / ') : '';
                                          return <option key={s.id} value={s.id}>{s.name}{extras ? ` — ${extras}` : ''}</option>;
                                        })}
                                      </select>
                                      <div style={{display:'flex',flexWrap:'wrap',gap:8,marginTop:5,fontSize:'0.7rem',color:'var(--text-tertiary)',alignItems:'center',lineHeight:1.4}}>
                                        {ln.stock_id ? (
                                          <>
                                            {ln.stock_code && <span style={{fontFamily:'monospace',color:'var(--text-secondary)',fontWeight:600}}>{ln.stock_code}</span>}
                                            {ln.barcode && (<><span style={{opacity:0.5}}>·</span><span style={{fontFamily:'monospace'}}>{ln.barcode}</span></>)}
                                            {colorSizeEnabled && ln.color_name && (<><span style={{opacity:0.5}}>·</span><strong style={{color:'var(--text-secondary)',fontWeight:600}}>{ln.color_name}</strong></>)}
                                            {colorSizeEnabled && ln.size_name && (<><span style={{opacity:0.5}}>·</span><strong style={{color:'var(--text-secondary)',fontWeight:600}}>{ln.size_name}</strong></>)}
                                            {!ln.stock_code && !ln.barcode && !ln.color_name && !ln.size_name && (
                                              <span style={{fontStyle:'italic',opacity:0.7}}>— ek bilgi yok —</span>
                                            )}
                                          </>
                                        ) : (
                                          <span style={{fontStyle:'italic',opacity:0.7}}>Kod / barkod / renk / beden seçilecek stok ile gelir</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td data-label="Birim Mkt" className="mob-w33" style={{padding:'2px',width:75}}>
                                  <input value={ln.quantity_base||''} disabled style={{...is2,textAlign:'right',color:'var(--accent-primary)',fontWeight:600}}/>
                                </td>
                                <td data-label="Birim" className="mob-w33" style={{padding:'2px',width:140}}>
                                  <select value={ln.unit_type||'unit1'} onChange={e=>updateLine(idx,'unit_type',e.target.value)} disabled={!canEdit} style={is2}>
                                    {(ln.units||[]).map(u=><option key={u.type} value={u.type}>{u.name} (x{u.ratio})</option>)}
                                    {(!ln.units||ln.units.length===0)&&<option value="unit1">Adet</option>}
                                  </select>
                                </td>
                                <td data-label="Miktar" className="mob-w33" style={{padding:'2px',width:75}}>
                                  <NumberInput value={ln.quantity} onChange={v=>updateLine(idx,'quantity',v)} decimals={3} disabled={!canEdit} style={{...is2,textAlign:'right'}}/>
                                </td>
                                <td className="line-br"/>
                                <td data-label={`Yerel Fiyat (${localCurrency})`} className={r3w} style={{padding:'2px',width:140}}>
                                  <div style={{display:'flex',gap:2,alignItems:'center'}}>
                                    <NumberInput
                                      value={isDiffCur?(ln.unit_price_local||0):ln.unit_price}
                                      onChange={v=>updateLine(idx,isDiffCur?'unit_price_local':'unit_price',v)}
                                      decimals={4} disabled={!canEdit} style={{...is2,textAlign:'right',flex:1}}/>
                                    <button type="button" onClick={() => openPricePicker(idx)} disabled={!canEdit || !ln.stock_id}
                                      title="Satış fiyat listesinden seç"
                                      style={{padding:'4px 6px',background:'var(--bg-secondary)',border:'1px solid var(--border-input)',borderRadius:4,cursor:(!canEdit||!ln.stock_id)?'not-allowed':'pointer',fontSize:'0.72rem',whiteSpace:'nowrap'}}>
                                      ₺▾
                                    </button>
                                  </div>
                                </td>
                                {isDiffCur&&(
                                  <td data-label={`İşlem Fiyatı (${header.currency_code})`} className="mob-w25" style={{padding:'2px',width:120}}>
                                    <NumberInput value={ln.unit_price} onChange={v=>updateLine(idx,'unit_price',v)} decimals={4} disabled={!canEdit} style={{...is2,textAlign:'right'}}/>
                                  </td>
                                )}
                                <td data-label="KDV D/H" className={`col-vat-inc ${r3w}`} style={{padding:'2px',width:45}}>
                                  <input type="checkbox" checked={!!ln.is_vat_included} onChange={e=>updateLine(idx,'is_vat_included',e.target.checked)} disabled={!canEdit}/>
                                </td>
                                <td className="line-br"/>
                                <td data-label="İskonto %" style={{padding:'2px',width:130}}>
                                  {(() => {
                                    const rates = [1,2,3,4,5].map(n => parseFloat(ln[`discount_rate${n}`])||0);
                                    const active = rates.filter(r => r > 0);
                                    const summary = active.length ? active.map(r => '%' + (+r.toFixed(2))).join('+') : '—';
                                    let combined = 1;
                                    rates.forEach(r => { if (r > 0) combined *= (1 - r/100); });
                                    const combinedPct = +((1 - combined) * 100).toFixed(2);
                                    const hasDiscount = active.length > 0;
                                    const openModal = () => { if (canEdit) setDiscountModalIdx(idx); };
                                    return (
                                      <button type="button" onClick={openModal}
                                        onKeyDown={e => { if ((e.key === 'Enter' || e.key === 'F2') && canEdit) { e.preventDefault(); openModal(); } }}
                                        disabled={!canEdit}
                                        title={hasDiscount ? `${summary} = birleşik %${combinedPct}` : 'İskonto eklemek için tıklayın'}
                                        style={{
                                          width:'100%', padding:'4px 6px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:4,
                                          background:'var(--bg-input)', border:'1px solid var(--border-input)', borderRadius:4,
                                          color: hasDiscount ? 'var(--danger)' : 'var(--text-tertiary)',
                                          fontSize:'0.78rem', fontFamily:'monospace', textAlign:'left',
                                          cursor: canEdit ? 'pointer' : 'default', opacity: canEdit ? 1 : 0.7,
                                        }}>
                                        <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1}}>{summary}</span>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{flexShrink:0,opacity:0.6}}>
                                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                        </svg>
                                      </button>
                                    );
                                  })()}
                                </td>
                                <td className="line-br"/>
                                {[['vat_rate','KDV%'],['otv_rate','OTV%'],['withholding_rate','Tvk%']].map(([dk,lbl])=>(
                                  <td key={dk} data-label={lbl} className="mob-w33" style={{padding:'2px',width:52}}>
                                    <input type="number" step="0.01" value={ln[dk]||''} onChange={e=>updateLine(idx,dk,e.target.value)} disabled={!canEdit} style={{...is2,textAlign:'right'}}/>
                                  </td>
                                ))}
                                <td className="line-br"/>
                                <td data-label="Depo" style={{padding:'2px',width:170}}>
                                  <select value={ln.warehouse_id||''} onChange={e=>updateLine(idx,'warehouse_id',e.target.value)} disabled={!canEdit} style={is2}>
                                    {lookups.warehouses.map(w=><option key={w.id} value={w.id}>{w.name}</option>)}
                                  </select>
                                </td>
                                <td data-label="Personel" style={{padding:'2px',width:150}}>
                                  <select value={ln.personnel_id||''} onChange={e=>updateLine(idx,'personnel_id',e.target.value)} disabled={!canEdit} style={is2}>
                                    <option value="">— yok —</option>
                                    {(lookups.personnel||[]).map(p => (
                                      <option key={p.id} value={p.id}>{p.full_name || (p.first_name + ' ' + (p.last_name||''))}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="line-br"/>
                                <td data-label="İsk.Tutar" className={`mob-readonly ${r7w}`} style={{padding:'2px',width:90}}>
                                  <span style={{fontFamily:'monospace',fontSize:'0.85rem',color:'var(--danger)',padding:'4px 0'}}>{ln.line_discount>0?'-'+fmt(ln.line_discount):'-'}</span>
                                </td>
                                <td data-label="KDV Tutar" className={`mob-readonly ${r7w}`} style={{padding:'2px',width:90}}>
                                  <span style={{fontFamily:'monospace',fontSize:'0.85rem',color:'var(--accent-primary)',padding:'4px 0'}}>{fmt(ln.line_vat)}</span>
                                </td>
                                <td data-label={`İşlem Dövizi (${header.currency_code})`} className={`col-total ${r7w}`} style={{padding:'2px',width:105,textAlign:'right',fontWeight:600,fontFamily:'monospace',fontSize:'0.82rem'}}>
                                  <span>{fmt(ln.line_total)} {header.currency_code}</span>
                                </td>
                                {isDiffCur&&(
                                  <td data-label={`Yerel Tutar (${localCurrency})`} className={`mob-readonly ${r7w}`} style={{padding:'2px',width:105}}>
                                    <span style={{fontFamily:'monospace',fontSize:'0.82rem',color:'var(--text-secondary)',padding:'4px 0'}}>{fmt(ln.line_total_local)} {localCurrency}</span>
                                  </td>
                                )}
                                {isQuoteMode && (
                                  <td data-label="Kâr %" style={{padding:'2px',width:80}}>
                                    <input type="number" step="0.01" value={ln.profit_pct||''}
                                      onChange={e=>{const v=e.target.value;setLines(prev=>{const nl=[...prev];nl[idx]={...nl[idx],profit_pct:v};return nl;});}}
                                      disabled={!canEdit}
                                      placeholder="—"
                                      style={{...is2, color: '#7c3aed', fontWeight: 600, textAlign: 'right'}}/>
                                  </td>
                                )}
                                {isOrderMode && (() => {
                                  const totalQ  = parseFloat(ln.quantity) || 0;
                                  const trQ     = parseFloat(ln.transferred_quantity) || 0;
                                  const remainQ = Math.max(0, totalQ - trQ);
                                  const fullyTransferred = totalQ > 0 && trQ >= totalQ;
                                  return (
                                    <>
                                      <td data-label="Aktarılan" style={{padding:'2px',width:90,textAlign:'right',fontFamily:'monospace',fontSize:'0.82rem',color:'var(--info)',fontWeight:600}}>
                                        {fmtQ(trQ)}
                                      </td>
                                      <td data-label="Kalan" style={{padding:'2px',width:90,textAlign:'right',fontFamily:'monospace',fontSize:'0.82rem',color: fullyTransferred ? 'var(--text-tertiary)' : 'var(--success)',fontWeight:600}}>
                                        {fmtQ(remainQ)}
                                      </td>
                                    </>
                                  );
                                })()}
                              </tr>
                              {/* Alt satır: Manuel + Kalem Açıklama + Sevk (stok kartı yukarıda rowSpan ile) */}
                              <tr style={{borderBottom:'1px solid var(--border-secondary)'}}>
                                <td/>
                                <td/>
                                {/* rowSpan stok kartı bu satırı kaplar — burada td YOK */}
                                <td colSpan={99} style={{padding:'4px 4px 10px 4px'}}>
                                  {(() => {
                                    const cols = [];
                                    if (isQuoteMode) cols.push('minmax(180px, 1fr)');
                                    cols.push('minmax(280px, 2fr)');
                                    if (accountAddresses.length > 0 && ln.stock_id) cols.push('minmax(200px, 1fr)');
                                    return (
                                      <div style={{display:'grid',gridTemplateColumns: cols.join(' '),gap:10,alignItems:'start'}}>
                                        {/* Manuel Ürün Adı yalnızca tekliflerde — diğer belgelerde stok zorunlu */}
                                        {isQuoteMode && (
                                          <div>
                                            <label style={{fontSize:'0.7rem',color:'var(--text-tertiary)',display:'block',marginBottom:2}}>Manuel Ürün Adı</label>
                                            <input
                                              type="text"
                                              value={ln.manual_stock_name||''}
                                              onChange={e=>updateLine(idx,'manual_stock_name',e.target.value)}
                                              onFocus={()=>setActiveLineIdx(idx)}
                                              disabled={!canEdit||!!ln.stock_id}
                                              placeholder={ln.stock_id ? '— stok seçildi —' : 'Stok yoksa manuel ürün adı yazın'}
                                              title={ln.stock_id ? 'Stok seçilmiş — manuel ad gerekmez' : 'Stok seçmeden ürün adı yazabilirsiniz'}
                                              style={{...is2, fontStyle: ln.stock_id ? 'normal' : 'italic', opacity: ln.stock_id ? 0.5 : 1}}
                                            />
                                          </div>
                                        )}
                                        <div style={{ display: 'grid', gridTemplateColumns: isOrderMode ? '160px 1fr' : '1fr', gap: 8 }}>
                                          {/* Kalem Teslim Tarihi — sipariş modunda, açıklamanın önünde */}
                                          {isOrderMode && (
                                            <div>
                                              <label style={{fontSize:'0.7rem',color:'var(--text-tertiary)',display:'block',marginBottom:2}}>📅 Kalem Teslim Tarihi</label>
                                              <input
                                                type="date"
                                                value={ln.delivery_date || ''}
                                                onChange={e=>{const v=e.target.value;setLines(prev=>{const nl=[...prev];nl[idx]={...nl[idx],delivery_date:v};return nl;});}}
                                                disabled={!canEdit}
                                                style={is2}
                                              />
                                            </div>
                                          )}
                                          <input
                                            type="text"
                                            value={ln.description||''}
                                            onChange={e=>{const v=e.target.value;setLines(prev=>{const nl=[...prev];nl[idx]={...nl[idx],description:v};return nl;});}}
                                            disabled={!canEdit}
                                            placeholder="Kalem açıklaması..."
                                            style={is2}
                                          />
                                        </div>
                                        {/* Sevk Adresi — cari adresler tanımlıysa ve stok seçildiyse görünür */}
                                        {accountAddresses.length > 0 && ln.stock_id && (
                                          <div>
                                            <label style={{fontSize:'0.7rem',color:'var(--text-tertiary)',display:'block',marginBottom:2}}>📍 Sevk Adresi</label>
                                            <select
                                              value={ln.delivery_address_id||''}
                                              onChange={e=>updateLine(idx,'delivery_address_id',e.target.value||null)}
                                              disabled={!canEdit}
                                              title={ln.delivery_address_id ? (accountAddresses.find(a=>a.id===ln.delivery_address_id)?.address || '') : 'Adres seçilmedi'}
                                              style={is2}>
                                              <option value="">— adres seç —</option>
                                              {accountAddresses.map(a => (
                                                <option key={a.id} value={a.id}>
                                                  {a.label}{a.is_default ? ' ★' : ''}{a.city ? ' (' + a.city + ')' : ''}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </td>
                              </tr>
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {canEdit && (
                      <div className="kalemler-add-row desktop-only" style={{display:'flex',justifyContent:'flex-start',marginTop:8}}>
                        <button className="btn-primary-sm"
                          onClick={() => { const newIdx = lines.length; addLine(); if (window.innerWidth <= 768) setMobileEditIdx(newIdx); }}
                          disabled={!canEdit||!lineEntryEnabled}
                          title={!lineEntryEnabled?lineDisabledReason:'Yeni satır ekle'}
                          style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:'0.84rem',fontWeight:600}}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                          Satır Ekle
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })()}

                {activeTab===2&&(
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:24}}>
                    <div>
                      <h3 style={{fontSize:'0.9rem',fontWeight:600,marginBottom:10,paddingBottom:6,borderBottom:'1px solid var(--border-secondary)'}}>Fatura Kesilen</h3>
                      <div className="form-row" style={{marginBottom:10}}><label>Sekil</label>
                        <div className="radio-group-row" style={{display:'flex',gap:16,marginTop:4,flexWrap:'wrap'}}>
                          {[['same','Carinin Adresi'],['customer','Farkli Cari'],['manual','Manuel']].map(([v,l])=>(
                            <label key={v} style={{display:'flex',gap:6,alignItems:'center',cursor:'pointer',fontSize:'0.85rem'}}>
                              <input type="radio" checked={header.bill_to_mode===v} disabled={!canEdit} onChange={()=>h('bill_to_mode',v)}/>{l}
                            </label>
                          ))}
                        </div>
                      </div>
                      {header.bill_to_mode==='customer'&&(
                        <div className="form-row"><label>Cari</label>
                          <select value={header.bill_to_customer_id} onChange={e=>{const c=lookups.accounts.find(x=>x.id===e.target.value);h('bill_to_customer_id',e.target.value);if(c){h('bill_to_name',c.title||'');h('bill_to_address',c.address||'');h('bill_to_city',c.city||'');}}} disabled={!canEdit} style={iStyle}>
                            <option value="">-- Seciniz --</option>{lookups.accounts.map(a=><option key={a.id} value={a.id}>{formatCariOption(a)}</option>)}
                          </select>
                        </div>
                      )}
                      {header.bill_to_mode!=='same'&&(
                        <div style={{display:'grid',gap:8}}>
                          {[['Ad/Unvan','bill_to_name'],['Vergi No','bill_to_tax_number'],['Adres','bill_to_address'],['Sehir','bill_to_city'],['Ilce','bill_to_district'],['Tel','bill_to_phone'],['E-posta','bill_to_email']].map(([lbl,field])=>(
                            <div key={field} className="form-row" style={{margin:0}}><label style={{fontSize:'0.8rem'}}>{lbl}</label><input value={header[field]||''} onChange={e=>h(field,e.target.value)} disabled={!canEdit} style={{...iStyle,padding:'6px 10px',fontSize:'0.85rem'}}/></div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 style={{fontSize:'0.9rem',fontWeight:600,marginBottom:10,paddingBottom:6,borderBottom:'1px solid var(--border-secondary)'}}>Sevkiyat Adresi</h3>
                      <div className="form-row" style={{marginBottom:10}}><label>Sekil</label>
                        <div className="radio-group-row" style={{display:'flex',gap:16,marginTop:4,flexWrap:'wrap'}}>
                          {[['none','Yok'],['general','Genel'],['per_line','Kalem Bazli']].map(([v,l])=>(
                            <label key={v} style={{display:'flex',gap:6,alignItems:'center',cursor:'pointer',fontSize:'0.85rem'}}>
                              <input type="radio" checked={header.ship_to_mode===v} disabled={!canEdit} onChange={()=>h('ship_to_mode',v)}/>{l}
                            </label>
                          ))}
                        </div>
                      </div>
                      {header.ship_to_mode==='general'&&(
                        <div style={{display:'grid',gap:8}}>
                          {[['Alici','ship_to_name'],['Adres','ship_to_address'],['Sehir','ship_to_city'],['Ilce','ship_to_district'],['Tel','ship_to_phone']].map(([lbl,field])=>(
                            <div key={field} className="form-row" style={{margin:0}}><label style={{fontSize:'0.8rem'}}>{lbl}</label><input value={header[field]||''} onChange={e=>h(field,e.target.value)} disabled={!canEdit} style={{...iStyle,padding:'6px 10px',fontSize:'0.85rem'}}/></div>
                          ))}
                          <div className="form-row" style={{margin:0}}><label style={{fontSize:'0.8rem'}}>Kargo</label>
                            <select value={header.carrier_id||''} onChange={e=>h('carrier_id',e.target.value)} disabled={!canEdit} style={{...iStyle,padding:'6px 10px',fontSize:'0.85rem'}}>
                              <option value="">--</option>{(lookups.carriers||[]).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </div>
                          <div className="form-row" style={{margin:0}}><label style={{fontSize:'0.8rem'}}>Takip No</label><input value={header.tracking_number||''} onChange={e=>h('tracking_number',e.target.value)} disabled={!canEdit} style={{...iStyle,padding:'6px 10px',fontSize:'0.85rem'}}/></div>
                        </div>
                      )}
                    </div>
                    {isWaybillMode && (
                      <div style={{gridColumn:'1 / -1'}}>
                        <h3 style={{fontSize:'0.9rem',fontWeight:600,marginBottom:10,paddingBottom:6,borderBottom:'1px solid var(--border-secondary)'}}>Taşıyıcı & Şoför Bilgileri</h3>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:10}}>
                          <div className="form-row" style={{margin:0}}><label style={{fontSize:'0.8rem'}}>Araç Plakası *</label><input value={header.vehicle_plate||''} onChange={e=>h('vehicle_plate',e.target.value.toUpperCase())} disabled={!canEdit} placeholder="34 ABC 123" style={{...iStyle,padding:'6px 10px',fontSize:'0.85rem'}}/></div>
                          <div className="form-row" style={{margin:0}}><label style={{fontSize:'0.8rem'}}>Şoför Ad-Soyad *</label><input value={header.driver_name||''} onChange={e=>h('driver_name',e.target.value)} disabled={!canEdit} style={{...iStyle,padding:'6px 10px',fontSize:'0.85rem'}}/></div>
                          <div className="form-row" style={{margin:0}}><label style={{fontSize:'0.8rem'}}>Şoför TCKN</label><input value={header.driver_tckn||''} onChange={e=>h('driver_tckn',e.target.value)} disabled={!canEdit} maxLength={11} style={{...iStyle,padding:'6px 10px',fontSize:'0.85rem'}}/></div>
                          <div className="form-row" style={{margin:0}}><label style={{fontSize:'0.8rem'}}>Taşıyıcı Ünvan</label><input value={header.carrier_name||''} onChange={e=>h('carrier_name',e.target.value)} disabled={!canEdit} style={{...iStyle,padding:'6px 10px',fontSize:'0.85rem'}}/></div>
                          <div className="form-row" style={{margin:0}}><label style={{fontSize:'0.8rem'}}>Taşıyıcı VKN/TCKN</label><input value={header.carrier_vkn||''} onChange={e=>h('carrier_vkn',e.target.value)} disabled={!canEdit} maxLength={11} style={{...iStyle,padding:'6px 10px',fontSize:'0.85rem'}}/></div>
                          <div className="form-row" style={{margin:0}}><label style={{fontSize:'0.8rem'}}>Takip No</label><input value={header.carrier_tracking_no||''} onChange={e=>h('carrier_tracking_no',e.target.value)} disabled={!canEdit} style={{...iStyle,padding:'6px 10px',fontSize:'0.85rem'}}/></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab===3&&(
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:12,maxWidth:900}}>
                    <label style={{ gridColumn:'1/-1', display:'flex', alignItems:'center', gap:8, padding:'8px 12px', background:'var(--bg-secondary)', border:'1px solid var(--border-secondary)', borderRadius:6, cursor: canEdit?'pointer':'default', fontSize:'0.85rem' }}>
                      <input type="checkbox" checked={!!header.is_ecommerce_order} onChange={e=>h('is_ecommerce_order', e.target.checked)} disabled={!canEdit} />
                      <span style={{ fontWeight:600 }}>E-Ticaret Siparişi</span>
                      <span style={{ color:'var(--text-tertiary)', fontSize:'0.78rem' }}>— bu belge bir e-ticaret kanalından gelmiştir</span>
                    </label>
                    <div className="form-row"><label>Platform</label>
                      <select value={header.ecommerce_platform_id||''} onChange={e=>{const p=(lookups.ecommercePlatforms||[]).find(x=>x.id===e.target.value);h('ecommerce_platform_id',e.target.value);if(p)h('commission_rate',p.commission_rate||0);}} disabled={!canEdit} style={iStyle}>
                        <option value="">-- Seciniz --</option>{(lookups.ecommercePlatforms||[]).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                    {!isOrderMode && <div className="form-row"><label>Siparis No</label><input value={header.ecommerce_order_no||''} onChange={e=>h('ecommerce_order_no',e.target.value)} disabled={!canEdit} style={iStyle}/></div>}
                    {!isOrderMode && <div className="form-row"><label>Siparis Tarihi</label><input type="date" value={header.ecommerce_order_date||''} onChange={e=>h('ecommerce_order_date',e.target.value)} disabled={!canEdit} style={iStyle}/></div>}
                    <div className="form-row"><label>Kargo</label>
                      <select value={header.carrier_id||''} onChange={e=>h('carrier_id',e.target.value)} disabled={!canEdit} style={iStyle}>
                        <option value="">-- Seciniz --</option>{(lookups.carriers||[]).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div className="form-row"><label>Takip No</label><input value={header.tracking_number||''} onChange={e=>h('tracking_number',e.target.value)} disabled={!canEdit} style={iStyle}/></div>
                    <div className="form-row"><label>Komisyon %</label><input type="number" step="0.01" value={header.commission_rate||0} onChange={e=>h('commission_rate',parseFloat(e.target.value)||0)} disabled={!canEdit} style={iStyle}/></div>
                    <div className="form-row"><label>Komisyon Tutari</label><input value={fmt(+((header.commission_rate||0)/100*(totals.grand_total||0)).toFixed(2))} readOnly style={{...iStyle,opacity:0.7}}/></div>
                  </div>
                )}

                {activeTab===4 && !isWaybillMode && (
                  <div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:10}}>
                      <div className="form-row"><label>Belge Türü</label><select value={header.e_doc_type||''} onChange={e=>h('e_doc_type',e.target.value)} disabled={!canEdit} style={iStyle}><option value="">Seciniz</option><option value="EFATURA">e-Fatura</option><option value="EARSIV">e-Arsiv</option><option value="KAGIT">Kagit</option></select></div>
                      <div className="form-row"><label>Senaryo</label><select value={header.e_invoice_scenario||''} onChange={e=>h('e_invoice_scenario',e.target.value)} disabled={!canEdit} style={iStyle}><option value="">Seciniz</option><option value="TEMEL">TEMEL</option><option value="TICARI">TICARI</option></select></div>
                      <div className="form-row"><label>Fatura Tipi</label><select value={header.e_invoice_type_code||''} onChange={e=>h('e_invoice_type_code',e.target.value)} disabled={!canEdit} style={iStyle}><option value="">Seciniz</option><option value="SATIS">SATIS</option><option value="IADE">IADE</option><option value="TEVKIFAT">TEVKIFAT</option><option value="ISTISNA">ISTISNA</option></select></div>
                      <div className="form-row"><label>KDV Istisna</label><select value={header.tax_exemption_code||''} onChange={e=>h('tax_exemption_code',e.target.value)} disabled={!canEdit} style={iStyle}><option value="">Yok</option><option value="301">301</option><option value="302">302</option><option value="310">310</option><option value="350">350</option></select></div>
                      <div className="form-row" style={{gridColumn:'1/3'}}><label>Istisna Açıklaması</label><input value={header.tax_exemption_reason||''} onChange={e=>h('tax_exemption_reason',e.target.value)} disabled={!canEdit} style={iStyle}/></div>
                      <div className="form-row"><label>ETTN</label><input value={selected?.e_invoice_ettn||''} disabled placeholder="Otomatik" style={{...iStyle,opacity:0.7,fontFamily:'monospace',fontSize:'0.8rem'}}/></div>
                      <div className="form-row"><label>e-Fatura Durumu</label><input value={selected?.e_invoice_status||'Gonderilmedi'} disabled style={{...iStyle,opacity:0.7}}/></div>
                    </div>

                    {/* ─── YN ÖKC (Yazar Kasa) Bilgileri — varsa göster ─── */}
                    {selected && (selected.okc_check_seq || selected.okc_receipt_no || selected.okc_invoice_no || selected.okc_z_no) && (
                      <div style={{marginTop:24, padding:14, background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:8}}>
                        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
                          <span style={{fontSize:20}}>🧾</span>
                          <div style={{fontWeight:700,fontSize:'0.95rem',color:'#f59e0b'}}>YN ÖKC (Yazar Kasa) Bilgileri</div>
                        </div>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:10}}>
                          <div className="form-row">
                            <label>Check Seq (Fiş Sıra No)</label>
                            <input value={selected.okc_check_seq||''} disabled style={{...iStyle,opacity:0.85,fontFamily:'monospace',fontWeight:600}}/>
                          </div>
                          <div className="form-row">
                            <label>Fiş No (Receipt)</label>
                            <input value={selected.okc_receipt_no||''} disabled style={{...iStyle,opacity:0.85,fontFamily:'monospace'}}/>
                          </div>
                          <div className="form-row">
                            <label>Z No</label>
                            <input value={selected.okc_z_no||''} disabled style={{...iStyle,opacity:0.85,fontFamily:'monospace'}}/>
                          </div>
                          <div className="form-row">
                            <label>EKÜ No</label>
                            <input value={selected.okc_eku_no||''} disabled style={{...iStyle,opacity:0.85,fontFamily:'monospace'}}/>
                          </div>
                          <div className="form-row">
                            <label>e-Belge No (Kasadan)</label>
                            <input value={selected.okc_invoice_no||''} disabled style={{...iStyle,opacity:0.85,fontFamily:'monospace'}} placeholder="—"/>
                          </div>
                          <div className="form-row">
                            <label>ETTN (Kasadan)</label>
                            <input value={selected.okc_ett_no||''} disabled style={{...iStyle,opacity:0.85,fontFamily:'monospace',fontSize:'0.78rem'}} placeholder="—"/>
                          </div>
                          {selected.okc_invoice_url && (
                            <div className="form-row" style={{gridColumn:'1/-1'}}>
                              <label>e-Belge Linki</label>
                              <div style={{display:'flex',gap:8}}>
                                <input value={selected.okc_invoice_url} disabled style={{...iStyle,opacity:0.85,fontFamily:'monospace',fontSize:'0.78rem',flex:1}}/>
                                <a href={selected.okc_invoice_url} target="_blank" rel="noreferrer" style={{padding:'8px 14px',background:'#f59e0b',color:'#fff',borderRadius:6,textDecoration:'none',fontWeight:600,fontSize:'0.82rem',whiteSpace:'nowrap'}}>🔗 Aç</a>
                                <button type="button" onClick={()=>{navigator.clipboard?.writeText(selected.okc_invoice_url); }} style={{padding:'8px 12px',background:'transparent',color:'var(--text-secondary)',border:'1px solid var(--border)',borderRadius:6,cursor:'pointer',fontSize:'0.82rem'}}>📋</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* e-İRSALİYE — Hızlı Bilişim entegrasyonu Faz 2 */}
                {activeTab===4 && isWaybillMode && (
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:12}}>
                    <div style={{gridColumn:'1 / -1', padding:12, background:'rgba(99,102,241,0.08)', borderRadius:8, border:'1px solid rgba(99,102,241,0.2)', fontSize:'0.82rem', color:'var(--text-secondary)'}}>
                      <strong>Hızlı Bilişim e-İrsaliye entegrasyonu Faz 2'de aktif olacak.</strong> Şu an alanlar veri girişi içindir; gönderim, durum sorgu ve QR kod sonraki sürümde devreye girer.
                    </div>
                    <div className="form-row"><label>Senaryo</label>
                      <select value={header.e_invoice_scenario||'TEMELIRSALIYE'} onChange={e=>h('e_invoice_scenario',e.target.value)} disabled={!canEdit} style={iStyle}>
                        <option value="TEMELIRSALIYE">Temel İrsaliye</option>
                        <option value="TASIYICIIRSALIYESI">Taşıyıcı İrsaliyesi</option>
                        <option value="IHRACAT">İhracat</option>
                      </select>
                    </div>
                    <div className="form-row"><label>Belge Türü</label>
                      <select value={header.e_doc_type||'EIRSALIYE'} onChange={e=>h('e_doc_type',e.target.value)} disabled={!canEdit} style={iStyle}>
                        <option value="EIRSALIYE">e-İrsaliye</option>
                        <option value="KAGIT">Kağıt İrsaliye</option>
                      </select>
                    </div>
                    <div className="form-row"><label>İlgili Sipariş No</label>
                      <input value={header.related_order_number||header.order_number||''} onChange={e=>h('order_number',e.target.value)} disabled={!canEdit} placeholder="Varsa..." style={iStyle}/>
                    </div>
                    <div className="form-row" style={{gridColumn:'1 / -1'}}><label>İlgili e-Fatura UUID</label>
                      <input value={header.related_invoice_uuid||''} onChange={e=>h('related_invoice_uuid',e.target.value)} disabled={!canEdit} placeholder="Önceden e-Fatura kesildiyse..." style={{...iStyle,fontFamily:'monospace',fontSize:'0.8rem'}}/>
                    </div>
                    <div className="form-row"><label>ETTN (UUID)</label>
                      <input value={selected?.e_invoice_ettn||'—'} disabled style={{...iStyle,opacity:0.7,fontFamily:'monospace',fontSize:'0.8rem'}}/>
                    </div>
                    <div className="form-row"><label>e-İrsaliye Durumu</label>
                      <input value={selected?.e_invoice_status||'Henüz gönderilmedi'} disabled style={{...iStyle,opacity:0.7}}/>
                    </div>
                    <div style={{gridColumn:'1 / -1', display:'flex', gap:8, marginTop:6}}>
                      <button className="btn-ghost" disabled title="Faz 2'de aktive edilecek">📤 e-İrsaliye Gönder</button>
                      <button className="btn-ghost" disabled title="Faz 2'de aktive edilecek">🔄 Durum Sorgula</button>
                      <button className="btn-ghost" disabled title="Faz 2'de aktive edilecek">📄 QR / PDF Görüntüle</button>
                    </div>
                  </div>
                )}

                {activeTab===5&&(
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:16}}>
                    <div className="form-row"><label>{isWaybillMode ? 'İrsaliye Notu (baskıda görünür)' : 'Fatura Notu (baskida gorunur)'}</label><textarea rows={6} value={header.notes||''} onChange={e=>h('notes',e.target.value)} disabled={!canEdit} style={{...iStyle,resize:'vertical',fontFamily:'Outfit'}}/></div>
                    <div className="form-row"><label>Dahili Not</label><textarea rows={6} value={header.internal_note||''} onChange={e=>h('internal_note',e.target.value)} disabled={!canEdit} style={{...iStyle,resize:'vertical',fontFamily:'Outfit'}}/></div>
                  </div>
                )}

                {activeTab===6 && (
                  <div style={{ padding: '8px 0' }}>
                    {linkedDocsLoading ? (
                      <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-tertiary)' }}>Yükleniyor...</div>
                    ) : linkedDocs.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
                        Bu faturaya bağlı belge yok. <br />
                        <span style={{ fontSize: '0.78rem' }}>(İrsaliye, sipariş, teklif, tahsilat/tediye gibi bağlantılar burada görünecek)</span>
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                          <thead>
                            <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-secondary)' }}>
                              <th style={{ padding: '8px', textAlign: 'left' }}>Tip</th>
                              <th style={{ padding: '8px', textAlign: 'left' }}>Belge No</th>
                              <th style={{ padding: '8px', textAlign: 'left' }}>Tarih</th>
                              <th style={{ padding: '8px', textAlign: 'center' }}>Durum</th>
                              <th style={{ padding: '8px', textAlign: 'right' }}>Tutar</th>
                            </tr>
                          </thead>
                          <tbody>
                            {linkedDocs.map((d, i) => {
                              const statusMap = { draft: 'Taslak', approved: 'Onaylı', cancelled: 'İptal', invoiced: 'Faturalandı' };
                              const statusColor = d.status === 'approved' || d.status === 'invoiced' ? '#16a34a' : d.status === 'cancelled' ? '#64748b' : '#d97706';
                              return (
                                <tr key={d.id || i} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                                  <td style={{ padding: '8px' }}>
                                    <span style={{ fontSize: '0.74rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600, background: d.color + '22', color: d.color }}>
                                      {d.type_label}
                                    </span>
                                  </td>
                                  <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{d.document_number || '-'}</td>
                                  <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{d.date ? new Date(d.date).toLocaleDateString('tr-TR') : '-'}</td>
                                  <td style={{ padding: '8px', textAlign: 'center' }}>
                                    <span style={{ fontSize: '0.7rem', padding: '2px 7px', borderRadius: 4, background: statusColor + '18', color: statusColor }}>
                                      {statusMap[d.status] || d.status}
                                    </span>
                                  </td>
                                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>
                                    {d.amount != null ? `${fmt(d.amount)} ${d.currency_code || ''}` : '-'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Bağlı tahsilat / tediye makbuzları */}
                    {(linkedReceipts.length > 0 || linkedPayments.length > 0) && (
                      <div style={{ marginTop: 20 }}>
                        <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem', color: 'var(--accent-primary)' }}>💰 Bağlı Tahsilat / Tediye Makbuzları</h4>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                          <thead>
                            <tr style={{ background: 'var(--bg-tertiary)' }}>
                              <th style={{ padding: 8, textAlign: 'left' }}>Tür</th>
                              <th style={{ padding: 8, textAlign: 'left' }}>Belge No</th>
                              <th style={{ padding: 8, textAlign: 'left' }}>Tarih</th>
                              <th style={{ padding: 8, textAlign: 'left' }}>Cari</th>
                              <th style={{ padding: 8, textAlign: 'right' }}>Tutar</th>
                            </tr>
                          </thead>
                          <tbody>
                            {linkedReceipts.map(r => (
                              <tr key={r.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                                <td style={{ padding: 8 }}><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 'var(--r-full)', background: 'var(--success-bg)', color: 'var(--success)', fontWeight: 700 }}>📥 Tahsilat</span></td>
                                <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 12 }}>{r.receipt_no}</td>
                                <td style={{ padding: 8 }}>{new Date(r.date).toLocaleDateString('tr-TR')}</td>
                                <td style={{ padding: 8 }}>{r.account_title || '—'}</td>
                                <td style={{ padding: 8, textAlign: 'right', fontWeight: 700, color: 'var(--success)' }}>{parseFloat(r.total_amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</td>
                              </tr>
                            ))}
                            {linkedPayments.map(p => (
                              <tr key={p.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                                <td style={{ padding: 8 }}><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 'var(--r-full)', background: 'var(--danger-bg)', color: 'var(--danger)', fontWeight: 700 }}>📤 Tediye</span></td>
                                <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 12 }}>{p.payment_no}</td>
                                <td style={{ padding: 8 }}>{new Date(p.date).toLocaleDateString('tr-TR')}</td>
                                <td style={{ padding: 8 }}>{p.account_title || '—'}</td>
                                <td style={{ padding: 8, textAlign: 'right', fontWeight: 700, color: 'var(--danger)' }}>{parseFloat(p.total_amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6, fontStyle: 'italic' }}>
                          ℹ Bu fatura silindiğinde bağlı tahsilat/tediye makbuzları da silinir.
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab===7 && isOrderMode && selected && (
                  <div style={{ padding: '8px 0' }}>
                    {transfers.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
                        Henüz aktarım yapılmamış. Aktarım yapıldıkça bu tabloda hangi belgeye ne kadar gittiği listelenir.
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                          <thead>
                            <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-secondary)' }}>
                              <th style={{ padding: '8px', textAlign: 'left' }}>Tarih</th>
                              <th style={{ padding: '8px', textAlign: 'left' }}>Hedef</th>
                              <th style={{ padding: '8px', textAlign: 'left' }}>Kalem</th>
                              <th style={{ padding: '8px', textAlign: 'right' }}>Miktar</th>
                              <th style={{ padding: '8px', textAlign: 'left' }}>Aktaran</th>
                              <th style={{ padding: '8px', textAlign: 'center', width: 60 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {transfers.map(t => (
                              <tr key={t.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                                <td style={{ padding: '8px', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                                  {t.transferred_at ? new Date(t.transferred_at).toLocaleString('tr-TR') : '-'}
                                </td>
                                <td style={{ padding: '8px' }}>
                                  <span style={{ fontSize: '0.7rem', padding: '2px 7px', borderRadius: 4, fontWeight: 600,
                                                background: t.target_type === 'waybill' ? 'rgba(16,185,129,0.15)' : 'rgba(124,58,237,0.15)',
                                                color: t.target_type === 'waybill' ? 'var(--success)' : '#7c3aed', marginRight: 6 }}>
                                    {t.target_type === 'waybill' ? 'İrsaliye' : 'Fatura'}
                                  </span>
                                  <span style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{t.target_document_number || '-'}</span>
                                </td>
                                <td style={{ padding: '8px', fontSize: '0.78rem' }}>
                                  <div style={{ color: 'var(--text-primary)' }}>#{t.line_no} · {t.stock_code}</div>
                                  <div style={{ color: 'var(--text-tertiary)', fontSize: '0.72rem' }}>{t.stock_name}</div>
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>
                                  {fmtQty(t.quantity)} {t.unit_code || ''}
                                </td>
                                <td style={{ padding: '8px', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                                  {t.transferred_by_name || '—'}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                  {onNavigate && (
                                    <button type="button" className="btn-ghost"
                                      style={{ padding: '3px 8px', fontSize: '0.72rem' }}
                                      title="Hedef belgeyi aç"
                                      onClick={() => onNavigate(
                                        t.target_type === 'waybill'
                                          ? (isOrderOutgoingMode ? 'waybills-purchase' : 'waybills-sales')
                                          : (isOrderOutgoingMode ? 'purchases' : 'sales'),
                                        t.target_id
                                      )}>
                                      Aç →
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
                {activeTab===8 && (
                  <div style={{ padding: '8px 0' }}>
                    <AttachmentManager
                      entityType={
                        isQuoteMode
                          ? (isQuoteIncomingMode ? 'purchase_quote' : 'sales_quote')
                          : isOrderMode
                            ? (isPurchaseMode ? 'purchase_order' : 'sales_order')
                            : isWaybillMode
                              ? (isPurchaseWaybillMode ? 'purchase_waybill' : 'sales_waybill')
                              : (isPurchaseMode ? 'purchase_invoice' : 'sales_invoice')
                      }
                      entityId={selected?.id || null}
                    />
                  </div>
                )}
                {activeTab===9 && isQuoteMode && selected && (
                  <QuoteActivities
                    quoteTable={isQuoteIncomingMode ? 'purchase_quotes' : 'sales_quotes'}
                    quoteId={selected.id}
                    quoteStatuses={quoteStatuses}
                    canEdit={canEdit}
                    users={lookups.personnel || []}
                  />
                )}
                {activeTab===10 && selected?.id && (() => {
                  // mode → custom_fields entity_type haritası
                  const cfEntityType = isQuoteMode
                    ? (isQuoteIncomingMode ? 'purchase_quote' : 'sales_quote')
                    : isOrderMode
                      ? (isOrderOutgoingMode ? 'purchase_order' : 'sales_order')
                      : isWaybillMode
                        ? (isPurchaseWaybillMode ? 'purchase_waybill' : 'sales_waybill')
                        : (isPurchaseMode ? 'purchase_invoice' : 'sales_invoice');
                  return (
                    <CustomFieldsSection
                      entityType={cfEntityType}
                      entityId={selected.id}
                      autoSave
                      disabled={!canEdit}
                    />
                  );
                })()}
                </RenderBoundary>
              </div>

              {/* TOPLAMLAR */}
              <div style={{flexShrink:0,borderTop:'2px solid var(--border-secondary)',background:'var(--bg-secondary)',padding:'10px 16px',display:'flex',justifyContent:'space-between',flexWrap:'wrap',gap:16,alignItems:'flex-end'}}>
                {/* Ortalama Karlılık — kalemlerin profit_pct alanlarının satış-ağırlıklı ortalaması.
                    Kar % kalem alanına manuel girilebilir veya Persan aktarımıyla otomatik dolar. */}
                {(() => {
                  const profitLines = lines.filter(l => {
                    const p = parseFloat(l.profit_pct);
                    return !isNaN(p) && (parseFloat(l.unit_price_net) || parseFloat(l.unit_price));
                  });
                  if (profitLines.length === 0) return <div />;
                  let weightedSum = 0, totalSale = 0;
                  profitLines.forEach(l => {
                    const pct = parseFloat(l.profit_pct) || 0;
                    const qty = parseFloat(l.quantity) || 0;
                    const sale = (parseFloat(l.unit_price_net) || parseFloat(l.unit_price) || 0) * qty;
                    weightedSum += pct * sale;
                    totalSale += sale;
                  });
                  const avgProfit = totalSale > 0 ? weightedSum / totalSale : 0;
                  const fmt2 = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
                  const color = avgProfit < 0 ? 'var(--danger)' : avgProfit < 10 ? 'var(--warning)' : 'var(--success)';
                  const cur = (header.currency_code || 'TRY').toUpperCase();
                  return (
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-tertiary)' }}>📊 Karlılık ({profitLines.length} kalem)</div>
                      <div>Toplam Satış: <strong>{fmt2(totalSale)}</strong> {cur}</div>
                      <div>Ortalama Kâr: <strong style={{ color, fontSize: '1rem' }}>%{fmt2(avgProfit)}</strong></div>
                    </div>
                  );
                })()}
                <div style={{display:'grid',gridTemplateColumns:'auto auto',gap:'2px 20px',fontSize:'0.85rem',alignItems:'end'}}>
                  <span style={{color:'var(--text-secondary)',textAlign:'right'}}>Ara Toplam:</span><span>{fmt(totals.subtotal)} {header.currency_code}</span>
                  {totals.discount_total>0&&<><span style={{color:'var(--danger)',textAlign:'right'}}>Iskonto:</span><span style={{color:'var(--danger)'}}>-{fmt(totals.discount_total)}</span></>}
                  <span style={{color:'var(--text-secondary)',textAlign:'right'}}>Net:</span><span>{fmt(totals.net_total)}</span>
                  {totals.otv_total>0&&<><span style={{color:'var(--text-secondary)',textAlign:'right'}}>OTV:</span><span>{fmt(totals.otv_total)}</span></>}
                  {/* KDV kırılımı — KDV toplamının hemen üstünde, küçük puntolu */}
                  {(Array.isArray(totals.vat_breakdown) ? totals.vat_breakdown : []).filter(v=>v.amount>0).map((v,i) => (
                    <React.Fragment key={i}>
                      <span style={{color:'var(--text-tertiary)',textAlign:'right',fontSize:'0.72rem'}}>%{v.rate} (Matrah {fmt(v.base)}):</span>
                      <span style={{color:'var(--text-tertiary)',fontSize:'0.72rem'}}>{fmt(v.amount)}</span>
                    </React.Fragment>
                  ))}
                  <span style={{color:'var(--text-secondary)',textAlign:'right'}}>KDV:</span><span>{fmt(totals.vat_total)}</span>
                  {totals.withholding_total>0&&<><span style={{color:'var(--warning)',textAlign:'right'}}>Tevkifat (-):</span><span style={{color:'var(--warning)'}}>{fmt(totals.withholding_total)}</span></>}
                  <span style={{fontWeight:700,fontSize:'1rem',textAlign:'right'}}>GENEL TOPLAM:</span>
                  <span style={{fontWeight:700,fontSize:'1rem',color:'var(--accent-primary)'}}>{fmt(totals.grand_total)} {header.currency_code}</span>
                  {isDiffCur&&<><span style={{color:'var(--text-secondary)',textAlign:'right',fontSize:'0.8rem'}}>Yerel ({localCurrency}):</span><span style={{fontSize:'0.8rem'}}>{fmt(totals.grand_total*(parseFloat(header.exchange_rate)||1))} {localCurrency}</span></>}
                </div>
              </div>
            </div>
        </Modal>

          {/* MOBİL DÜZENLEME SHEET — sadece mobilde, satıra dokunulduğunda */}
          {mobileEditIdx !== null && lines[mobileEditIdx] && (() => {
            const ln = lines[mobileEditIdx];
            const idx = mobileEditIdx;
            const stock = lookups.stocks.find(s => String(s.id) === String(ln.stock_id));
            const sheetInput = {width:'100%',padding:'10px 12px',background:'var(--bg-input)',border:'1px solid var(--border-input)',borderRadius:8,color:'var(--text-primary)',fontSize:16,boxSizing:'border-box',minHeight:42};
            const labelStyle = {fontSize:'0.78rem',color:'var(--text-tertiary)',display:'block',marginBottom:4,fontWeight:600};
            return (
              <div className="line-sheet-backdrop" onClick={() => setMobileEditIdx(null)}>
                <div className="line-sheet" onClick={e => e.stopPropagation()}>
                  <div className="line-sheet-header">
                    <button className="btn-ghost" onClick={() => setMobileEditIdx(null)}>← Kapat</button>
                    <h3 style={{margin:0,fontSize:'0.95rem',flex:1,textAlign:'center'}}>Kalem #{idx+1}</h3>
                    {canEdit && <button className="btn-danger-sm" onClick={() => { removeLine(idx); setMobileEditIdx(null); }}>Sil</button>}
                  </div>
                  <div className="line-sheet-body">
                    <div style={{marginBottom:12}}>
                      <label style={labelStyle}>Stok</label>
                      <select value={ln.stock_id} onChange={e => updateLine(idx,'stock_id',e.target.value)} disabled={!canEdit} style={sheetInput}>
                        <option value="">Stok seç...</option>
                        {lookups.stocks.map(s => {
                          const cs = colorSizeEnabled;
                          const extras = cs ? [s.color_name, s.size_name].filter(Boolean).join(' / ') : '';
                          return <option key={s.id} value={s.id}>{s.name}{extras ? ` — ${extras}` : ''}</option>;
                        })}
                      </select>
                      {(ln.stock_code || ln.barcode) && (
                        <div style={{fontSize:'0.72rem',color:'var(--text-tertiary)',marginTop:4,fontFamily:'monospace'}}>
                          {ln.stock_code} {ln.barcode && `· ${ln.barcode}`}
                        </div>
                      )}
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
                      <div>
                        <label style={labelStyle}>Miktar</label>
                        <NumberInput value={ln.quantity} onChange={v=>updateLine(idx,'quantity',v)} decimals={3} disabled={!canEdit} style={{...sheetInput,textAlign:'right'}}/>
                      </div>
                      <div>
                        <label style={labelStyle}>Birim</label>
                        <select value={ln.unit_type||'unit1'} onChange={e => updateLine(idx,'unit_type',e.target.value)} disabled={!canEdit} style={sheetInput}>
                          {(ln.units||[]).map(u => <option key={u.type} value={u.type}>{u.name} (x{u.ratio})</option>)}
                          {(!ln.units||ln.units.length===0) && <option value="unit1">Adet</option>}
                        </select>
                      </div>
                    </div>
                    {colorSizeEnabled && ln.stock_id && (
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
                        <div>
                          <label style={labelStyle}>Renk</label>
                          <input value={ln.color_name||'-'} disabled style={{...sheetInput,opacity:0.7}}/>
                        </div>
                        <div>
                          <label style={labelStyle}>Beden</label>
                          <input value={ln.size_name||'-'} disabled style={{...sheetInput,opacity:0.7}}/>
                        </div>
                      </div>
                    )}
                    <div style={{display:'grid',gridTemplateColumns: isDiffCur ? '1fr auto 1fr' : '1fr auto',gap:8,marginBottom:12,alignItems:'end'}}>
                      <div>
                        <label style={labelStyle}>Yerel Fiyat ({localCurrency})</label>
                        <NumberInput
                          value={isDiffCur ? (ln.unit_price_local||0) : ln.unit_price}
                          onChange={v => updateLine(idx, isDiffCur?'unit_price_local':'unit_price', v)}
                          decimals={4} disabled={!canEdit} style={{...sheetInput,textAlign:'right'}}/>
                      </div>
                      <button type="button" onClick={() => openPricePicker(idx)} disabled={!canEdit || !ln.stock_id}
                        title="Satış fiyat listesinden seç"
                        style={{padding:'10px 12px',background:'var(--bg-secondary)',border:'1px solid var(--border-input)',borderRadius:8,fontSize:14,cursor:(!canEdit||!ln.stock_id)?'not-allowed':'pointer',minHeight:42}}>₺ ▾</button>
                      {isDiffCur && (
                        <div>
                          <label style={labelStyle}>İşlem ({header.currency_code})</label>
                          <NumberInput value={ln.unit_price} onChange={v=>updateLine(idx,'unit_price',v)} decimals={4} disabled={!canEdit} style={{...sheetInput,textAlign:'right'}}/>
                        </div>
                      )}
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:12}}>
                      <div>
                        <label style={labelStyle}>KDV %</label>
                        <input type="number" step="0.01" value={ln.vat_rate||''} onChange={e => updateLine(idx,'vat_rate',e.target.value)} disabled={!canEdit} style={{...sheetInput,textAlign:'right'}}/>
                      </div>
                      <div>
                        <label style={labelStyle}>OTV %</label>
                        <input type="number" step="0.01" value={ln.otv_rate||''} onChange={e => updateLine(idx,'otv_rate',e.target.value)} disabled={!canEdit} style={{...sheetInput,textAlign:'right'}}/>
                      </div>
                      <div>
                        <label style={labelStyle}>Tvk %</label>
                        <input type="number" step="0.01" value={ln.withholding_rate||''} onChange={e => updateLine(idx,'withholding_rate',e.target.value)} disabled={!canEdit} style={{...sheetInput,textAlign:'right'}}/>
                      </div>
                    </div>
                    <div style={{marginBottom:12,padding:'8px 10px',background:'var(--bg-secondary)',borderRadius:8,display:'flex',alignItems:'center',gap:8}}>
                      <input type="checkbox" id={`vat-inc-${idx}`} checked={!!ln.is_vat_included} onChange={e => updateLine(idx,'is_vat_included',e.target.checked)} disabled={!canEdit} style={{width:20,height:20}}/>
                      <label htmlFor={`vat-inc-${idx}`} style={{fontSize:'0.88rem',cursor:'pointer'}}>KDV fiyata dahil</label>
                    </div>
                    <div style={{marginBottom:12}}>
                      <label style={labelStyle}>İndirimler (%)</label>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:6}}>
                        {[1,2,3,4,5].map(n => (
                          <input key={n} type="number" step="0.01" placeholder={`İsk${n}`} value={ln[`discount_rate${n}`]||''} onChange={e => updateLine(idx,`discount_rate${n}`,e.target.value)} disabled={!canEdit} style={{...sheetInput,textAlign:'center',padding:'8px 4px'}}/>
                        ))}
                      </div>
                    </div>
                    <div style={{marginBottom:12}}>
                      <label style={labelStyle}>Depo</label>
                      <select value={ln.warehouse_id||''} onChange={e => updateLine(idx,'warehouse_id',e.target.value)} disabled={!canEdit} style={sheetInput}>
                        {lookups.warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                      </select>
                    </div>
                    <div style={{marginBottom:12}}>
                      <label style={labelStyle}>Personel</label>
                      <select value={ln.personnel_id||''} onChange={e => updateLine(idx,'personnel_id',e.target.value)} disabled={!canEdit} style={sheetInput}>
                        <option value="">— yok —</option>
                        {(lookups.personnel||[]).map(p => (
                          <option key={p.id} value={p.id}>{p.full_name || (p.first_name + ' ' + (p.last_name||''))}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isOrderMode ? '150px 1fr' : isQuoteMode ? '1fr 100px' : '1fr', gap: 8, marginBottom: 12 }}>
                      {isOrderMode && (
                        <div>
                          <label style={labelStyle}>📅 Teslim Tarihi</label>
                          <input type="date" value={ln.delivery_date||''} onChange={e => { const v=e.target.value; setLines(prev => { const nl=[...prev]; nl[idx]={...nl[idx],delivery_date:v}; return nl; }); }} disabled={!canEdit} style={sheetInput}/>
                        </div>
                      )}
                      <div>
                        <label style={labelStyle}>Açıklama</label>
                        <input type="text" value={ln.description||''} onChange={e => { const v=e.target.value; setLines(prev => { const nl=[...prev]; nl[idx]={...nl[idx],description:v}; return nl; }); }} disabled={!canEdit} placeholder="Kalem açıklaması..." style={sheetInput}/>
                      </div>
                      {isQuoteMode && (
                        <div>
                          <label style={labelStyle}>Kâr %</label>
                          <input type="number" step="0.01" value={ln.profit_pct||''} onChange={e => { const v=e.target.value; setLines(prev => { const nl=[...prev]; nl[idx]={...nl[idx],profit_pct:v}; return nl; }); }} disabled={!canEdit} placeholder="—" style={{...sheetInput, color:'#7c3aed', fontWeight:600}}/>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="line-sheet-footer">
                    <div style={{display:'grid',gridTemplateColumns:'auto auto',gap:'2px 16px',fontSize:'0.85rem',marginBottom:10}}>
                      <span style={{color:(ln.line_discount>0?'var(--danger)':'var(--text-secondary)'),textAlign:'right'}}>İskonto:</span>
                      <span style={{color:(ln.line_discount>0?'var(--danger)':'var(--text-secondary)'),fontFamily:'monospace'}}>{ln.line_discount>0?'-':''}{fmt(ln.line_discount||0)}</span>
                      <span style={{color:'var(--text-secondary)',textAlign:'right'}}>KDV:</span><span style={{fontFamily:'monospace'}}>{fmt(ln.line_vat||0)}</span>
                      <span style={{fontWeight:700,textAlign:'right'}}>TUTAR:</span>
                      <span style={{fontWeight:700,color:'var(--accent-primary)',fontFamily:'monospace'}}>{fmt(ln.line_total||0)} {header.currency_code}</span>
                    </div>
                    <button className="btn-primary-sm" onClick={() => setMobileEditIdx(null)} style={{width:'100%',padding:'12px'}}>Tamam</button>
                  </div>
                </div>
              </div>
            );
          })()}
        </>
        );
      })()}

      {qlmConfig && (
        <QuickListManager
          open={true}
          title={qlmConfig.title}
          items={qlmConfig.items}
          apiBase={qlmConfig.apiBase}
          extraBody={qlmConfig.extraBody}
          onClose={()=>setShowQuick(null)}
          onChanged={fetchLookups}
        />
      )}

      {/* İskonto modal'ı — kalem satırında 5 kademeli iskontoyu tek modal'da düzenle */}
      {discountModalIdx !== null && lines[discountModalIdx] && (() => {
        const ln = lines[discountModalIdx];
        const rates = [1,2,3,4,5].map(n => parseFloat(ln[`discount_rate${n}`])||0);
        let combined = 1;
        rates.forEach(r => { if (r > 0) combined *= (1 - r/100); });
        const combinedPct = +((1 - combined) * 100).toFixed(2);
        const subtotal = (parseFloat(ln.quantity)||0) * (parseFloat(ln.unit_price)||0);
        const discountAmount = subtotal * (1 - combined);
        const stockLabel = ln.stock_name || ln.manual_stock_name || ln.stock_code || ('Satır ' + (discountModalIdx+1));
        const close = () => setDiscountModalIdx(null);
        const clearAll = () => { [1,2,3,4,5].forEach(n => updateLine(discountModalIdx, `discount_rate${n}`, 0)); };
        return (
          <Modal
            title="İskonto Düzenle"
            subtitle={<>{stockLabel} · 5 kademeli iskonto sırayla uygulanır</>}
            size="sm"
            onClose={close}
            closeOnOverlay={false}
            primaryAction={{ label: 'Tamam', onClick: close }}
            destructiveAction={canEdit && rates.some(r=>r>0) ? { label: 'Tümünü Temizle', onClick: clearAll } : null}
          >
            <div style={{display:'grid', gap:10}}>
              {[1,2,3,4,5].map(n => (
                <div key={n} className="form-row" style={{margin:0,display:'grid',gridTemplateColumns:'90px 1fr',alignItems:'center',gap:10}}>
                  <label style={{fontSize:'0.85rem',color:'var(--text-secondary)'}}>İskonto {n} %</label>
                  <input
                    type="number" step="0.01" min="0" max="100"
                    autoFocus={n===1}
                    value={ln[`discount_rate${n}`]||''}
                    onChange={e => updateLine(discountModalIdx, `discount_rate${n}`, e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); close(); } }}
                    disabled={!canEdit}
                    placeholder="0"
                    style={{padding:'8px 10px',background:'var(--bg-input)',border:'1px solid var(--border-input)',borderRadius:6,color:'var(--text-primary)',fontSize:'0.9rem',textAlign:'right',fontFamily:'monospace'}}
                  />
                </div>
              ))}
              <div style={{
                marginTop:6, padding:'10px 12px', background:'var(--bg-secondary)', borderRadius:8,
                display:'grid', gridTemplateColumns:'1fr auto', gap:'4px 12px', fontSize:'0.85rem',
              }}>
                <span style={{color:'var(--text-secondary)'}}>Birleşik iskonto:</span>
                <span style={{fontFamily:'monospace',fontWeight:700,color: combinedPct>0 ? 'var(--danger)' : 'var(--text-tertiary)'}}>%{combinedPct}</span>
                <span style={{color:'var(--text-secondary)'}}>İskonto tutarı:</span>
                <span style={{fontFamily:'monospace',fontWeight:600,color: discountAmount>0 ? 'var(--danger)' : 'var(--text-tertiary)'}}>
                  {discountAmount>0?'-':''}{fmt(discountAmount)} {header.currency_code}
                </span>
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* Sipariş Durumu yöneticisi (cari grup gibi) */}
      {showOrderStatusMgr && (
        <QuickListManager
          open={true}
          title="Sipariş Durumu"
          items={orderStatuses}
          apiBase="/order-statuses"
          onClose={()=>setShowOrderStatusMgr(false)}
          onChanged={fetchLookups}
        />
      )}

      {/* Teklif Durumu yöneticisi */}
      {showQuoteStatusMgr && (
        <QuickListManager
          open={true}
          title="Teklif Durumu"
          items={quoteStatuses}
          apiBase="/quote-statuses"
          onClose={()=>setShowQuoteStatusMgr(false)}
          onChanged={fetchLookups}
        />
      )}

      {/* Belge Ayarları modal'ı — stok/cari hareket atlama */}
      {showDocSettings && (
        <Modal
          title="⚙ Belge Ayarları"
          subtitle="Bu fatura kaydedilirken hangi hareketlerin yazılacağını belirler"
          size="md"
          onClose={() => setShowDocSettings(false)}
          primaryAction={{ label: 'Tamam', onClick: () => setShowDocSettings(false) }}
        >
          <div style={{display:'grid', gap:14}}>
            <label style={{
              display:'flex', alignItems:'flex-start', gap:12, padding:14,
              border:'1px solid var(--border-color)', borderRadius:8,
              cursor: canEdit ? 'pointer' : 'default',
              background: header.record_to_stock !== false ? 'rgba(16,185,129,0.05)' : 'transparent',
              borderColor: header.record_to_stock !== false ? 'rgba(16,185,129,0.3)' : 'var(--border-color)',
            }}>
              <input type="checkbox" checked={header.record_to_stock !== false}
                onChange={e => h('record_to_stock', e.target.checked)} disabled={!canEdit}
                style={{width:18, height:18, marginTop:2, cursor: canEdit ? 'pointer' : 'default'}}/>
              <div style={{flex:1}}>
                <div style={{fontWeight:700, fontSize:14, marginBottom:4}}>📦 Stok Hareketine Yaz</div>
                <div style={{fontSize:12, color:'var(--text-secondary)'}}>
                  İşaretli: Fatura kalemleri için <strong>stok_movements</strong> tablosuna kayıt yazılır, depo stoku güncellenir.<br/>
                  İşaretsiz: Stoğa hiç dokunulmaz.
                </div>
              </div>
            </label>

            <label style={{
              display:'flex', alignItems:'flex-start', gap:12, padding:14,
              border:'1px solid var(--border-color)', borderRadius:8,
              cursor: canEdit ? 'pointer' : 'default',
              background: header.record_to_account !== false ? 'rgba(16,185,129,0.05)' : 'transparent',
              borderColor: header.record_to_account !== false ? 'rgba(16,185,129,0.3)' : 'var(--border-color)',
            }}>
              <input type="checkbox" checked={header.record_to_account !== false}
                onChange={e => h('record_to_account', e.target.checked)} disabled={!canEdit}
                style={{width:18, height:18, marginTop:2, cursor: canEdit ? 'pointer' : 'default'}}/>
              <div style={{flex:1}}>
                <div style={{fontWeight:700, fontSize:14, marginBottom:4}}>👤 Cari Hareketine Yaz</div>
                <div style={{fontSize:12, color:'var(--text-secondary)'}}>
                  İşaretli: Cari hesap bakiyesi güncellenir, kredi limit kontrolü yapılır, <strong>prim/hakediş</strong> hesaplanır, <strong>muhasebe yevmiyesi</strong> kaydedilir.<br/>
                  İşaretsiz: Cari, prim ve muhasebe atlanır.
                </div>
              </div>
            </label>

            {(header.record_to_stock === false || header.record_to_account === false) && (
              <div style={{padding:12, background:'rgba(245,158,11,0.1)', borderLeft:'3px solid #f59e0b', borderRadius:4, fontSize:13, color:'#92400e'}}>
                ⚠ <strong>Sadece belge kaydı modu</strong> — Fatura sadece bir kayıt olarak saklanır.
                e-Belge gönderimi yine yapılabilir, fakat stok ya da cari hareketi yazılmaz.
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Kısmi aktarım modal'ı — kalem-bazlı miktar seçici */}
      {convertModalOpen && selected && (
        <Modal
          title={`Sipariş Aktarımı — ${selected.document_number || ''}`}
          size="lg"
          onClose={closeConvertModal}
          closeOnOverlay={false}
          primaryAction={{ label: convertTarget === 'waybill' ? 'İrsaliyeye Aktar' : 'Faturaya Aktar', onClick: submitConvert, loading: convertingOrder }}
          cancelAction={{ label: 'Vazgeç', onClick: closeConvertModal, disabled: convertingOrder }}
        >
          <div style={{ padding: 16 }}>
            <div style={{ marginBottom: 14, padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700, marginBottom: 6 }}>Hedef Belge</div>
              <div style={{ display: 'flex', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem' }}>
                  <input type="radio" name="ct" value="waybill" checked={convertTarget==='waybill'} onChange={()=>setConvertTarget('waybill')} />
                  📦 {isOrderOutgoingMode ? 'Alış İrsaliyesi' : 'Satış İrsaliyesi'}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem' }}>
                  <input type="radio" name="ct" value="invoice" checked={convertTarget==='invoice'} onChange={()=>setConvertTarget('invoice')} />
                  📄 {isOrderOutgoingMode ? 'Alış Faturası' : 'Satış Faturası'}
                </label>
              </div>
            </div>

            <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginBottom: 6, fontStyle: 'italic' }}>
              Aktarmak istediğin kalemlerden miktar gir. 0 yazarsan o kalem aktarılmaz. Miktar kalan miktarı aşamaz.
            </div>

            <div style={{ overflowX: 'auto', border: '1px solid var(--border-secondary)', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-secondary)' }}>
                    <th style={{ padding: '8px', textAlign: 'left', width: 50 }}>#</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Stok</th>
                    <th style={{ padding: '8px', textAlign: 'right', width: 90 }}>Toplam</th>
                    <th style={{ padding: '8px', textAlign: 'right', width: 90 }}>Aktarılan</th>
                    <th style={{ padding: '8px', textAlign: 'right', width: 90 }}>Kalan</th>
                    <th style={{ padding: '8px', textAlign: 'right', width: 130 }}>Aktarılacak</th>
                  </tr>
                </thead>
                <tbody>
                  {convertItems.map((it, idx) => (
                    <tr key={it.order_line_id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                      <td style={{ padding: '8px' }}>{it.line_no}</td>
                      <td style={{ padding: '8px', fontSize: '0.78rem' }}>{it.stock_label}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtQty(it.total)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace', color: 'var(--text-tertiary)' }}>{fmtQty(it.transferred)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtQty(it.remaining)} {it.unit_code}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                        <input type="number" min={0} max={it.remaining} step={qtyStep()}
                          value={it.qty}
                          onChange={e => {
                            const v = e.target.value;
                            setConvertItems(prev => {
                              const arr = [...prev];
                              const num = v === '' ? 0 : parseFloat(v);
                              arr[idx] = { ...arr[idx], qty: Math.min(it.remaining, Math.max(0, isNaN(num) ? 0 : num)) };
                              return arr;
                            });
                          }}
                          style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border-input)', borderRadius: 6, background: 'var(--bg-input)', color: 'var(--text-primary)', textAlign: 'right', fontFamily: 'monospace' }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button size="sm" variant="ghost" onClick={() => setConvertItems(prev => prev.map(it => ({ ...it, qty: it.remaining })))}>Tümünü Doldur</Button>
              <Button size="sm" variant="ghost" onClick={() => setConvertItems(prev => prev.map(it => ({ ...it, qty: 0 })))}>Tümünü Sıfırla</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* FULL-FORM EKLEME — mevcut modül sayfasını overlay olarak açar */}
      {quickAddOpen && (
        <ModuleOverlay
          type={quickAddOpen}
          onClose={() => setQuickAddOpen(null)}
          onSaved={async (rec) => {
            await fetchLookups();
            if (!rec?.id) return;
            if (quickAddOpen === 'cari') {
              // Fatura müşterisi olarak otomatik seç
              const cur = rec.currency_code || localCurrency;
              const er = cur === localCurrency ? 1 : (parseFloat(((lookups.exchangeRates || {})[cur])?.selling_rate) || 1);
              const days = parseInt(rec.payment_term_days) || 0;
              const baseDate = new Date(header.invoice_date || new Date());
              baseDate.setDate(baseDate.getDate() + days);
              const dueDate = baseDate.toISOString().substring(0,10);
              setHeader(prev => ({ ...prev, account_id: rec.id, currency_code: cur, exchange_rate: er, due_date: dueDate }));
            } else if (quickAddOpen === 'prospect') {
              // Potansiyel cari oluşturuldu — teklifin müşterisi olarak ata (prospect_account_id'ye)
              const cur = rec.currency_code || localCurrency;
              const er  = cur === localCurrency ? 1 : (parseFloat(((lookups.exchangeRates || {})[cur])?.selling_rate) || 1);
              setHeader(prev => ({
                ...prev,
                account_id: rec.id,         // SearchableSelect bu id'yi gösterir
                prospect_account_id: rec.id,
                is_prospect_account: true,
                currency_code: cur,
                exchange_rate: er,
              }));
            } else if (quickAddOpen === 'personel') {
              setHeader(prev => ({ ...prev, personnel_id: rec.id }));
            } else if (quickAddOpen === 'stok') {
              setMsg({ type: 'success', text: 'Stok eklendi — kalemlere artık seçebilirsiniz' });
              setTimeout(() => setMsg(null), 3000);
            }
          }}
        />
      )}

      {/* ONAY GEÇMİŞİ MODAL — durum rozetine tıklayınca kim/ne zaman onayladığını gösterir */}
      {approvalHistory && (
        <Modal open onClose={() => setApprovalHistory(null)} title="Onay Geçmişi" size="md"
          cancelAction={{ label: 'Kapat', onClick: () => setApprovalHistory(null) }}>
          {approvalHistory.loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}>Yükleniyor...</div>
          ) : approvalHistory.error ? (
            <div style={{ padding: 12, color: 'var(--danger)' }}>Hata: {approvalHistory.error}</div>
          ) : !approvalHistory.data ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}>
              Bu belge için onay kaydı yok (otomatik onaylanmış olabilir).
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ padding: 10, background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.84rem' }}>
                <div><strong>Hiyerarşi:</strong> {approvalHistory.data.hierarchy_name || '—'}</div>
                <div><strong>Mevcut Durum:</strong> <span style={{ fontWeight: 700, color: approvalHistory.data.status === 'approved' ? 'var(--success)' : approvalHistory.data.status === 'rejected' ? 'var(--danger)' : '#b45309' }}>
                  {approvalHistory.data.status === 'approved' ? '✓ Onaylandı' : approvalHistory.data.status === 'rejected' ? '✕ Reddedildi' : approvalHistory.data.status === 'sent_back' ? '↩ Geri Gönderildi' : '⏳ Bekliyor'}
                </span></div>
                {approvalHistory.data.finalized_at && (
                  <div><strong>Tamamlanma:</strong> {new Date(approvalHistory.data.finalized_at).toLocaleString('tr-TR')}</div>
                )}
              </div>
              {approvalHistory.data.actions?.length > 0 ? (
                <div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: 6 }}>İşlem Geçmişi</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                        <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 500 }}>Seviye</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 500 }}>Eylem</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 500 }}>Kullanıcı</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 500 }}>Tarih / Saat</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 500 }}>Not</th>
                      </tr>
                    </thead>
                    <tbody>{approvalHistory.data.actions.map(a => (
                      <tr key={a.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                        <td style={{ padding: '8px' }}>
                          {a.level_no}{a.level_name ? ' — ' + a.level_name : ''}
                        </td>
                        <td style={{ padding: '8px' }}>
                          {(() => {
                            const cfg =
                              a.action === 'approved'  ? { lbl: '✓ Onayladı',     bg: 'rgba(16,185,129,0.12)', clr: 'var(--success)' }
                            : a.action === 'rejected'  ? { lbl: '✕ Reddetti',     bg: 'rgba(239,68,68,0.12)',  clr: 'var(--danger)'  }
                            : a.action === 'revoked'   ? { lbl: '↺ Onayı Geri Aldı', bg: 'rgba(99,102,241,0.12)', clr: '#6366f1' }
                            : a.action === 'sent_back' ? { lbl: '↩ Geri Gönderdi', bg: 'rgba(245,158,11,0.12)', clr: '#b45309' }
                            : { lbl: a.action, bg: 'rgba(100,116,139,0.12)', clr: 'var(--text-secondary)' };
                            return <span style={{ fontSize: '0.74rem', padding: '2px 8px', borderRadius: 'var(--r-full)', fontWeight: 700, background: cfg.bg, color: cfg.clr }}>{cfg.lbl}</span>;
                          })()}
                        </td>
                        <td style={{ padding: '8px' }}>
                          {a.actor_name || a.actor_username || '—'}
                          {a.actor_username && a.actor_name && <span style={{ color: 'var(--text-tertiary)', fontSize: '0.78rem', marginLeft: 4 }}>@{a.actor_username}</span>}
                        </td>
                        <td style={{ padding: '8px', color: 'var(--text-secondary)', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          {new Date(a.created_at).toLocaleString('tr-TR')}
                        </td>
                        <td style={{ padding: '8px', color: 'var(--text-secondary)', fontStyle: a.note ? 'normal' : 'italic' }}>
                          {a.note || '—'}
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                  Henüz herhangi bir eylem (onay/red) yapılmadı.
                </div>
              )}
            </div>
          )}
        </Modal>
      )}

      {/* CARİ LİSTE SEÇİCİ MODAL — DataTable (sıralama, sütun ayarları, kolon filtresi, resize) */}
      {showAccountList && (() => {
        const q = (accountListSearch || '').toLowerCase().trim();
        const filtered = (lookups.accounts || []).filter(a => !q ||
          (a.code || '').toLowerCase().includes(q) ||
          (a.title || '').toLowerCase().includes(q) ||
          (a.tax_number || '').includes(q) ||
          (a.id_number || '').includes(q) ||
          (a.phone || '').includes(q) ||
          (a.mobile || '').includes(q));
        const cariCols = [
          { id: 'code',    header: 'Kod',     accessor: 'code', sortable: true, width: 110,
            cell: a => <span className="stock-code">{a.code}</span> },
          { id: 'title',   header: 'Ünvan',   accessor: 'title', sortable: true,
            cell: a => <span className="stock-name-cell">{a.title}</span> },
          { id: 'type',    header: 'Tip',     accessor: 'type', sortable: true, width: 100 },
          { id: 'tax',     header: 'VD / TC', sortable: true, width: 150,
            accessor: a => a.identity_type === 'individual' ? (a.id_number || '') : `${a.tax_office || ''} ${a.tax_number || ''}`,
            cell: a => <span className="stock-secondary" style={{fontSize:'0.78rem'}}>
              {a.identity_type === 'individual' ? (a.id_number || '—') : (a.tax_office || a.tax_number ? `${a.tax_office || ''}${a.tax_office && a.tax_number ? ' / ' : ''}${a.tax_number || ''}` : '—')}
            </span> },
          { id: 'phone',   header: 'Telefon', sortable: true, width: 130,
            accessor: a => a.phone || a.mobile || '',
            cell: a => <span className="stock-secondary">{a.phone || a.mobile || '—'}</span> },
          { id: 'city',    header: 'Şehir',   accessor: 'city', sortable: true, width: 110,
            cell: a => <span className="stock-secondary">{a.city || '—'}</span> },
          { id: 'balance', header: 'Bakiye',  accessor: a => parseFloat(a.balance) || 0, sortable: true, width: 160,
            headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
            cell: a => {
              const b = parseFloat(a.balance) || 0;
              return <span className="stock-price-cell" style={{color: b > 0 ? 'var(--danger)' : b < 0 ? 'var(--success)' : 'var(--text-tertiary)'}}>
                {fmtAmount(Math.abs(b))} {a.currency_code || ''}
              </span>;
            } },
        ];
        return (
          <div className="modal-overlay" onClick={() => setShowAccountList(false)}>
            <div className="modal-box" style={{ maxWidth: 980, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: 0 }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-secondary)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, flex: '0 0 auto' }}>Cari Listesi</h3>
                <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 12, background: 'var(--accent-glow)', color: 'var(--accent-primary)', fontWeight: 600 }}>{filtered.length} kayıt</span>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <input autoFocus type="text" placeholder="Kod, ünvan, VD/TC, telefon ile ara..."
                    value={accountListSearch} onChange={e => setAccountListSearch(e.target.value)}
                    style={{ width: '100%', padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>
                <button className="btn-primary-sm" onClick={() => { setShowAccountList(false); setQuickAddOpen('cari'); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Yeni Cari
                </button>
                <button onClick={() => setShowAccountList(false)} title="Kapat"
                  style={{ width: 32, height: 32, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border-secondary)', borderRadius: 8, background: 'var(--bg-card)', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div style={{ flex: 1, overflow: 'auto' }} className="stocks-table-wrap">
                <DataTable
                  pageKey="invoice-cari-picker"
                  tableClassName="stocks-table"
                  rowClassName="stocks-table-row"
                  emptyText={q ? 'Eşleşen cari bulunamadı' : 'Henüz cari yok'}
                  data={filtered}
                  rowKey="id"
                  onRowClick={a => { selectAccount(a.id); setShowAccountList(false); }}
                  columns={cariCols}
                />
              </div>
            </div>
          </div>
        );
      })()}

      {/* ÇOKLU STOK SEÇİM MODALI */}
      {showMultiStock && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 720, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Çoklu Stok Seç</h3>
              <button className="btn-modal-close" onClick={() => setShowMultiStock(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <input
                autoFocus
                placeholder="Kod, ad veya barkod ile ara..."
                value={multiStockSearch}
                onChange={e => setMultiStockSearch(e.target.value)}
                style={{ flex: 1, padding: '8px 12px', border: '1px solid var(--border-input)', borderRadius: 8, background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '0.88rem' }}
              />
              <button className="btn-ghost" style={{ whiteSpace: 'nowrap' }} onClick={() => toggleSelectAll(filteredMultiStocks)}>
                {filteredMultiStocks.length > 0 && filteredMultiStocks.every(s => selectedStockIds.has(s.id)) ? 'Tümünü Bırak' : 'Tümünü Seç'}
              </button>
            </div>

            <div style={{ flex: 1, overflow: 'auto', border: '1px solid var(--border-secondary)', borderRadius: 8 }}>
              <DataTable
                pageKey="multi-stock-picker"
                emptyText="Stok bulunamadı"
                data={filteredMultiStocks.slice(0, 300)}
                rowKey="id"
                onRowClick={s => toggleStockSelection(s.id)}
                rowClassName={s => selectedStockIds.has(s.id) ? 'multi-stock-selected' : ''}
                columns={[
                  { id: 'check', header: '✓', sortable: false, hideable: false, draggable: false, filterable: false, width: 40,
                    headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
                    cell: s => <input type="checkbox" checked={selectedStockIds.has(s.id)} onChange={() => toggleStockSelection(s.id)} onClick={e => e.stopPropagation()} style={{ cursor: 'pointer' }} /> },
                  { id: 'code', header: 'Kod', accessor: 'code', sortable: true, width: 130,
                    cellStyle: { fontFamily: 'monospace', fontSize: '0.8rem' } },
                  { id: 'name', header: 'Ad', accessor: 'name', sortable: true,
                    cellStyle: { fontWeight: 500 } },
                  ...(colorSizeEnabled ? [
                    { id: 'color', header: 'Renk', accessor: 'color_name', sortable: true, width: 100,
                      cell: s => <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{s.color_name || '-'}</span> },
                    { id: 'size', header: 'Beden', accessor: 'size_name', sortable: true, width: 90,
                      cell: s => <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{s.size_name || '-'}</span> },
                  ] : []),
                  { id: 'barcode', header: 'Barkod', accessor: 'barcode', sortable: true, width: 150,
                    cell: s => <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>{s.barcode || '—'}</span> },
                ]}
              />
              {filteredMultiStocks.length > 300 && (
                <div style={{ padding: 10, textAlign: 'center', color: 'var(--text-tertiary)', fontStyle: 'italic', fontSize: '0.82rem' }}>
                  İlk 300 sonuç gösteriliyor — aramayı daraltın
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Seçilen: <strong style={{ color: 'var(--accent-primary)' }}>{selectedStockIds.size}</strong> stok
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost" onClick={() => setShowMultiStock(false)}>İptal</button>
                <button className="btn-primary-sm" onClick={addSelectedStocks} disabled={selectedStockIds.size === 0}>
                  Ekle ({selectedStockIds.size})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSeriesPicker && (
        <div className="modal-overlay" onClick={() => setShowSeriesPicker(false)}>
          <div className="modal-box" style={{ maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Seri Listesi</h3>
              <button className="btn-modal-close" onClick={() => setShowSeriesPicker(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <input autoFocus placeholder="Ad, kod veya barkod ile ara..." value={seriesSearch} onChange={e => setSeriesSearch(e.target.value)}
              style={{ marginBottom: 10, padding: '8px 12px', border: '1px solid var(--border-input)', borderRadius: 8, background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '0.88rem' }} />
            <div style={{ flex: 1, overflow: 'auto', border: '1px solid var(--border-secondary)', borderRadius: 8 }}>
              {seriesPickerLoading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}>Yükleniyor...</div>}
              {!seriesPickerLoading && seriesList.length === 0 && (
                <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)' }}>Henüz seri tanımlı değil. Stok kartından seri paketleri tanımlayın.</div>
              )}
              {!seriesPickerLoading && seriesList.length > 0 && (() => {
                const q = seriesSearch.toLowerCase();
                const filtered = !q ? seriesList : seriesList.filter(s =>
                  (s.name||'').toLowerCase().includes(q) ||
                  (s.code||'').toLowerCase().includes(q) ||
                  (s.barcode||'').toLowerCase().includes(q) ||
                  (s.product_code||'').toLowerCase().includes(q)
                );
                return (
                  <DataTable
                    pageKey="series-picker"
                    emptyText="Eşleşen seri yok"
                    data={filtered}
                    rowKey="id"
                    onRowClick={s => pickSeries(s.id)}
                    columns={[
                      { id: 'product_code', header: 'Ürün Kodu', accessor: 'product_code', sortable: true, width: 130,
                        cell: s => <span style={{ fontFamily: 'monospace', color: 'var(--accent-primary)' }}>{s.product_code}</span> },
                      { id: 'name', header: 'Seri Adı', accessor: 'name', sortable: true,
                        cellStyle: { fontWeight: 500 } },
                      { id: 'barcode', header: 'Barkod', accessor: 'barcode', sortable: true, width: 150,
                        cell: s => <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>{s.barcode || '—'}</span> },
                    ]}
                  />
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* FİYAT SEÇİCİ MODAL — satır için satış fiyat listesi */}
      {pricePicker && (
        <div className="modal-overlay" onClick={() => setPricePicker(null)}>
          <div className="modal-box" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Fiyat Listesi — {pricePicker.stockName}</h3>
              <button className="btn-modal-close" onClick={() => setPricePicker(null)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            {pricePickerLoading ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}>Yükleniyor...</div>
            ) : pricePicker.items.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
                Bu stok için tanımlı satış fiyatı yok.
                <br/><small>Stok kartı → Fiyat & Vergi sekmesinden ekleyin.</small>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', border: '1px solid var(--border-secondary)', borderRadius: 8 }}>
                <table style={{ width: '100%', minWidth: 620, borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead style={{ background: 'var(--bg-secondary)' }}>
                    <tr>
                      <th style={{ padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap' }}>Açıklama</th>
                      <th style={{ padding: '8px 8px', textAlign: 'right', width: 110, whiteSpace: 'nowrap' }}>Fiyat-1 ★</th>
                      <th style={{ padding: '8px 8px', textAlign: 'right', width: 110, whiteSpace: 'nowrap' }}>Fiyat-2</th>
                      <th style={{ padding: '8px 8px', textAlign: 'right', width: 110, whiteSpace: 'nowrap' }}>Fiyat-3</th>
                      <th style={{ padding: '8px 8px', textAlign: 'center', width: 60, whiteSpace: 'nowrap' }}>Para</th>
                      <th style={{ padding: '8px 8px', textAlign: 'center', width: 50, whiteSpace: 'nowrap' }}>KDV</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricePicker.items.map(it => {
                      const cur = it.item_currency_code || it.def_currency_code || 'TRY';
                      const cell = (val, field) => {
                        const num = parseFloat(val) || 0;
                        if (num <= 0) return <span style={{color:'var(--text-tertiary)'}}>—</span>;
                        return (
                          <button type="button" onClick={() => pickPrice(it, field)}
                            style={{padding:'4px 8px', background:'rgba(99,102,241,0.08)', border:'1px solid rgba(99,102,241,0.3)', borderRadius:4, cursor:'pointer', fontFamily:'monospace', fontWeight:600, color:'var(--accent-primary)'}}>
                            {num.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </button>
                        );
                      };
                      return (
                        <tr key={it.price_definition_id} style={{borderTop:'1px solid var(--border-secondary)'}}>
                          <td style={{padding:'7px 10px'}}>
                            <div style={{fontWeight:600}}>{it.name} {it.def_is_preferred && <span style={{color:'#f59e0b'}}>★</span>}</div>
                            <div style={{fontSize:'0.7rem', color:'var(--text-tertiary)'}}>{it.price_group}</div>
                          </td>
                          <td style={{padding:'5px 8px', textAlign:'right'}}>{cell(it.price1, 'price1')}</td>
                          <td style={{padding:'5px 8px', textAlign:'right'}}>{cell(it.price2, 'price2')}</td>
                          <td style={{padding:'5px 8px', textAlign:'right'}}>{cell(it.price3, 'price3')}</td>
                          <td style={{padding:'5px 8px', textAlign:'center', fontSize:'0.75rem', color:'var(--text-tertiary)'}}>{cur}</td>
                          <td style={{padding:'5px 8px', textAlign:'center', fontSize:'0.75rem'}}>{it.vat_included ? 'Dahil' : 'Hariç'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ marginTop: 12, fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
              Bir fiyatı tıklayarak satıra aktarabilirsiniz. Fiyat-1: ana satış, Fiyat-2: üstü çizili (indirim referansı), Fiyat-3: vadeli/kart fiyatı.
            </div>
          </div>
        </div>
      )}

      <BarcodeScanner
        open={barcodeScannerOpen}
        onDetected={(code) => { setBarcodeScannerOpen(false); setBarcodeInput(code); handleBarcodeScan(code); }}
        onClose={() => setBarcodeScannerOpen(false)}
      />

      {/* MANUEL İRSALİYE AKTARIM MODALI — master/detail */}
      {showWaybillImport && (() => {
        const toggleId = (id) => setImportPickedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
        const allChecked = availableWaybills.length > 0 && importPickedIds.length === availableWaybills.length;
        const pickedWaybills = availableWaybills.filter(w => importPickedIds.includes(w.id));
        const allLines = pickedWaybills.flatMap(w => (w.lines || []).map(l => ({ ...l, _wbDoc: w.document_number })));
        const totalQty = allLines.reduce((s, l) => s + (parseFloat(l.quantity) || 0), 0);
        const totalAmount = allLines.reduce((s, l) => s + (parseFloat(l.line_total) || 0), 0);
        return (
        <Modal
          title="İrsaliyeden Aktar"
          subtitle={<span>{availableWaybills.length} faturalanmamış irsaliye{importPickedIds.length > 0 ? ` · ${importPickedIds.length} seçili · ${allLines.length} kalem` : ''}</span>}
          size="xl"
          onClose={() => setShowWaybillImport(false)}
          cancelAction={{ label: 'İptal', onClick: () => setShowWaybillImport(false) }}
          primaryAction={{
            label: `Aktar (${importPickedIds.length})`,
            onClick: importPickedWaybillsLines,
            disabled: importPickedIds.length === 0,
          }}
        >
          {availableWaybills.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)' }}>
              Bu cariye ait faturalanmamış onaylı irsaliye yok.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* ÜST: İrsaliye listesi */}
              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>İrsaliyeler</div>
                <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border-secondary)', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-secondary)', position: 'sticky', top: 0, zIndex: 1 }}>
                        <th style={{ padding: '8px', width: 36, textAlign: 'center' }}>
                          <input type="checkbox" checked={allChecked}
                            onChange={e => setImportPickedIds(e.target.checked ? availableWaybills.map(w => w.id) : [])}
                          />
                        </th>
                        <th style={{ padding: '8px', textAlign: 'left' }}>Tarih</th>
                        <th style={{ padding: '8px', textAlign: 'left' }}>Belge No</th>
                        <th style={{ padding: '8px', textAlign: 'right' }}>Tutar</th>
                        <th style={{ padding: '8px', textAlign: 'center' }}>Kalem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {availableWaybills.map(w => {
                        const checked = importPickedIds.includes(w.id);
                        return (
                          <tr key={w.id}
                              onClick={() => toggleId(w.id)}
                              style={{ borderTop: '1px solid var(--border-secondary)', cursor: 'pointer', background: checked ? 'rgba(124,58,237,0.08)' : 'transparent' }}>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <input type="checkbox" checked={checked} readOnly tabIndex={-1} style={{ pointerEvents: 'none' }} />
                            </td>
                            <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{new Date(w.waybill_date).toLocaleDateString('tr-TR')}</td>
                            <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{w.document_number}</td>
                            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{fmtAmount(w.grand_total || 0)} {w.currency_code}</td>
                            <td style={{ padding: '8px', textAlign: 'center', color: 'var(--text-tertiary)' }}>{(w.lines || []).length}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ALT: Seçili irsaliyelerin satırları (master) */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Seçili Kalemler {allLines.length > 0 && <span style={{ color: 'var(--accent-primary)' }}>({allLines.length})</span>}
                  </div>
                  {allLines.length > 0 && (
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                      Toplam Miktar: <strong>{fmtAmount(totalQty)}</strong> · Toplam Tutar: <strong>{fmtAmount(totalAmount)}</strong>
                    </div>
                  )}
                </div>
                <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--border-secondary)', borderRadius: 8 }}>
                  {allLines.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
                      İrsaliye seçince satırları burada görünecek
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ background: 'var(--bg-secondary)', position: 'sticky', top: 0, zIndex: 1 }}>
                          <th style={{ padding: '6px 8px', textAlign: 'left' }}>İrsaliye</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left' }}>Stok Kodu</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left' }}>Stok Adı</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Miktar</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Birim Fiyat</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Tutar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allLines.map((l, i) => (
                          <tr key={i} style={{ borderTop: '1px solid var(--border-secondary)' }}>
                            <td style={{ padding: '6px 8px', fontSize: '0.74rem', fontFamily: 'monospace', color: 'var(--text-tertiary)' }}>{l._wbDoc}</td>
                            <td style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--accent-primary)' }}>{l.stock_code || '-'}</td>
                            <td style={{ padding: '6px 8px' }}>{l.stock_name || '-'}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtAmount(l.quantity || 0)}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtAmount(l.unit_price || 0)}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{fmtAmount(l.line_total || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {importPickedIds.length > 0 && (
                <div style={{ padding: '10px 14px', background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 8, fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                  ℹ <strong>Aktar</strong>'a basınca yukarıdaki kalemler fatura editörüne eklenecek. <strong>Kaydet</strong>'e basınca cari hareketi oluşur, irsaliyeler "Faturalandı" olur. Stok çift sayılmaz.
                </div>
              )}
            </div>
          )}
        </Modal>
        );
      })()}

      {/* Yazdırma şablon seçici (Verilen/Alınan Teklif + Siparişler) */}
      {showPrintPicker && (
        <PrintTemplatePicker
          context={showPrintPicker}
          onClose={() => setShowPrintPicker(null)}
        />
      )}

      {/* Verilen Teklif → Alınan Sipariş aktarım modalı */}
      {showConvertToOrder && (
        <ConvertQuoteToOrderModal
          quoteId={selected?.id}
          quoteDocNo={selected?.document_number}
          accountId={header.account_id}
          prospectAccountId={header.is_prospect_account ? header.account_id : (header.prospect_account_id || null)}
          isProspect={!!header.is_prospect_account}
          accountTitle={(lookups.accounts || []).find(a => String(a.id) === String(header.account_id))?.title || header.account_title || ''}
          lines={lines}
          header={header}
          totals={totals}
          stocks={lookups.stocks || []}
          loading={convertingToOrder}
          onClose={() => setShowConvertToOrder(false)}
          onConverted={(orderDocNo) => {
            setShowConvertToOrder(false);
            setMsg({ type: 'success', text: `✓ Sipariş oluşturuldu: ${orderDocNo}` });
            fetchInvoices();
          }}
          setLoading={setConvertingToOrder}
        />
      )}

      {/* Hızlı Görüşme Ekle — liste'den, modal açmadan */}
      {activityForQuote && (
        <Modal
          title={`💬 Görüşme / Takip — ${activityForQuote.doc_no}${activityForQuote.account_title ? ' · ' + activityForQuote.account_title : ''}`}
          size="md"
          onClose={() => setActivityForQuote(null)}
          closeOnOverlay={true}
        >
          <QuoteActivities
            quoteTable={isQuoteIncomingMode ? 'purchase_quotes' : 'sales_quotes'}
            quoteId={activityForQuote.id}
            quoteStatuses={quoteStatuses}
            canEdit={true}
            users={lookups.users || lookups.personnel || []}
          />
        </Modal>
      )}
    </div>
    </>
    </RenderBoundary>
  );
}

// ─── Yazdırma şablon seçici ───────────────────────────────────────
function PrintTemplatePicker({ context, onClose }) {
  const { docLabel, lines, header, totals, account, personnel, selected: docSelected, docType } = context;
  const docNumber = docSelected?.document_number || header.document_number || header.invoice_number || '';
  const personnelName = personnel?.full_name || personnel?.name || docSelected?.personnel_name || '';
  // Sunucudan şablonları çek
  const [serverTpls, setServerTpls] = useState([]);
  const [selected, setSelected] = useState(null);
  const [designing, setDesigning] = useState(null);  // null | template object (new/edit)
  const [visualDesigner, setVisualDesigner] = useState(null);  // null | { tplId, mode } — banded visual designer
  const reloadTemplates = useCallback(() => {
    if (!docType) return;
    api.get('/print-templates', { params: { doc_type: docType } })
      .then(r => {
        const list = r.data?.success ? (r.data.data || []) : [];
        setServerTpls(list);
        if (list.length > 0 && (selected == null || !list.find(t => t.id === selected))) {
          setSelected(list.find(t => t.is_default)?.id || list[0].id);
        }
      }).catch(() => {});
  }, [docType, selected]);
  useEffect(() => { reloadTemplates(); /* eslint-disable-next-line */ }, [docType]);

  const allTemplates = serverTpls.length > 0
    ? serverTpls.map(t => ({ key: t.id, label: t.name + (t.is_default ? ' (varsayılan)' : ''), tpl: t }))
    : [
        { key: 'standard',  label: 'Standart A4',         tpl: { paper_size: 'A4', orientation: 'portrait', font_size: 10, margin_mm: 15, show_logo: 1, show_account: 1, show_notes: 1, primary_color: '#1d6ef5' } },
        { key: 'compact',   label: 'Kompakt (yarım)',     tpl: { paper_size: 'A4', orientation: 'portrait', font_size: 8.5, margin_mm: 10, show_logo: 1, show_account: 1, show_notes: 0, primary_color: '#1d6ef5' } },
      ];
  if (selected == null && allTemplates.length > 0 && serverTpls.length === 0) {
    setSelected('standard');
  }
  const activeTpl = allTemplates.find(t => t.key === selected)?.tpl || allTemplates[0]?.tpl;
  const isUserTpl = serverTpls.find(t => t.id === selected);

  // Yeni şablon: önce isim sor, profesyonel default layout ile DB'de kayıt yarat ve visual designer'ı aç
  // Yeni şablon yaratma modal'ı state
  const [newTplDialog, setNewTplDialog] = useState(false);
  const openNew = () => setNewTplDialog(true);

  // Modal'dan gelen tercihe göre şablonu yarat
  // source: 'blank' = sıfırdan boş, 'default' = standart şablon, 'copy:<id>' = mevcut şablondan kopyala
  const createTemplate = async ({ name, source }) => {
    if (!name?.trim()) { alert('Şablon adı gerekli'); return; }
    try {
      let layout = null;
      let paper_size = 'A4', orientation = 'portrait', font_size = 10, margin_mm = 12, primary_color = '#1d6ef5';
      if (source === 'default') {
        layout = getDefaultLayout(docType);
      } else if (source && source.startsWith('copy:')) {
        const srcId = source.slice(5);
        const src = serverTpls.find(t => t.id === srcId);
        if (src) {
          let srcLayout = src.layout_json;
          if (typeof srcLayout === 'string') { try { srcLayout = JSON.parse(srcLayout); } catch { srcLayout = null; } }
          layout = srcLayout || getDefaultLayout(docType);
          paper_size = src.paper_size || 'A4';
          orientation = src.orientation || 'portrait';
          font_size = parseFloat(src.font_size) || 10;
          margin_mm = parseFloat(src.margin_mm) || 12;
          primary_color = src.primary_color || '#1d6ef5';
        }
      }
      // 'blank' veya kaynak bulunamadıysa: layout null kalır, kullanıcı designer'da sıfırdan başlar
      const body = {
        doc_type: docType, name: name.trim(),
        paper_size: layout?.paper_size || paper_size,
        orientation: layout?.page_orientation || orientation,
        font_size: layout?.font_size || font_size,
        margin_mm: layout?.margin_left || margin_mm,
        show_logo: true, show_account: true, show_notes: true,
        primary_color: layout?.accent_color || primary_color,
        layout_json: layout,
      };
      const r = await api.post('/print-templates', body);
      if (r.data?.success) {
        const newId = r.data.data?.id;
        setNewTplDialog(false);
        setVisualDesigner({ tplId: newId });
        reloadTemplates();
      }
    } catch (e) { alert(e.response?.data?.message || 'Şablon oluşturulamadı'); }
  };

  // Tasarım: seçili kullanıcı şablonunu visual designer'da aç
  const openDesign = () => {
    if (!isUserTpl) { alert('Yerleşik şablonlar düzenlenemez. "+ Yeni Şablon" ile yeni bir tane oluştur.'); return; }
    setVisualDesigner({ tplId: isUserTpl.id });
  };
  const deleteTpl = async () => {
    if (!isUserTpl) { alert('Yerleşik şablon silinemez.'); return; }
    if (!window.confirm(`"${isUserTpl.name}" şablonu silinsin mi?`)) return;
    try {
      await api.delete('/print-templates/' + isUserTpl.id);
      setSelected(null);
      reloadTemplates();
    } catch (e) { alert(e.response?.data?.message || 'Silme başarısız'); }
  };

  // HTML üretimi — Yazdır, PDF, Mail tarafından paylaşılır
  const buildHtml = ({ autoPrint = false } = {}) => {
    const fmt2 = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(n) || 0);
    const t = activeTpl || {};
    const printScript = autoPrint ? '<script>window.onload=function(){window.focus();window.print();}<\/script>' : '';

    // BANDED LAYOUT — şablonda layout_json varsa designer'da gördüğünle birebir aynı çıktı
    let layoutJson = t.layout_json;
    if (typeof layoutJson === 'string') { try { layoutJson = JSON.parse(layoutJson); } catch { layoutJson = null; } }
    if (layoutJson && Array.isArray(layoutJson.elements) && layoutJson.elements.length > 0) {
      // doc_header alanları
      const mainRow = {
        id: docSelected?.id || 'doc',
        document_number: docNumber,
        invoice_date: header.invoice_date || '',
        invoice_time: header.invoice_time || '',
        due_date: header.due_date || '',
        valid_until: header.valid_until || '',
        account_code: account?.code || '',
        account_title: account?.title || header.account_title || header.bill_to_name || '',
        account_tax_office: account?.tax_office || '',
        account_tax_number: account?.tax_number || header.bill_to_tax_number || '',
        account_address: account?.address || header.bill_to_address || '',
        account_city: account?.city || header.bill_to_city || '',
        account_district: account?.district || header.bill_to_district || '',
        account_country: account?.country || '',
        account_phone: account?.phone || '',
        account_mobile: account?.mobile || '',
        account_email: account?.email || '',
        personnel_name: personnelName,
        currency_code: header.currency_code || '',
        exchange_rate: parseFloat(header.exchange_rate) || 1,
        subtotal: parseFloat(totals?.subtotal) || 0,
        discount_total: parseFloat(totals?.discount_total) || 0,
        net_total: parseFloat(totals?.net_total) || 0,
        vat_total: parseFloat(totals?.vat_total) || 0,
        grand_total: parseFloat(totals?.grand_total) || 0,
        avg_profit_pct: parseFloat(docSelected?.avg_profit_pct) || null,
        description: header.description || '',
        notes: header.notes || '',
        internal_notes: header.internal_note || header.internal_notes || '',
      };
      // doc_line alanları
      const cleanDescForBanded = (s) => (s || '').replace(/\s*·?\s*Karlılık:\s*%[-0-9.,]+\s*/gi, '').trim();
      const detailRows = (lines || []).map((l, i) => ({
        master_id: mainRow.id,
        line_no: i + 1,
        stock_code: l.stock_code || '',
        stock_name: l.stock_name || l.manual_stock_name || '',
        manual_stock_name: l.manual_stock_name || '',
        description: cleanDescForBanded(l.description),
        barcode: l.barcode || '',
        color_name: l.color_name || '',
        size_name: l.size_name || '',
        quantity: parseFloat(l.quantity) || 0,
        unit_code: l.unit_code || l.unit_name || '',
        unit_price: parseFloat(l.unit_price) || 0,
        unit_price_net: parseFloat(l.unit_price_net) || 0,
        discount_rate1: parseFloat(l.discount_rate1) || 0,
        discount_rate2: parseFloat(l.discount_rate2) || 0,
        discount_rate3: parseFloat(l.discount_rate3) || 0,
        discount_amount: parseFloat(l.discount_amount) || 0,
        vat_rate: parseFloat(l.vat_rate) || 0,
        vat_amount: parseFloat(l.vat_amount) || 0,
        line_net: parseFloat(l.line_net) || 0,
        line_total: parseFloat(l.line_total) || 0,
        personnel_name: l.personnel_name || '',
      }));
      const html = renderPrintHtml({
        layout: layoutJson,
        name: t.name || docLabel,
        mainRows: [mainRow],
        detailRowsByMaster: { [mainRow.id]: detailRows },
        context: {
          title: docLabel,
          subtitle: docNumber,
          company_name: '',
          filter_summary: '',
          footer_text: '',
          date_today: new Date().toISOString().slice(0, 10),
          row_count: detailRows.length,
          page_number: 1,
          total_pages: 1,
        },
      });
      return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${docLabel} — ${docNumber}</title></head><body>${html}${printScript}</body></html>`;
    }
    const margin = parseFloat(t.margin_mm) || 15;
    const fontSize = parseFloat(t.font_size) || 10;
    const color = t.primary_color || '#1d6ef5';
    const showAccount = t.show_account !== 0 && t.show_account !== false;
    const showNotes = t.show_notes !== 0 && t.show_notes !== false;
    const showSignature = !!t.show_signature;
    const css = `
      @page { size: ${t.paper_size || 'A4'} ${t.orientation || 'portrait'}; margin: ${margin}mm; }
      body { font-family: 'Segoe UI', Arial, sans-serif; padding: 0; color: #1a2332; font-size: ${fontSize}pt; }
      h1 { font-size: ${Math.max(fontSize + 6, 14)}pt; margin: 0 0 12px; }
      table { width: 100%; border-collapse: collapse; margin: 14px 0; font-size: ${fontSize}pt; }
      th, td { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; }
      th { background: #f4f6fa; text-align: left; font-weight: 700; }
      .right { text-align: right; }
      .totals { margin-top: 16px; display: flex; justify-content: flex-end; }
      .totals table { width: auto; min-width: 280px; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid ${color}; padding-bottom: 10px; margin-bottom: 10px; }
      .info { font-size: ${fontSize}pt; }
      .info b { color: #5a6a7e; font-weight: 600; margin-right: 6px; }
      .grand { font-weight: 800; font-size: ${Math.max(fontSize + 3, 12)}pt; color: ${color}; }
      .tpl-header { margin-bottom: 8px; font-size: ${fontSize}pt; }
      .tpl-footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #e2e8f0; font-size: ${Math.max(fontSize - 1, 8)}pt; color: #5a6a7e; }
      .signature { margin-top: 30px; display: flex; justify-content: space-between; font-size: ${fontSize}pt; }
      .signature .box { width: 200px; border-top: 1px solid #5a6a7e; padding-top: 4px; text-align: center; }
      ${t.extra_css || ''}
    `;
    // Açıklamadan karlılık satırını gizle (Persan kaynaklı kalemler için)
    const cleanDesc = (s) => (s || '')
      .replace(/\s*·?\s*Karlılık:\s*%[-0-9.,]+\s*/gi, '')
      .replace(/\s+·\s*$/g, '')
      .trim();
    const rowsHtml = (lines || []).map((l, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${(l.stock_name || l.manual_stock_name || '').replace(/</g, '&lt;')}</td>
        <td>${cleanDesc(l.description).replace(/</g, '&lt;')}</td>
        <td class="right">${fmt2(l.quantity)}</td>
        <td class="right">${fmt2(l.unit_price)}</td>
        <td class="right">%${fmt2(l.vat_rate)}</td>
        <td class="right">${fmt2(l.line_total)}</td>
      </tr>
    `).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${docLabel} — ${docNumber}</title><style>${css}</style></head>
      <body>
        ${t.header_html ? `<div class="tpl-header">${t.header_html}</div>` : ''}
        <div class="header">
          <div>
            <h1>${docLabel}</h1>
            <div class="info">
              <div><b>Belge No:</b> ${docNumber || '-'}</div>
              <div><b>Tarih:</b> ${header.invoice_date || ''}</div>
              ${header.valid_until ? `<div><b>Geçerlilik:</b> ${header.valid_until}</div>` : ''}
              ${personnelName ? `<div><b>Hazırlayan:</b> ${personnelName.toString().replace(/</g, '&lt;')}</div>` : ''}
            </div>
          </div>
          ${showAccount ? `
          <div class="info" style="text-align:right">
            <div><b>Cari:</b> ${((account?.title || header.account_title || header.bill_to_name) || '-').toString().replace(/</g, '&lt;')}</div>
            ${(account?.tax_number || header.bill_to_tax_number) ? `<div><b>VKN/TCKN:</b> ${(account?.tax_number || header.bill_to_tax_number).toString().replace(/</g, '&lt;')}</div>` : ''}
            ${(account?.tax_office) ? `<div><b>Vergi Dairesi:</b> ${account.tax_office.toString().replace(/</g, '&lt;')}</div>` : ''}
            ${(account?.address || header.bill_to_address) ? `<div>${(account?.address || header.bill_to_address).toString().replace(/</g, '&lt;')}</div>` : ''}
            ${(account?.district || account?.city || header.bill_to_city || header.bill_to_district) ? `<div>${[account?.district || header.bill_to_district, account?.city || header.bill_to_city].filter(Boolean).join(' / ').replace(/</g, '&lt;')}</div>` : ''}
            ${(account?.phone) ? `<div><b>Telefon:</b> ${account.phone.toString().replace(/</g, '&lt;')}</div>` : ''}
            ${(account?.mobile) ? `<div><b>Cep:</b> ${account.mobile.toString().replace(/</g, '&lt;')}</div>` : ''}
            ${(account?.email) ? `<div><b>E-posta:</b> ${account.email.toString().replace(/</g, '&lt;')}</div>` : ''}
          </div>` : ''}
        </div>
        <table>
          <thead><tr>
            <th>#</th><th>Ürün/Hizmet</th><th>Açıklama</th>
            <th class="right">Miktar</th><th class="right">Birim Fiyat</th><th class="right">KDV</th><th class="right">Toplam</th>
          </tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
        <div class="totals">
          <table>
            <tr><td>Ara Toplam:</td><td class="right">${fmt2(totals?.subtotal || 0)} ${header.currency_code || ''}</td></tr>
            ${(totals?.discount_total || 0) > 0 ? `<tr><td>İskonto:</td><td class="right">-${fmt2(totals.discount_total)}</td></tr>` : ''}
            <tr><td>Net:</td><td class="right">${fmt2(totals?.net_total || 0)}</td></tr>
            <tr><td>KDV:</td><td class="right">${fmt2(totals?.vat_total || 0)}</td></tr>
            <tr class="grand"><td>GENEL TOPLAM:</td><td class="right">${fmt2(totals?.grand_total || 0)} ${header.currency_code || ''}</td></tr>
          </table>
        </div>
        ${(showNotes && header.notes) ? `<div style="margin-top:18px;padding:10px 12px;background:#f4f6fa;border-left:3px solid ${color};font-size:${Math.max(fontSize - 1, 8)}pt"><b>Notlar:</b><br>${header.notes.replace(/</g, '&lt;').replace(/\n/g, '<br>')}</div>` : ''}
        ${showSignature ? `<div class="signature"><div class="box">Hazırlayan</div><div class="box">Müşteri İmza</div></div>` : ''}
        ${t.footer_html ? `<div class="tpl-footer">${t.footer_html}</div>` : ''}
        ${printScript}
      </body></html>`;
    return html;
  };

  const doPrint = () => {
    const w = window.open('', 'PRINT', 'height=800,width=1000');
    if (!w) { alert('Pop-up engellenmiş — yazdırma için izin verin'); return; }
    w.document.open();
    w.document.write(buildHtml({ autoPrint: true }));
    w.document.close();
  };

  const downloadPdf = async () => {
    try {
      const t = activeTpl || {};
      const filename = `${docLabel.replace(/\s+/g, '_')}_${docNumber || 'belge'}.pdf`;
      const paperSize = String(t.paper_size || 'a4').toLowerCase();
      const orientation = String(t.orientation || 'portrait').toLowerCase();
      const SIZES = { a4: { w: 210, h: 297 }, a5: { w: 148, h: 210 }, letter: { w: 216, h: 279 } };
      const dims = SIZES[paperSize] || SIZES.a4;
      const pageW = orientation === 'landscape' ? dims.h : dims.w;
      const pageH = orientation === 'landscape' ? dims.w : dims.h;
      const pxPerMm = 3.7795275591;
      const pageWpx = Math.round(pageW * pxPerMm);
      const pageHpx = Math.round(pageH * pxPerMm);

      // 1) Aynı dokümandaki gizli iframe içinde içeriği render et
      const iframe = document.createElement('iframe');
      iframe.style.cssText = `position:fixed;left:-99999px;top:0;width:${pageWpx}px;height:${pageHpx}px;border:0;`;
      document.body.appendChild(iframe);
      const doc = iframe.contentDocument;
      // Şablon'un kenar boşluklarını çıkar (mm) — banded layout'tan, yoksa template seviyesinden, son çare 15mm
      let layoutForMargins = null;
      try { layoutForMargins = typeof t.layout_json === 'string' ? JSON.parse(t.layout_json) : t.layout_json; } catch { layoutForMargins = null; }
      const marginT = parseFloat(layoutForMargins?.margin_top    ?? t.margin_mm ?? 12) || 12;
      const marginR = parseFloat(layoutForMargins?.margin_right  ?? t.margin_mm ?? 12) || 12;
      const marginB = parseFloat(layoutForMargins?.margin_bottom ?? t.margin_mm ?? 12) || 12;
      const marginL = parseFloat(layoutForMargins?.margin_left   ?? t.margin_mm ?? 12) || 12;
      // renderPrintHtml çıktısı tam HTML; script bloklarını çıkar (otomatik print iframe'i tetiklemesin)
      // Body'ye padding ver — yazdırma çıktısındaki @page margin'le birebir aynı görsel kenar boşluğu
      // Body min-height = sayfa yüksekliği → page_footer absolute bottom:0 sayfanın altına yapışır
      const extraStyle = `<style>
        body{
          padding:${marginT}mm ${marginR}mm ${marginB}mm ${marginL}mm !important;
          margin:0 !important; box-sizing:border-box;
          position:relative; min-height:${pageH}mm;
        }
      </style>`;
      const html = buildHtml({ autoPrint: false })
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        // page_footer: position:fixed → position:absolute + bottom:0 sayfanın altına yapışsın
        .replace(/position:fixed;([^"]*?);/g, 'position:absolute;$1;')
        .replace('</head>', extraStyle + '</head>');
      doc.open();
      doc.write(html);
      doc.close();

      // 2) Layout settle + fontlar
      await new Promise(r => setTimeout(r, 200));
      if (iframe.contentDocument?.fonts?.ready) {
        try { await iframe.contentDocument.fonts.ready; } catch (_) {}
      }
      await new Promise(r => setTimeout(r, 200));

      // 3) html2canvas + jsPDF — iframe body'sini snapshot al
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');
      const body = iframe.contentDocument.body;
      body.style.background = '#ffffff';
      body.style.margin = '0';
      // İçeriğin gerçek yüksekliği (boş alanı dahil etme — pdf'te 2. sayfaya neden oluyordu)
      const contentH = body.scrollHeight;
      const canvas = await html2canvas(body, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: pageWpx,
        height: contentH,
        windowWidth: pageWpx,
        windowHeight: contentH,
      });

      console.log('[PDF] canvas:', canvas.width, 'x', canvas.height, 'contentH:', contentH);

      // 4) Canvas → PDF
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF({ unit: 'mm', format: paperSize, orientation, compress: true });
      const imgWidth = pageW;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const TOLERANCE = 0.5; // mm; rounding hataları için yedek
      if (imgHeight <= pageH + TOLERANCE) {
        // Tek sayfa
        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
      } else {
        // Çok sayfalı — her sayfa için kanvas dikey olarak kayar
        let heightLeft = imgHeight;
        let position = 0;
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageH;
        while (heightLeft > TOLERANCE) {
          position -= pageH;
          pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageH;
        }
      }
      pdf.save(filename);
      document.body.removeChild(iframe);
    } catch (e) {
      console.error('PDF download:', e);
      alert('PDF oluşturulamadı: ' + (e.message || ''));
    }
  };

  const [showEmailForm, setShowEmailForm] = useState(false);

  // Mail için PDF Blob üret (downloadPdf'in mantığını döndüren versiyon)
  const generatePdfBlob = async () => {
    const mod = await import('html2pdf.js'); // unused — html2canvas+jsPDF
    void mod;
    const t = activeTpl || {};
    const paperSize = String(t.paper_size || 'a4').toLowerCase();
    const orientation = String(t.orientation || 'portrait').toLowerCase();
    const SIZES = { a4: { w: 210, h: 297 }, a5: { w: 148, h: 210 }, letter: { w: 216, h: 279 } };
    const dims = SIZES[paperSize] || SIZES.a4;
    const pageW = orientation === 'landscape' ? dims.h : dims.w;
    const pageH = orientation === 'landscape' ? dims.w : dims.h;
    const pxPerMm = 3.7795275591;
    const pageWpx = Math.round(pageW * pxPerMm);
    const pageHpx = Math.round(pageH * pxPerMm);

    let layoutForMargins = null;
    try { layoutForMargins = typeof t.layout_json === 'string' ? JSON.parse(t.layout_json) : t.layout_json; } catch { layoutForMargins = null; }
    const marginT = parseFloat(layoutForMargins?.margin_top    ?? t.margin_mm ?? 12) || 12;
    const marginR = parseFloat(layoutForMargins?.margin_right  ?? t.margin_mm ?? 12) || 12;
    const marginB = parseFloat(layoutForMargins?.margin_bottom ?? t.margin_mm ?? 12) || 12;
    const marginL = parseFloat(layoutForMargins?.margin_left   ?? t.margin_mm ?? 12) || 12;

    const iframe = document.createElement('iframe');
    iframe.style.cssText = `position:fixed;left:-99999px;top:0;width:${pageWpx}px;height:${pageHpx}px;border:0;`;
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    const extraStyle = `<style>body{padding:${marginT}mm ${marginR}mm ${marginB}mm ${marginL}mm !important;margin:0 !important;box-sizing:border-box;position:relative;min-height:${pageH}mm;}</style>`;
    const html = buildHtml({ autoPrint: false })
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/position:fixed;([^"]*?);/g, 'position:absolute;$1;')
      .replace('</head>', extraStyle + '</head>');
    doc.open(); doc.write(html); doc.close();
    await new Promise(r => setTimeout(r, 200));
    if (iframe.contentDocument?.fonts?.ready) { try { await iframe.contentDocument.fonts.ready; } catch (_) {} }
    await new Promise(r => setTimeout(r, 200));

    const html2canvas = (await import('html2canvas')).default;
    const { jsPDF } = await import('jspdf');
    const body = iframe.contentDocument.body;
    body.style.background = '#ffffff';
    const contentH = body.scrollHeight;
    const canvas = await html2canvas(body, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false, width: pageWpx, height: contentH, windowWidth: pageWpx, windowHeight: contentH });
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pdf = new jsPDF({ unit: 'mm', format: paperSize, orientation, compress: true });
    const imgWidth = pageW;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const TOL = 0.5;
    if (imgHeight <= pageH + TOL) {
      pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
    } else {
      let heightLeft = imgHeight; let position = 0;
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageH;
      while (heightLeft > TOL) {
        position -= pageH; pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageH;
      }
    }
    document.body.removeChild(iframe);
    // PDF'i Base64 olarak döndür
    return pdf.output('datauristring').split(',')[1]; // raw base64
  };

  const sendEmail = async ({ to, cc, subject, message, attachPdf }) => {
    try {
      // PDF ekle (varsayılan) — html ise sadece mesaj gönder
      let attachments = [];
      let bodyHtml;
      if (attachPdf !== false) {
        const pdfBase64 = await generatePdfBlob();
        const fname = `${docLabel.replace(/\s+/g, '_')}_${docNumber || 'belge'}.pdf`;
        attachments.push({ filename: fname, content: pdfBase64, encoding: 'base64', contentType: 'application/pdf' });
        // E-posta gövdesi sadece mesaj — belge eklenmiştir notu
        bodyHtml = `<div style="font-family:Arial,sans-serif;font-size:14px;line-height:1.5;color:#1a2332;max-width:600px;margin:0 auto;padding:20px">
          ${message ? message.replace(/</g, '&lt;').replace(/\n/g, '<br>') : ''}
          <p style="margin-top:24px;padding-top:14px;border-top:1px solid #e2e8f0;color:#64748b;font-size:13px">
            📎 Belge "<strong>${fname}</strong>" olarak ek olarak gönderildi.
          </p>
        </div>`;
      } else {
        // HTML inline (eski davranış)
        const docBody = buildHtml({ autoPrint: false });
        const intro = message ? `<div style="font-family:Arial,sans-serif;padding:14px 18px;background:#f4f6fa;border-radius:6px;margin-bottom:14px;white-space:pre-wrap">${message.replace(/</g, '&lt;')}</div>` : '';
        bodyHtml = docBody.replace('<body>', `<body>${intro}`);
      }
      const r = await api.post('/print-templates/send-email', {
        to, cc: cc || undefined, subject, html: bodyHtml, attachments,
        doc_type: docType, doc_id: docSelected?.id, doc_number: docNumber,
      });
      if (r.data?.success) {
        alert('✓ E-posta gönderildi: ' + to);
        setShowEmailForm(false);
      }
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'E-posta gönderilemedi');
    }
  };

  return (
    <>
    <Modal
      title={<>🖨 {docLabel} — Yazdır <span style={{ marginLeft: 8, fontSize: '0.78rem', color: 'var(--text-tertiary)', fontWeight: 400 }}>{docNumber}</span></>}
      size="sm"
      onClose={onClose}
      closeOnOverlay={false}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', width: '100%' }}>
          <Button variant="secondary" size="md" onClick={downloadPdf}
            title="PDF dosyası olarak indir">📄 PDF İndir</Button>
          <Button variant="secondary" size="md" onClick={() => setShowEmailForm(true)}>✉ Mail Gönder</Button>
          <Button variant="primary" size="md" onClick={doPrint}>🖨 Yazdır</Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Şablon yönetim aksiyonları */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="success" size="sm" onClick={openNew}>+ Yeni Şablon</Button>
          <Button variant="ghost" size="sm" onClick={openDesign} disabled={!isUserTpl}>🎨 Tasarım</Button>
          <Button variant="danger" size="sm" onClick={deleteTpl} disabled={!isUserTpl}>🗑 Sil</Button>
        </div>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          Yazdırılacak şablonu seçin. Şablonu özelleştirmek için "+ Yeni Şablon" oluştur, sonra "🎨 Tasarım" ile düzenle.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '40vh', overflowY: 'auto' }}>
          {allTemplates.map(t => (
            <label key={t.key} style={{
              padding: '10px 12px', border: '2px solid ' + (selected === t.key ? 'var(--accent-primary)' : 'var(--border-secondary)'),
              borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
              background: selected === t.key ? 'rgba(99,102,241,0.06)' : 'var(--bg-secondary)',
              fontSize: '0.9rem',
            }}>
              <input type="radio" checked={selected === t.key} onChange={() => setSelected(t.key)} />
              <span style={{ fontWeight: 600, flex: 1 }}>{t.label}</span>
              {!t.tpl.id && <span style={{ fontSize: '0.7rem', padding: '2px 6px', background: 'var(--bg-tertiary)', borderRadius: 3, color: 'var(--text-tertiary)' }}>Yerleşik</span>}
            </label>
          ))}
        </div>
      </div>
    </Modal>

    {/* Mail Gönder formu */}
      {showEmailForm && (
        <EmailSendForm
          defaultTo={account?.email || ''}
          docLabel={docLabel}
          docNumber={docNumber}
          accountTitle={account?.title || ''}
          onClose={() => setShowEmailForm(false)}
          onSend={sendEmail}
        />
      )}

      {/* Yeni Şablon Yaratma Dialog'u */}
      {newTplDialog && (
        <NewTemplateDialog
          docLabel={docLabel}
          existingTemplates={serverTpls}
          onClose={() => setNewTplDialog(false)}
          onCreate={createTemplate}
        />
      )}

      {/* Visual banded designer — tam ekran overlay (click event'i parent'a sıçramasın) */}
      {visualDesigner && (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', inset: 0, background: 'var(--bg-primary)',
            zIndex: 3000, display: 'flex', flexDirection: 'column',
          }}
        >
          <ReportDesignerPage
            reportKey={docType + '_doc'}
            apiBase="/print-templates"
            initialTplId={visualDesigner.tplId}
            pageTitle={`🎨 ${docLabel} Şablon Tasarımı`}
            previewData={(() => {
              // Designer'da Önizle butonunda kullanılacak gerçek belge verisi
              const cleanDescForBanded = (s) => (s || '').replace(/\s*·?\s*Karlılık:\s*%[-0-9.,]+\s*/gi, '').trim();
              const mainRow = {
                id: docSelected?.id || 'preview-doc',
                document_number: docNumber,
                invoice_date: header.invoice_date || '',
                invoice_time: header.invoice_time || '',
                due_date: header.due_date || '',
                valid_until: header.valid_until || '',
                account_code: account?.code || '',
                account_title: account?.title || header.account_title || header.bill_to_name || '',
                account_tax_office: account?.tax_office || '',
                account_tax_number: account?.tax_number || header.bill_to_tax_number || '',
                account_address: account?.address || header.bill_to_address || '',
                account_city: account?.city || header.bill_to_city || '',
                account_district: account?.district || header.bill_to_district || '',
                account_country: account?.country || '',
                account_phone: account?.phone || '',
                account_mobile: account?.mobile || '',
                account_email: account?.email || '',
                personnel_name: personnelName,
                currency_code: header.currency_code || '',
                exchange_rate: parseFloat(header.exchange_rate) || 1,
                subtotal: parseFloat(totals?.subtotal) || 0,
                discount_total: parseFloat(totals?.discount_total) || 0,
                net_total: parseFloat(totals?.net_total) || 0,
                vat_total: parseFloat(totals?.vat_total) || 0,
                grand_total: parseFloat(totals?.grand_total) || 0,
                avg_profit_pct: parseFloat(docSelected?.avg_profit_pct) || null,
                description: header.description || '',
                notes: header.notes || '',
                internal_notes: header.internal_note || header.internal_notes || '',
              };
              const detailRows = (lines || []).map((l, i) => ({
                master_id: mainRow.id,
                line_no: i + 1,
                stock_code: l.stock_code || '',
                stock_name: l.stock_name || l.manual_stock_name || '',
                manual_stock_name: l.manual_stock_name || '',
                description: cleanDescForBanded(l.description),
                barcode: l.barcode || '',
                color_name: l.color_name || '',
                size_name: l.size_name || '',
                quantity: parseFloat(l.quantity) || 0,
                unit_code: l.unit_code || l.unit_name || '',
                unit_price: parseFloat(l.unit_price) || 0,
                unit_price_net: parseFloat(l.unit_price_net) || 0,
                discount_rate1: parseFloat(l.discount_rate1) || 0,
                discount_rate2: parseFloat(l.discount_rate2) || 0,
                discount_rate3: parseFloat(l.discount_rate3) || 0,
                discount_amount: parseFloat(l.discount_amount) || 0,
                vat_rate: parseFloat(l.vat_rate) || 0,
                vat_amount: parseFloat(l.vat_amount) || 0,
                line_net: parseFloat(l.line_net) || 0,
                line_total: parseFloat(l.line_total) || 0,
                personnel_name: l.personnel_name || '',
              }));
              return {
                mainRow, detailRows,
                context: {
                  title: docLabel,
                  subtitle: docNumber,
                  company_name: '',
                  date_today: new Date().toISOString().slice(0, 10),
                  row_count: detailRows.length,
                  page_number: 1,
                  total_pages: 1,
                },
              };
            })()}
            onClose={(saved) => {
              setVisualDesigner(null);
              if (saved) reloadTemplates();
            }}
          />
        </div>
      )}
    </>
  );
}

// ─── Şablon tasarımı formu (basit ayarlar — gelişmiş banded designer ileride) ──
function TemplateDesignerForm({ template, onClose, onSaved }) {
  const [t, setT] = useState(template);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    if (!t.name?.trim()) { setError('Şablon adı zorunludur'); return; }
    setSaving(true); setError('');
    try {
      if (t.id) await api.put('/print-templates/' + t.id, t);
      else      await api.post('/print-templates', t);
      onSaved();
    } catch (e) { setError(e.response?.data?.message || 'Kayıt başarısız'); }
    finally { setSaving(false); }
  };
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2600,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: 10, width: 'min(720px, 95vw)',
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: 16, borderBottom: '1px solid var(--border-secondary)' }}>
          <strong style={{ fontSize: '1.05rem' }}>🎨 Şablon Tasarımı {t.id ? '— Düzenle' : '— Yeni'}</strong>
        </div>
        <div style={{ padding: 16, overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Şablon Adı *</label>
            <input type="text" value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })}
              placeholder="Örn: Verilen Teklif - Standart"
              style={{ width: '100%', padding: 8, marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Kağıt Boyutu</label>
            <select value={t.paper_size} onChange={(e) => setT({ ...t, paper_size: e.target.value })}
              style={{ width: '100%', padding: 8, marginTop: 4 }}>
              <option value="A4">A4</option>
              <option value="A5">A5</option>
              <option value="Letter">Letter</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Yönlendirme</label>
            <select value={t.orientation} onChange={(e) => setT({ ...t, orientation: e.target.value })}
              style={{ width: '100%', padding: 8, marginTop: 4 }}>
              <option value="portrait">Dikey</option>
              <option value="landscape">Yatay</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Yazı Boyutu (pt)</label>
            <input type="number" step="0.5" min="6" max="20" value={t.font_size}
              onChange={(e) => setT({ ...t, font_size: parseFloat(e.target.value) || 10 })}
              style={{ width: '100%', padding: 8, marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Kenar Boşluğu (mm)</label>
            <input type="number" step="0.5" min="0" max="50" value={t.margin_mm}
              onChange={(e) => setT({ ...t, margin_mm: parseFloat(e.target.value) || 15 })}
              style={{ width: '100%', padding: 8, marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Vurgu Rengi</label>
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <input type="color" value={t.primary_color} onChange={(e) => setT({ ...t, primary_color: e.target.value })}
                style={{ width: 50, height: 36, padding: 0, border: '1px solid var(--border-secondary)', borderRadius: 4 }} />
              <input type="text" value={t.primary_color} onChange={(e) => setT({ ...t, primary_color: e.target.value })}
                style={{ flex: 1, padding: 8, fontFamily: 'monospace' }} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Bölümler</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, marginTop: 4 }}>
              <label><input type="checkbox" checked={!!t.show_logo} onChange={(e) => setT({ ...t, show_logo: e.target.checked })} /> Logo</label>
              <label><input type="checkbox" checked={!!t.show_account} onChange={(e) => setT({ ...t, show_account: e.target.checked })} /> Cari bilgileri</label>
              <label><input type="checkbox" checked={!!t.show_notes} onChange={(e) => setT({ ...t, show_notes: e.target.checked })} /> Notlar</label>
              <label><input type="checkbox" checked={!!t.show_signature} onChange={(e) => setT({ ...t, show_signature: e.target.checked })} /> İmza alanları</label>
              <label><input type="checkbox" checked={!!t.is_default} onChange={(e) => setT({ ...t, is_default: e.target.checked })} /> <strong>Varsayılan</strong></label>
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
              Üst Bilgi (HTML) <span style={{ color: 'var(--text-tertiary)' }}>— logo &lt;img&gt; veya firma sloganı</span>
            </label>
            <textarea rows={3} value={t.header_html} onChange={(e) => setT({ ...t, header_html: e.target.value })}
              placeholder='<img src="https://.../logo.png" height="40"> &nbsp; <strong>Firma Adı</strong>'
              style={{ width: '100%', padding: 8, marginTop: 4, fontFamily: 'monospace', fontSize: 12 }} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
              Alt Bilgi (HTML)
            </label>
            <textarea rows={3} value={t.footer_html} onChange={(e) => setT({ ...t, footer_html: e.target.value })}
              placeholder="Tel: ... · IBAN: ... · vergi dairesi"
              style={{ width: '100%', padding: 8, marginTop: 4, fontFamily: 'monospace', fontSize: 12 }} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
              Ek CSS (gelişmiş) <span style={{ color: 'var(--text-tertiary)' }}>— örnek: <code>.grand &#123; color: red; &#125;</code></span>
            </label>
            <textarea rows={3} value={t.extra_css} onChange={(e) => setT({ ...t, extra_css: e.target.value })}
              style={{ width: '100%', padding: 8, marginTop: 4, fontFamily: 'monospace', fontSize: 12 }} />
          </div>
          {error && (
            <div style={{ gridColumn: '1 / -1', padding: 8, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', color: 'var(--danger)', borderRadius: 6, fontSize: 13 }}>
              {error}
            </div>
          )}
        </div>
        <div style={{ padding: 12, borderTop: '1px solid var(--border-secondary)', display: 'flex', gap: 8 }}>
          <button onClick={onClose} disabled={saving} style={{
            flex: 1, padding: 10, background: 'var(--bg-secondary)',
            border: '1px solid var(--border-secondary)', borderRadius: 6,
            fontWeight: 600, cursor: saving ? 'wait' : 'pointer',
          }}>Vazgeç</button>
          <button onClick={save} disabled={saving} style={{
            flex: 2, padding: 10, background: 'var(--success)', color: '#fff',
            border: 'none', borderRadius: 6, fontWeight: 800, cursor: saving ? 'wait' : 'pointer',
          }}>{saving ? 'Kaydediliyor...' : '✓ Kaydet'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Verilen Teklif → Alınan Sipariş Aktarım Modalı ─────────────────
// - Teslim tarihi zorunlu
// - Potansiyel cari ile yapılan teklifte: bilgilendirme + cari kartı oluşturma
// - Manuel stoklu kalemler için stok seçimi zorunlu
function ConvertQuoteToOrderModal({ quoteId, quoteDocNo, accountId, prospectAccountId, isProspect, accountTitle, lines, header, totals, stocks, loading, setLoading, onClose, onConverted }) {
  const today = new Date().toISOString().slice(0, 10);
  const defDeliv = (() => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  })();
  const [deliveryDate, setDeliveryDate] = useState(defDeliv);
  const [orderNumber, setOrderNumber] = useState('');
  const [createProspect, setCreateProspect] = useState(isProspect);
  // Manuel kalemler için stok seçimi: { lineIdx: stock_id }
  const manualIdxs = (lines || []).map((l, i) => (!l.stock_id && (l.manual_stock_name || l.stock_name)) ? i : -1).filter(i => i >= 0);
  const [stockPicks, setStockPicks] = useState({});
  const [err, setErr] = useState('');
  const [stockSearches, setStockSearches] = useState({});

  const allManualPicked = manualIdxs.every(i => !!stockPicks[i]);

  const submit = async () => {
    setErr('');
    if (!deliveryDate || !/^\d{4}-\d{2}-\d{2}$/.test(deliveryDate)) {
      setErr('Geçerli bir Teslim Tarihi girin'); return;
    }
    if (!allManualPicked) {
      setErr(`${manualIdxs.length - Object.keys(stockPicks).filter(k => stockPicks[k]).length} manuel kalem için stok seçilmemiş`);
      return;
    }
    setLoading(true);
    try {
      // 1) Potansiyel cari → gerçek cariye dönüştür
      let finalAccountId = accountId;
      if (isProspect && createProspect && prospectAccountId) {
        const r = await api.post(`/prospect-accounts/${prospectAccountId}/convert`);
        if (r.data?.success) {
          finalAccountId = r.data.data?.id;
        } else {
          throw new Error('Potansiyel cari → cari dönüşümü başarısız');
        }
      } else if (isProspect && !createProspect) {
        throw new Error('Potansiyel cari için sipariş oluşturulamaz — Cari Kartı Oluştur seçeneği işaretlenmeli');
      }

      // 2) Sipariş kalemlerini hazırla — manuel kalemlere stok ata
      const orderLines = (lines || []).map((l, i) => {
        const out = { ...l };
        if (manualIdxs.includes(i) && stockPicks[i]) {
          const stk = (stocks || []).find(s => s.id === stockPicks[i]);
          if (stk) {
            out.stock_id = stk.id;
            out.stock_code = stk.code;
            out.stock_name = stk.name;
            out.manual_stock_name = null;
            out.barcode = stk.barcode || out.barcode || null;
            // Birim — kullanıcı seçtikten sonra stoğun varsayılan birimi
            if (!out.unit_id) {
              out.unit_id = stk.unit_id;
              out.unit_name = stk.unit_name || stk.unit_code;
            }
          }
        }
        return out;
      });

      // 3) /sales-orders POST body
      const body = {
        account_id: finalAccountId,
        warehouse_id: header.warehouse_id || null,
        personnel_id: header.personnel_id || null,
        invoice_date: today,
        invoice_time: new Date().toTimeString().slice(0, 5),
        delivery_date: deliveryDate,
        order_number: orderNumber || null,
        currency_code: header.currency_code || 'TRY',
        exchange_rate: parseFloat(header.exchange_rate) || 1,
        local_currency: header.local_currency || 'TRY',
        is_vat_included: !!header.is_vat_included,
        description: `Verilen Teklif ${quoteDocNo} → Sipariş`,
        notes: header.notes || '',
        internal_notes: header.internal_note || header.internal_notes || '',
        subtotal: totals.subtotal, discount_total: totals.discount_total,
        net_total: totals.net_total, otv_total: totals.otv_total || 0,
        vat_total: totals.vat_total, withholding_total: totals.withholding_total || 0,
        grand_total: totals.grand_total,
        lines: orderLines,
        status: 'approved',
        source_quote_id: quoteId,
      };
      const r = await api.post('/sales-orders', body);
      if (r.data?.success) {
        onConverted(r.data.data?.document_number || r.data.data?.id);
      } else {
        throw new Error(r.data?.message || 'Sipariş oluşturulamadı');
      }
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Aktarım başarısız');
    } finally { setLoading(false); }
  };

  return (
    <Modal
      title={`🛒 Siparişe Aktar — ${quoteDocNo}`}
      size="md"
      onClose={() => !loading && onClose()}
      closeOnOverlay={false}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
          <Button variant="primary" size="md" onClick={submit} loading={loading}
            disabled={loading || !deliveryDate || !allManualPicked || (isProspect && !createProspect)}>
            ✓ Siparişe Aktar
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Cari bilgisi */}
        <div style={{ padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: 6, fontSize: '0.85rem' }}>
          <div style={{ color: 'var(--text-tertiary)', fontSize: 11, marginBottom: 2 }}>CARİ</div>
          <div style={{ fontWeight: 700 }}>
            {isProspect && '🆕 '} {accountTitle || '—'}
            {isProspect && <span style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px', background: 'rgba(245,158,11,0.15)', color: '#d97706', borderRadius: 3 }}>POTANSİYEL</span>}
          </div>
        </div>

        {/* Potansiyel cari uyarısı */}
        {isProspect && (
          <div style={{
            padding: 12, borderRadius: 6, fontSize: '0.85rem',
            background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
          }}>
            <div style={{ fontWeight: 700, color: '#d97706', marginBottom: 6 }}>⚠️ Potansiyel Cari</div>
            <div style={{ color: 'var(--text-secondary)', marginBottom: 8 }}>
              Bu teklif potansiyel cari kartı ile yapılmış. Sipariş oluşturmak için önce **cari kartı** oluşturulmalı.
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={createProspect} onChange={(e) => setCreateProspect(e.target.checked)} />
              <span style={{ fontWeight: 600 }}>✓ Potansiyel cariden cari kartı oluştur ve siparişi bu kartla yarat</span>
            </label>
          </div>
        )}

        {/* Teslim tarihi */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
            Teslim Tarihi * <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(zorunlu)</span>
          </label>
          <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)}
            style={{ width: '100%', padding: 8 }} />
        </div>

        {/* Sipariş No (opsiyonel) */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
            Müşteri Sipariş No <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(opsiyonel)</span>
          </label>
          <input type="text" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)}
            placeholder="Müşterinin kendi referansı (varsa)" style={{ width: '100%', padding: 8 }} />
        </div>

        {/* Manuel stok kalemleri */}
        {manualIdxs.length > 0 && (
          <div style={{ padding: 12, borderRadius: 6, background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.25)' }}>
            <div style={{ fontWeight: 700, color: '#7c3aed', marginBottom: 8, fontSize: '0.85rem' }}>
              ⚙ Manuel Kalemler — Stok Seçin
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginBottom: 10 }}>
              Aşağıdaki kalemler stok kartına bağlı değil (manuel girilmiş). Sipariş için her birine bir stok atayın.
            </div>
            {manualIdxs.map(idx => {
              const l = lines[idx];
              const search = (stockSearches[idx] || '').toLowerCase();
              const filtered = search
                ? (stocks || []).filter(s =>
                    (s.code || '').toLowerCase().includes(search) ||
                    (s.name || '').toLowerCase().includes(search) ||
                    (s.barcode || '').toLowerCase().includes(search)
                  ).slice(0, 30)
                : (stocks || []).slice(0, 30);
              const picked = (stocks || []).find(s => s.id === stockPicks[idx]);
              return (
                <div key={idx} style={{ marginBottom: 12, padding: '10px 12px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-secondary)' }}>
                  <div style={{ fontSize: '0.82rem', marginBottom: 6 }}>
                    <strong>{idx + 1}. {l.manual_stock_name || l.stock_name}</strong>
                    <span style={{ color: 'var(--text-tertiary)', marginLeft: 6 }}>· Miktar: {l.quantity} · Birim Fiyat: {l.unit_price}</span>
                  </div>
                  {picked ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ flex: 1, fontSize: '0.85rem' }}>
                        ✓ <strong>{picked.code}</strong> — {picked.name}
                      </span>
                      <Button size="sm" variant="ghost" onClick={() => setStockPicks(p => ({ ...p, [idx]: null }))}>Değiştir</Button>
                    </div>
                  ) : (
                    <>
                      <input type="text" placeholder="Stok kodu/adı ara..."
                        value={stockSearches[idx] || ''}
                        onChange={(e) => setStockSearches(p => ({ ...p, [idx]: e.target.value }))}
                        style={{ width: '100%', padding: 6, marginBottom: 6, fontSize: '0.82rem' }} />
                      <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid var(--border-secondary)', borderRadius: 4 }}>
                        {filtered.length === 0 ? (
                          <div style={{ padding: 8, fontSize: '0.78rem', color: 'var(--text-tertiary)', textAlign: 'center' }}>Sonuç yok</div>
                        ) : filtered.map(s => (
                          <div key={s.id} onClick={() => setStockPicks(p => ({ ...p, [idx]: s.id }))}
                            style={{
                              padding: '6px 8px', cursor: 'pointer', fontSize: '0.78rem',
                              borderBottom: '1px solid var(--border-secondary)',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                            <strong>{s.code}</strong> — {s.name}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {err && <div style={{ padding: 8, background: 'rgba(220,38,38,0.08)', color: 'var(--danger)', borderRadius: 6, fontSize: 13 }}>{err}</div>}

        <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
          ℹ Sipariş, teklifin kalem ve toplamları ile aynı değerlerle oluşturulur. Teklif onaylı kalır; isterseniz sonradan değiştirebilirsiniz.
        </div>
      </div>
    </Modal>
  );
}

// ─── Mail gönderme formu ─────────────────────────────────────────
function EmailSendForm({ defaultTo, docLabel, docNumber, accountTitle, onClose, onSend }) {
  const [to, setTo] = useState(defaultTo || '');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(`${docLabel} — ${docNumber}${accountTitle ? ' · ' + accountTitle : ''}`);
  const [message, setMessage] = useState(`Sayın ${accountTitle || 'Yetkili'},\n\nEkte ${docLabel.toLowerCase()} (${docNumber}) bilgilerinizi paylaşıyorum.\n\nİyi çalışmalar dilerim.`);
  const [attachPdf, setAttachPdf] = useState(true);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    if (!to || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) { setErr('Geçerli alıcı e-posta girin'); return; }
    if (!subject.trim()) { setErr('Konu zorunlu'); return; }
    setSending(true); setErr('');
    try { await onSend({ to: to.trim(), cc: cc.trim() || null, subject: subject.trim(), message, attachPdf }); }
    catch (e) { setErr(e.message); }
    finally { setSending(false); }
  };
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2700,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: 10, width: 'min(520px, 95vw)',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: 14, borderBottom: '1px solid var(--border-secondary)' }}>
          <strong style={{ fontSize: '1.05rem' }}>✉ E-posta Gönder</strong>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
            {docLabel} · {docNumber}
          </div>
        </div>
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Alıcı (To) *</label>
            <input type="email" value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="ornek@firma.com" style={{ width: '100%', padding: 8, marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Cc (opsiyonel)</label>
            <input type="email" value={cc} onChange={(e) => setCc(e.target.value)}
              placeholder="iletisim@firma.com" style={{ width: '100%', padding: 8, marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Konu *</label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
              style={{ width: '100%', padding: 8, marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Mesaj (opsiyonel)</label>
            <textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
              style={{ width: '100%', padding: 8, marginTop: 4, fontFamily: 'inherit', resize: 'vertical' }} />
          </div>
          <div style={{
            padding: '10px 12px', borderRadius: 6,
            border: '1px solid var(--border-secondary)',
            background: 'var(--bg-secondary)',
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} />
              <span style={{ fontSize: 13, fontWeight: 600 }}>📎 Belgeyi PDF olarak ekle</span>
            </label>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4, marginLeft: 24 }}>
              {attachPdf
                ? 'PDF dosyası mail eki olarak gönderilir, mail gövdesinde sadece mesajınız yer alır.'
                : 'Belge HTML olarak mail gövdesinde yer alır (eski davranış).'}
            </div>
          </div>
          {err && <div style={{ padding: 8, background: 'rgba(220,38,38,0.08)', color: 'var(--danger)', borderRadius: 6, fontSize: 13 }}>{err}</div>}
        </div>
        <div style={{ padding: 12, borderTop: '1px solid var(--border-secondary)', display: 'flex', gap: 8 }}>
          <button onClick={onClose} disabled={sending} style={{
            flex: 1, padding: 10, background: 'var(--bg-secondary)',
            border: '1px solid var(--border-secondary)', borderRadius: 6,
            fontWeight: 600, cursor: sending ? 'wait' : 'pointer',
          }}>Vazgeç</button>
          <button onClick={submit} disabled={sending} style={{
            flex: 2, padding: 10, background: '#7c3aed', color: '#fff',
            border: 'none', borderRadius: 6, fontWeight: 800, cursor: sending ? 'wait' : 'pointer',
          }}>{sending ? 'Gönderiliyor...' : '✉ Gönder'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Yeni Şablon Yaratma Dialog'u ─────────────────────────────────
// 3 seçenek: 1) Sıfırdan boş 2) Standart varsayılan layout 3) Mevcut şablondan kopyala
function NewTemplateDialog({ docLabel, existingTemplates = [], onClose, onCreate }) {
  const [name, setName] = useState(`${docLabel} - Yeni Şablon`);
  const [source, setSource] = useState('default');
  const [submitting, setSubmitting] = useState(false);

  const userTpls = existingTemplates || [];

  const submit = async () => {
    if (!name.trim()) { alert('Şablon adı gerekli'); return; }
    setSubmitting(true);
    try { await onCreate({ name: name.trim(), source }); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal
      title="🆕 Yeni Şablon Oluştur"
      size="sm"
      onClose={() => !submitting && onClose()}
      closeOnOverlay={false}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
          <Button variant="primary" size="md" onClick={submit} loading={submitting}>✓ Oluştur ve Tasarla</Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
            Şablon Adı *
          </label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }} autoFocus />
        </div>
        <div>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
            Başlangıç noktası
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{
              padding: '10px 12px', border: '2px solid ' + (source === 'default' ? 'var(--accent-primary)' : 'var(--border-secondary)'),
              borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
              background: source === 'default' ? 'rgba(99,102,241,0.06)' : 'var(--bg-secondary)',
              fontSize: '0.9rem',
            }}>
              <input type="radio" checked={source === 'default'} onChange={() => setSource('default')} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>📋 Standart şablonu kullan</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                  Hazır profesyonel layout (başlık + cari + tablo + toplamlar + imza)
                </div>
              </div>
            </label>
            <label style={{
              padding: '10px 12px', border: '2px solid ' + (source === 'blank' ? 'var(--accent-primary)' : 'var(--border-secondary)'),
              borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
              background: source === 'blank' ? 'rgba(99,102,241,0.06)' : 'var(--bg-secondary)',
              fontSize: '0.9rem',
            }}>
              <input type="radio" checked={source === 'blank'} onChange={() => setSource('blank')} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>📄 Sıfırdan boş başla</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                  Hiçbir element olmadan boş sayfa — tüm tasarımı sıfırdan kur
                </div>
              </div>
            </label>
            {userTpls.length > 0 && (
              <div style={{
                padding: '10px 12px', border: '2px solid ' + (source.startsWith('copy:') ? 'var(--accent-primary)' : 'var(--border-secondary)'),
                borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 6,
                background: source.startsWith('copy:') ? 'rgba(99,102,241,0.06)' : 'var(--bg-secondary)',
                fontSize: '0.9rem',
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="radio" checked={source.startsWith('copy:')}
                    onChange={() => setSource('copy:' + userTpls[0].id)} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>📋 Başka şablondan kopyala</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                      Mevcut bir şablonu kopya olarak başla, sonra düzenle
                    </div>
                  </div>
                </label>
                {source.startsWith('copy:') && (
                  <select value={source.slice(5)} onChange={(e) => setSource('copy:' + e.target.value)}
                    style={{ padding: '6px 10px', fontSize: '0.85rem', marginLeft: 24 }}>
                    {userTpls.map(t => (
                      <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (varsayılan)' : ''}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default SalesInvoicePage;
